import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import GrowthKit from "@/pages/GrowthKit";
import VisitsAnalytics from "@/components/VisitsAnalytics";
import EditorialCalendar from "@/components/EditorialCalendar";
import { ComposeEmail, ThreadButton } from "@/components/ComposeEmail";
import { CallbackButton, UpcomingCallbacks } from "@/components/Callbacks";
import PartnerConsole from "@/components/PartnerConsole";
import AdminPartners from "@/components/AdminPartners";
import MetaConnect from "@/components/MetaConnect";
import Guides from "@/components/Guides";
import MyPhotos from "@/components/MyPhotos";
import WhatsAppConnect from "@/components/WhatsAppConnect";
import TikTokStudio from "@/components/TikTokStudio";
import GrowthPlan from "@/components/GrowthPlan";

function CoBrand() {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/me/branding").then(r => setB(r.data)).catch(() => {}); }, []);
  if (!b?.cobrand) return null;
  return (
    <div className="mt-3 flex items-center gap-2 text-xs text-stone" data-testid="cobrand-badge">
      <span>×</span>{b.logo_url && <img src={b.logo_url} alt="" className="h-5 max-w-[90px] object-contain" />}<span className="text-ink font-medium truncate">{b.brand_name}</span>
    </div>
  );
}
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ChartLineUp, UsersThree, CalendarBlank, Chat, Toolbox, Storefront, Gear, CreditCard, SignOut,
  Sparkle, WhatsappLogo, EnvelopeSimple, Phone, Globe, Trash, Plus, PaperPlaneTilt, Check, ArrowUpRight,
  Megaphone, ArrowsClockwise, InstagramLogo, FacebookLogo, GoogleLogo, Copy, Star, Compass, AirplaneTilt,
  Headset, Warning, ClockCounterClockwise, Tag, Fire, ArrowClockwise, Question, MagnifyingGlass, ChartBar, TiktokLogo, Article, PencilSimple, Code, Gift, MedalMilitary, ShareNetwork, TrendUp, ChatCircleText, LinkedinLogo, Copy as CopyIcon,
  Handshake, Buildings,
} from "@phosphor-icons/react";
import { DASHBOARD_FAQ, DASHBOARD_FAQ_CATEGORIES } from "@/data/faqs";

const NAV = [
  { k: "overview",  l: "Panoramica",   i: ChartLineUp },
  { k: "analytics", l: "Analytics",    i: ChartBar },
  { k: "inbox",     l: "Inbox AI",     i: Chat },
  { k: "tickets",   l: "Ticket Support", i: Headset },
  { k: "reviews",   l: "Recensioni AI",i: Star },
  { k: "bookings",  l: "Prenotazioni", i: CalendarBlank },
  { k: "calendars", l: "Calendari iCal", i: ArrowsClockwise },
  { k: "guests",    l: "Ospiti",       i: UsersThree },
  { k: "suppliers", l: "Fornitori",    i: Toolbox },
  { k: "services",  l: "Servizi",      i: Storefront },
  { k: "marketing", l: "Marketing AI", i: Megaphone },
  { k: "autopilot", l: "Social Autopilot", i: Article },
  { k: "social_connect", l: "Collega Social", i: Handshake },
  { k: "whatsapp", l: "Collega WhatsApp", i: Chat },
  { k: "photos", l: "Le mie foto", i: Storefront },
  { k: "tiktok", l: "TikTok Studio", i: Article },
  { k: "growth_plan", l: "Piano Follower", i: ChartLineUp },
  { k: "editorial", l: "Calendario Editoriale", i: CalendarBlank },
  { k: "visits",    l: "Visite Sito",   i: ChartBar },
  { k: "partners_admin", l: "Partner",  i: Handshake },
  { k: "partner",   l: "Console Partner", i: Buildings },
  { k: "growth",    l: "Growth Kit",      i: Megaphone },
  { k: "outreach",  l: "Cold Outreach", i: Megaphone },
  { k: "preview",   l: "Anteprima Invii", i: PencilSimple },
  { k: "history",   l: "Storico Email", i: Article },
  { k: "replies",   l: "Draft-Reply Inbox", i: ChatCircleText },
  { k: "voice",     l: "Chiamate AI",   i: Phone },
  { k: "widget",    l: "Widget Sito",    i: Code },
  { k: "faqcms",    l: "FAQ CMS",       i: Question },
  { k: "caseStudies", l: "Case Studies",  i: Article },
  { k: "ai",        l: "AI Config",    i: Sparkle },
  { k: "billing",   l: "Abbonamento",  i: CreditCard },
  { k: "help",      l: "Aiuto & FAQ",  i: Question },
];

function KPI({ label, value, hint, testid, delta }) {
  const showDelta = typeof delta === "number";
  const positive = showDelta && delta >= 0;
  return (
    <div className="rounded-2xl border border-line bg-canvas p-6" data-testid={testid}>
      <div className="text-xs uppercase tracking-widest text-stone">{label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="font-serif text-3xl text-ink">{value}</div>
        {showDelta && (
          <span
            data-testid={`${testid}-delta`}
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${positive ? "bg-olive/10 text-olive" : "bg-terracotta/10 text-terracotta"}`}>
            {positive ? "+" : ""}{delta}%
          </span>
        )}
      </div>
      {hint && <div className="mt-1 text-xs text-stone">{hint}</div>}
    </div>
  );
}

function ChannelIcon({ ch }) {
  const map = { whatsapp: WhatsappLogo, email: EnvelopeSimple, phone: Phone, website: Globe, booking: CalendarBlank };
  const I = map[ch] || Chat;
  return <I weight="fill" size={16} className="text-olive" />;
}

// ----- OVERVIEW -----
function Overview({ setView }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/overview").then(r => setData(r.data)); }, []);
  if (!data) return <div className="text-stone">Caricamento…</div>;
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="kpi-revenue"  label="Fatturato" value={`€${data.revenue.toLocaleString('it')}`} hint="Totale prenotazioni" />
        <KPI testid="kpi-bookings" label="Prenotazioni" value={data.bookings_count} hint="Attive e future" />
        <KPI testid="kpi-guests"   label="Ospiti CRM" value={data.guests_count} hint="Nel tuo database" />
        <KPI testid="kpi-inbox"    label="Da rispondere" value={data.inbox_new} hint="Otelya è pronta" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-line bg-canvas p-6">
          <div className="flex items-center justify-between">
            <div className="font-serif text-xl text-ink">Prossime prenotazioni</div>
            <Button variant="ghost" size="sm" onClick={()=>setView("bookings")} data-testid="overview-see-all-bookings">Vedi tutte <ArrowUpRight size={14} className="ml-1"/></Button>
          </div>
          <div className="mt-4 divide-y divide-line">
            {data.upcoming_bookings.length === 0 && <div className="text-sm text-stone py-4">Nessuna prenotazione in arrivo.</div>}
            {data.upcoming_bookings.map(b => (
              <div key={b.id} className="py-3 flex items-center justify-between gap-4" data-testid={`upcoming-${b.id}`}>
                <div>
                  <div className="text-ink font-medium">{b.guest_name} <span className="text-stone font-normal">· {b.property_unit}</span></div>
                  <div className="text-xs text-stone">{b.check_in} → {b.check_out} · {b.guests_count} ospiti</div>
                </div>
                <div className="text-right">
                  <div className="font-serif text-lg text-ink">€{b.total_amount.toLocaleString('it')}</div>
                  <Badge variant="outline" className="text-[10px] uppercase tracking-widest">{b.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-line bg-canvas p-6">
          <div className="font-serif text-xl text-ink">Performance Otelya</div>
          <div className="mt-4 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-stone">Tasso di conversione</div>
              <div className="font-serif text-2xl text-ink mt-1">{data.conversion_rate}%</div>
              <div className="mt-2 h-1.5 bg-sand rounded-full overflow-hidden"><div className="h-full bg-terracotta" style={{width:`${data.conversion_rate}%`}}/></div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-stone">Tempo medio di risposta</div>
              <div className="font-serif text-2xl text-ink mt-1">{data.response_time_seconds}s</div>
              <div className="text-xs text-stone mt-1">Umani: ~4 ore</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- ANALYTICS -----
const LANG_LABELS = { it: "Italiano", en: "English", fr: "Français", de: "Deutsch", es: "Español", pt: "Português", nl: "Nederlands", ru: "Русский", zh: "中文", ja: "日本語", ar: "العربية", unknown: "Sconosciuta" };
const CHANNEL_META = {
  whatsapp: { label: "WhatsApp", color: "bg-olive",       icon: WhatsappLogo },
  email:    { label: "Email",    color: "bg-terracotta",  icon: EnvelopeSimple },
  phone:    { label: "Telefono", color: "bg-gold",        icon: Phone },
  website:  { label: "Sito web", color: "bg-ink",         icon: Globe },
  booking:  { label: "Prenotazioni", color: "bg-olivelight", icon: CalendarBlank },
};

function fmtDuration(sec) {
  if (!sec) return "—";
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec/60)}min`;
  return `${(sec/3600).toFixed(1)}h`;
}

function TrendChart({ data }) {
  if (!data?.length) return null;
  const max = Math.max(1, ...data.map(d => d.count));
  const W = 600, H = 140, pad = 20;
  const stepX = (W - pad*2) / Math.max(1, data.length - 1);
  const pts = data.map((d, i) => [pad + i*stepX, H - pad - (d.count / max) * (H - pad*2)]);
  const path = pts.map((p, i) => (i===0?`M ${p[0]} ${p[1]}`:`L ${p[0]} ${p[1]}`)).join(" ");
  const areaPath = `${path} L ${pts[pts.length-1][0]} ${H-pad} L ${pts[0][0]} ${H-pad} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40" data-testid="analytics-trend-chart">
      <defs>
        <linearGradient id="trendFill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#8B7355" stopOpacity="0.35"/>
          <stop offset="100%" stopColor="#8B7355" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#trendFill)" />
      <path d={path} stroke="#C46A4B" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p[0]} cy={p[1]} r="3" fill="#C46A4B" />
          <title>{data[i].date}: {data[i].count} messaggi</title>
        </g>
      ))}
    </svg>
  );
}

function Analytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const load = () => api.get(`/analytics/overview?days=${days}`).then(r => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);
  if (!data) return <div className="text-stone">Caricamento…</div>;

  const channelEntries = Object.entries(data.by_channel).sort((a,b) => b[1]-a[1]);
  const maxChan = Math.max(1, ...channelEntries.map(([,v]) => v));
  const maxLang = Math.max(1, ...data.top_languages.map(l => l.count));

  return (
    <div className="space-y-8">
      {/* Header controls */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-stone text-sm max-w-xl">
          Come Otelya sta lavorando per te — volumi, lingue e tempi di risposta a colpo d'occhio.
        </p>
        <div className="flex items-center gap-2" data-testid="analytics-window">
          {[7, 30, 90].map(d => (
            <button key={d} onClick={()=>setDays(d)} data-testid={`window-${d}`}
              className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border transition ${days===d?"bg-ink text-canvas border-ink":"bg-canvas text-stone border-line hover:border-ink"}`}>
              {d}g
            </button>
          ))}
          <Button variant="ghost" size="sm" onClick={load} data-testid="analytics-refresh">
            <ArrowClockwise size={14} className="mr-1"/> Aggiorna
          </Button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="kpi-total"      label="Messaggi ricevuti"  value={data.total_messages} hint={`Ultimi ${data.window_days} giorni`} delta={data.deltas?.total_messages}/>
        <KPI testid="kpi-replied"    label="Risposti da Otelya" value={data.replied_messages} hint={`${data.total_messages ? Math.round(data.replied_messages*100/data.total_messages) : 0}% del totale`} delta={data.deltas?.replied_messages}/>
        <KPI testid="kpi-avg-resp"   label="Risposta media"     value={fmtDuration(data.avg_response_seconds)} hint="Umani: ~4h" />
        <KPI testid="kpi-time-saved" label="Tempo risparmiato"  value={`${data.time_saved_hours}h`} hint={`${data.time_saved_minutes} minuti totali`} delta={data.deltas?.time_saved_hours}/>
      </div>

      {/* Trend + Response quality */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border border-line bg-canvas p-6" data-testid="analytics-trend-card">
          <div className="flex items-center justify-between mb-2">
            <div className="font-serif text-xl text-ink">Volume messaggi</div>
            <div className="text-xs uppercase tracking-widest text-stone">Ultimi 14 giorni</div>
          </div>
          <TrendChart data={data.daily_trend} />
        </div>
        <div className="rounded-2xl border border-line bg-canvas p-6" data-testid="analytics-pending-card">
          <div className="font-serif text-xl text-ink">Da rispondere</div>
          <div className="mt-4 font-serif text-5xl text-terracotta leading-none">{data.pending_messages}</div>
          <p className="mt-3 text-sm text-stone">Messaggi in attesa di risposta AI. Otelya può gestirli automaticamente attivando l'auto-reply in AI Config.</p>
        </div>
      </div>

      {/* Channels + Languages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-line bg-canvas p-6" data-testid="analytics-channels-card">
          <div className="flex items-center justify-between mb-4">
            <div className="font-serif text-xl text-ink">Messaggi per canale</div>
            <div className="text-xs uppercase tracking-widest text-stone">Tot. {data.total_messages}</div>
          </div>
          <div className="space-y-3">
            {channelEntries.map(([key, count]) => {
              const meta = CHANNEL_META[key] || { label: key, color: "bg-stone", icon: Chat };
              const Icon = meta.icon;
              const pct = data.total_messages ? Math.round(count*100/data.total_messages) : 0;
              return (
                <div key={key} data-testid={`channel-row-${key}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <div className="flex items-center gap-2 text-ink">
                      <Icon weight="fill" size={16} className="text-olive"/>
                      <span>{meta.label}</span>
                    </div>
                    <div className="text-stone">{count} <span className="text-xs opacity-60">· {pct}%</span></div>
                  </div>
                  <div className="h-2 bg-sand rounded-full overflow-hidden">
                    <div className={`h-full ${meta.color}`} style={{width: `${(count/maxChan)*100}%`}}/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-line bg-canvas p-6" data-testid="analytics-languages-card">
          <div className="flex items-center justify-between mb-4">
            <div className="font-serif text-xl text-ink">Lingue degli ospiti</div>
            <div className="text-xs uppercase tracking-widest text-stone">Top {data.top_languages.length}</div>
          </div>
          {data.top_languages.length === 0 ? (
            <div className="text-sm text-stone py-4">Nessun dato ancora — quando gli ospiti scriveranno, vedrai qui la ripartizione linguistica.</div>
          ) : (
            <div className="space-y-3">
              {data.top_languages.map((l) => (
                <div key={l.lang} data-testid={`lang-row-${l.lang}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <div className="flex items-center gap-2 text-ink">
                      <span className="text-xs uppercase tracking-widest text-stone font-mono">{l.lang}</span>
                      <span>{LANG_LABELS[l.lang] || l.lang}</span>
                    </div>
                    <div className="text-stone">{l.count} <span className="text-xs opacity-60">· {l.pct}%</span></div>
                  </div>
                  <div className="h-2 bg-sand rounded-full overflow-hidden">
                    <div className="h-full bg-terracotta" style={{width: `${(l.count/maxLang)*100}%`}}/>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Insight footer */}
      <div className="rounded-2xl border border-olive/30 bg-olive/5 p-6" data-testid="analytics-insight">
        <div className="flex items-start gap-3">
          <Sparkle weight="fill" size={20} className="text-olive mt-0.5"/>
          <div>
            <div className="font-serif text-lg text-ink">Insight Otelya</div>
            <p className="mt-1 text-sm text-stone leading-relaxed">
              {data.total_messages === 0
                ? "Ancora nessun messaggio in questa finestra. Attiva WhatsApp, Email e il modulo del sito per iniziare a vedere i dati."
                : `Nelle ultime ${data.window_days} giornate Otelya ha gestito ${data.replied_messages} conversazioni con un tempo medio di ${fmtDuration(data.avg_response_seconds)}. Hai risparmiato circa ${data.time_saved_hours} ore di lavoro sulla scrivania.`
              }
            </p>
          </div>
        </div>
      </div>

      {/* Email engagement analytics */}
      <EmailEngagement days={days}/>
    </div>
  );
}

function EmailEngagement({ days = 30 }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/analytics/emails?days=${days}`).then(r => setData(r.data)); }, [days]);
  if (!data) return null;
  const totalSent = data.counts.sent || data.counts.delivered || 0;
  return (
    <div data-testid="email-engagement">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone">Email transazionali</div>
          <div className="font-serif text-2xl text-ink mt-1">Come Otelya scrive ai tuoi ospiti</div>
        </div>
        <div className="text-xs text-stone">Via Resend · Ultimi {data.window_days} giorni</div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="kpi-email-sent"      label="Email inviate"    value={totalSent}                                hint="Include welcome, notifiche, ricevute"/>
        <KPI testid="kpi-email-delivery"  label="Delivery rate"    value={`${data.delivery_rate}%`}                 hint={`${data.counts.bounced || 0} bounced`}/>
        <KPI testid="kpi-email-open"      label="Open rate"        value={`${data.open_rate}%`}                     hint={`${data.counts.opened} aperture`}/>
        <KPI testid="kpi-email-click"     label="Click rate"       value={`${data.click_rate}%`}                    hint={`${data.counts.clicked} click`}/>
      </div>

      {(data.by_type.length > 0 || data.top_links.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {data.by_type.length > 0 && (
            <div className="rounded-2xl border border-line bg-canvas p-6" data-testid="email-by-type">
              <div className="font-serif text-lg text-ink mb-4">Performance per tipo</div>
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-widest text-stone">
                  <tr><th className="text-left py-2">Tipo</th><th className="text-right py-2">Inviate</th><th className="text-right py-2">Aperture</th><th className="text-right py-2">Click</th></tr>
                </thead>
                <tbody>
                  {data.by_type.map(t => (
                    <tr key={t.tag_type} className="border-t border-line" data-testid={`email-type-${t.tag_type}`}>
                      <td className="py-2 text-ink">{t.tag_type || "—"}</td>
                      <td className="py-2 text-right text-ink font-medium">{t.sent}</td>
                      <td className="py-2 text-right"><span className="text-olive">{t.opened}</span> <span className="text-xs text-stone">({t.open_rate}%)</span></td>
                      <td className="py-2 text-right"><span className="text-terracotta">{t.clicked}</span> <span className="text-xs text-stone">({t.click_rate}%)</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data.top_links.length > 0 && (
            <div className="rounded-2xl border border-line bg-canvas p-6" data-testid="email-top-links">
              <div className="font-serif text-lg text-ink mb-4">Link più cliccati</div>
              <div className="space-y-2">
                {data.top_links.map((l) => (
                  <div key={l.url} className="flex items-center justify-between text-sm">
                    <a href={l.url} target="_blank" rel="noreferrer" className="text-terracotta hover:underline truncate max-w-xs" title={l.url}>{l.url}</a>
                    <span className="text-ink font-medium ml-2">{l.clicks}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {totalSent === 0 && (
        <div className="mt-4 rounded-2xl border border-dashed border-line bg-sand/40 p-6 text-center text-sm text-stone" data-testid="email-empty">
          Nessun evento email ancora tracciato. Configura il webhook Resend su <span className="font-mono text-xs bg-canvas px-2 py-0.5 rounded">/api/webhooks/resend</span> abilitando gli eventi <em>sent · delivered · opened · clicked</em> per iniziare a vedere le statistiche qui.
        </div>
      )}
    </div>
  );
}

// ----- COLD OUTREACH CRM -----
const OUTREACH_COLUMNS = [
  { k: "new",       l: "Nuovi",      color: "bg-sand text-ink" },
  { k: "contacted", l: "Contattati", color: "bg-terracotta/15 text-terracotta" },
  { k: "replied",   l: "Risposti",   color: "bg-gold/20 text-[#8a6d16]" },
  { k: "demo",      l: "Prova gratuita", color: "bg-olive/15 text-olive" },
  { k: "won",       l: "Clienti",    color: "bg-olive text-canvas" },
  { k: "lost",      l: "Persi",      color: "bg-ink/10 text-ink" },
];
const PROP_TYPES = ["hotel", "agriturismo", "bnb", "villa", "yacht", "spa", "restaurant", "camping", "village", "other"];

function AutoCampaignPanel({ onChange }) {
  const [camp, setCamp] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dailyLimit, setDailyLimit] = useState(20);
  const [startDate, setStartDate] = useState("");

  const load = async () => {
    const [c, s] = await Promise.all([api.get("/outreach/campaign"), api.get("/prospects/stats")]);
    setCamp(c.data || { enabled: false, daily_limit: 20 });
    setStats(s.data);
    if (c.data?.daily_limit) setDailyLimit(c.data.daily_limit);
    if (c.data?.start_date) setStartDate(c.data.start_date);
  };
  useEffect(() => { load(); }, []);

  const save = async (enabled) => {
    setBusy(true);
    try {
      const r = await api.post("/outreach/campaign", {
        daily_limit: dailyLimit,
        // PRESERVE existing config (never overwrite with less-cautious defaults)
        send_window_utc: camp?.send_window_utc || [7, 8, 9],
        follow_up_days: camp?.follow_up_days || [3, 7, 14],
        enabled: enabled ?? !camp?.enabled,
        start_date: startDate || null,
        ramp_up: camp?.ramp_up || [2, 3, 5, 7, 10, 12, 12, 15, 15, 18, 18, 20, 20, 20, 20],
      });
      setCamp(r.data.campaign);
      toast.success(r.data.campaign.enabled ? `Campagna ATTIVA · cap max ${dailyLimit}/giorno (ramp-up cauto)` : "Campagna in pausa");
      onChange?.();
    } catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  const queueAll = async () => {
    setBusy(true);
    try {
      const r = await api.post("/outreach/campaign/queue");
      toast.success(`${r.data.queued} prospect messi in coda`);
      load(); onChange?.();
    } catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  const VOLUMES = [
    { v: 5,  l: "5",  d: "Ultra-cauto · dominio nuovo" },
    { v: 10, l: "10", d: "Cauto · settimana 1-2" },
    { v: 15, l: "15", d: "Standard · post warm-up" },
    { v: 20, l: "20", d: "Full · dopo primi risultati" },
  ];

  return (
    <div className="rounded-2xl bg-gradient-to-br from-canvas to-sand/40 border border-line p-6 lg:p-7" data-testid="auto-campaign-panel">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full grid place-items-center flex-shrink-0 ${camp?.enabled ? "bg-olive/20 text-olive" : "bg-stone/10 text-stone"}`}>
            <Sparkle weight={camp?.enabled ? "fill" : "regular"} size={20} />
          </div>
          <div>
            <div className="text-xs tracking-widest uppercase text-stone">Campagna Automatica</div>
            <h3 className="font-serif text-xl text-ink mt-0.5">
              {camp?.enabled ? (
                <span>
                  Invii <em className="italic text-olive">ATTIVI</em> · oggi <strong>{camp?.today_cap ?? "—"}</strong> · cap max {camp?.daily_limit}/giorno · lun-ven 9:00-11:00
                </span>
              ) : (
                <span>Campagna <em className="italic">in pausa</em></span>
              )}
            </h3>
            <p className="text-sm text-stone mt-1 max-w-2xl leading-relaxed">
              L'AI genera email personalizzate per ogni struttura, le invia in batch tra le 9-11 (orario Italia) rispettando le regole anti-spam GDPR
              e programma follow-up automatici a <strong>+3, +7, +14 giorni</strong> per chi non risponde.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={queueAll} variant="outline" className="rounded-full border-line h-9 text-xs" disabled={busy} data-testid="campaign-queue-all-btn">
            Metti tutti in coda
          </Button>
          <Button onClick={()=>save()} className={`rounded-full h-9 text-xs ${camp?.enabled ? "bg-terracotta hover:bg-terracotta/90 text-canvas" : "bg-olive hover:bg-olive/90 text-canvas"}`} disabled={busy} data-testid="campaign-toggle-btn">
            {camp?.enabled ? "Pausa campagna" : "Attiva campagna"}
          </Button>
        </div>
      </div>

      {/* Volume selector: max ceiling + ramp-up preview */}
      <div className="mt-5 rounded-xl bg-canvas border border-line p-4">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-[11px] tracking-widest uppercase text-stone">Cap massimo giornaliero</div>
          <div className="text-[10px] text-stone">La <em>ramp-up</em> cauta parte da 2/giorno e cresce gradualmente — vedi sotto</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {VOLUMES.map(v => (
            <button
              key={v.v}
              onClick={() => { setDailyLimit(v.v); save(camp?.enabled); }}
              className={`text-left rounded-lg p-3 border transition ${dailyLimit === v.v ? "bg-ink text-canvas border-ink" : "bg-canvas border-line hover:border-ink/30"}`}
              data-testid={`volume-${v.v}-btn`}
            >
              <div className={`font-serif text-2xl leading-none ${dailyLimit === v.v ? "text-canvas" : "text-ink"}`}>{v.l}</div>
              <div className={`text-[11px] mt-1 ${dailyLimit === v.v ? "text-canvas/70" : "text-stone"}`}>cap max/giorno</div>
              <div className={`text-[10px] mt-1 ${dailyLimit === v.v ? "text-gold" : "text-terracotta"}`}>{v.d}</div>
            </button>
          ))}
        </div>

        {/* Real ramp-up progression */}
        {camp?.ramp_up && camp.ramp_up.length > 0 && (
          <div className="mt-4 rounded-lg bg-sand/60 border border-line p-3" data-testid="rampup-progression">
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-[11px] tracking-widest uppercase text-stone">Progressione reale (working days)</div>
              <div className="text-[10px] text-olive font-medium">Oggi cap: {camp.today_cap ?? "—"}</div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {camp.ramp_up.map((n, i) => {
                const isToday = camp.today_cap === n && i === 0; // approssimativo, il badge sotto è più accurato
                return (
                  <div key={i} className={`px-2 py-1 rounded text-[10px] font-mono ${isToday ? "bg-terracotta text-canvas" : "bg-canvas border border-line text-ink"}`} title={`Working day ${i+1}`}>
                    <span className="text-stone">D{i+1}:</span> <strong>{Math.min(n, camp.daily_limit || 20)}</strong>
                  </div>
                );
              })}
              <div className="px-2 py-1 rounded text-[10px] font-mono bg-olive/10 border border-olive/30 text-olive" title="Da qui in poi resta al cap max">
                D{camp.ramp_up.length + 1}+: <strong>{camp.daily_limit || 20}</strong>
              </div>
            </div>
            <div className="text-[10px] text-stone mt-2 leading-relaxed">
              Ogni "D" è un giorno lavorativo (lun-ven). Weekend saltati. Cap capped a <strong>{camp.daily_limit || 20}/giorno</strong> (il valore selezionato sopra).
              Ramp-up progettata per proteggere la reputazione del dominio `hello@otelya.app`.
            </div>
          </div>
        )}

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <label className="text-xs text-stone">Inizia da:</label>
          <input
            type="date"
            value={startDate?.split("T")[0] || ""}
            onChange={e => setStartDate(e.target.value)}
            onBlur={() => save(camp?.enabled)}
            className="rounded-md border border-line bg-canvas px-3 py-1.5 text-sm text-ink focus:outline-none focus:border-terracotta"
            data-testid="campaign-start-date"
          />
          <span className="text-[11px] text-stone">Lascia vuoto per partire subito (rispetta comunque la finestra oraria 9-11 lun-ven)</span>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
          <div className="rounded-xl bg-canvas border border-line p-3">
            <div className="text-[11px] uppercase tracking-widest text-stone">In coda</div>
            <div className="font-serif text-xl text-ink mt-0.5">{stats.queued || 0}</div>
          </div>
          <div className="rounded-xl bg-canvas border border-line p-3">
            <div className="text-[11px] uppercase tracking-widest text-stone">Inviate oggi</div>
            <div className="font-serif text-xl text-ink mt-0.5">{stats.sent_today || 0} / {camp?.daily_limit || 20}</div>
          </div>
          <div className="rounded-xl bg-canvas border border-line p-3">
            <div className="text-[11px] uppercase tracking-widest text-stone">Contattati</div>
            <div className="font-serif text-xl text-ink mt-0.5">{(stats.by_status?.contacted || 0) + (stats.by_status?.replied || 0) + (stats.by_status?.demo || 0) + (stats.by_status?.won || 0)}</div>
          </div>
          <div className="rounded-xl bg-canvas border border-line p-3">
            <div className="text-[11px] uppercase tracking-widest text-stone">Reply rate</div>
            <div className="font-serif text-xl text-ink mt-0.5">{stats.reply_rate}%</div>
          </div>
          <div className="rounded-xl bg-canvas border border-line p-3">
            <div className="text-[11px] uppercase tracking-widest text-stone">→ Clienti</div>
            <div className="font-serif text-xl text-olive mt-0.5">{stats.by_status?.won || 0}</div>
          </div>
        </div>
      )}

      {/* Compliance notice */}
      <div className="mt-4 rounded-xl bg-ink/95 text-canvas/90 p-4 text-xs leading-relaxed flex items-start gap-2">
        <Check weight="bold" size={14} className="text-olive flex-shrink-0 mt-0.5" />
        <div>
          <strong className="text-canvas">Regole anti-spam attive:</strong> volume max scelto sopra · finestra 9-11 Italia · lun-ven · zero allegati (solo link) · opt-out GDPR incluso · 3 follow-up max poi stop automatico · nessuna parola trigger.
        </div>
      </div>
    </div>
  );
}

function OutreachHistory() {
  const [data, setData] = useState({ total: 0, events: [], summary: {} });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // event for modal
  const [regenBusy, setRegenBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/outreach/history?kind=${filter}&limit=500`);
      setData(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const regenerateForView = async () => {
    // For historical emails (before Sep 16, 2026) that don't have body stored,
    // we can regenerate a "reasonable approximation" via the preview endpoint.
    if (!selected?.prospect_id) return;
    setRegenBusy(true);
    try {
      const r = await api.post(`/outreach/campaign/preview/regenerate?prospect_id=${selected.prospect_id}`);
      setSelected({ ...selected, body: r.data.body, subject: r.data.subject, regenerated: true });
      toast.success("Email rigenerata (approssimazione — Claude non è deterministico)");
    } catch { toast.error("Rigenerazione fallita"); }
    finally { setRegenBusy(false); }
  };

  const kindStyles = {
    sent: { bg: "bg-terracotta/10", text: "text-terracotta", label: "INVIATA" },
    followup: { bg: "bg-olive/10", text: "text-olive", label: "FOLLOW-UP" },
    "reply-received": { bg: "bg-gold/20", text: "text-ink", label: "RISPOSTA RICEVUTA" },
    "reply-sent": { bg: "bg-olive/20", text: "text-olive", label: "RISPOSTA INVIATA" },
    error: { bg: "bg-red-100", text: "text-red-700", label: "ERRORE" },
  };
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" }) : "-";
  const s = data.summary || {};
  return (
    <div className="space-y-6" data-testid="outreach-history-view">
      <div className="flex items-baseline gap-3">
        <div className="w-1.5 h-8 rounded-full bg-terracotta"></div>
        <div>
          <div className="text-xs uppercase tracking-widest text-canvas/60">Storico Email</div>
          <h2 className="font-serif text-3xl text-canvas">Tutte le email · inviate e ricevute</h2>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="rounded-xl bg-canvas p-4 border border-line" data-testid="history-kpi-sent">
          <div className="text-xs text-stone">INVIATE</div><div className="font-serif text-2xl text-terracotta">{s.sent || 0}</div>
        </div>
        <div className="rounded-xl bg-canvas p-4 border border-line" data-testid="history-kpi-followup">
          <div className="text-xs text-stone">FOLLOW-UP</div><div className="font-serif text-2xl text-olive">{s.followup || 0}</div>
        </div>
        <div className="rounded-xl bg-canvas p-4 border border-line" data-testid="history-kpi-received">
          <div className="text-xs text-stone">RISPOSTE RICEVUTE</div><div className="font-serif text-2xl text-ink">{s.received || 0}</div>
        </div>
        <div className="rounded-xl bg-canvas p-4 border border-line" data-testid="history-kpi-reply-sent">
          <div className="text-xs text-stone">RISPOSTE INVIATE</div><div className="font-serif text-2xl text-olive">{s.reply_sent || 0}</div>
        </div>
        <div className="rounded-xl bg-canvas p-4 border border-line" data-testid="history-kpi-errors">
          <div className="text-xs text-stone">ERRORI</div><div className="font-serif text-2xl text-red-700">{s.errors || 0}</div>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap" data-testid="history-filters">
        {[["all","Tutto"],["sent","Inviate"],["received","Ricevute"],["followup","Follow-up"],["error","Errori"]].map(([k,l])=> (
          <button key={k} onClick={()=>setFilter(k)}
            data-testid={`history-filter-${k}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest rounded-full transition ${filter===k? "bg-terracotta text-canvas" : "bg-canvas text-stone hover:bg-terracotta/10"}`}>
            {l}
          </button>
        ))}
      </div>
      <div className="rounded-2xl bg-canvas border border-line overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-stone" data-testid="history-loading">Caricamento…</div>
        ) : data.events.length === 0 ? (
          <div className="p-8 text-center text-stone" data-testid="history-empty">
            Nessun evento ancora. Le email inviate/ricevute appariranno qui in tempo reale.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-sand text-xs uppercase tracking-widest text-stone">
              <tr>
                <th className="text-left px-4 py-3">Data</th>
                <th className="text-left px-4 py-3">Tipo</th>
                <th className="text-left px-4 py-3">Struttura</th>
                <th className="text-left px-4 py-3">Contatto</th>
                <th className="text-left px-4 py-3">Subject / Intent</th>
                <th className="text-left px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {data.events.map((e, i) => {
                const st = kindStyles[e.kind] || { bg:"bg-line/50", text:"text-stone", label: e.kind };
                const hasBody = !!(e.body && e.body.length > 0);
                return (
                  <tr key={i} className="border-t border-line/40 hover:bg-sand/30 cursor-pointer" onClick={() => setSelected(e)} data-testid={`history-row-${i}`}>
                    <td className="px-4 py-3 text-stone whitespace-nowrap">{fmtDate(e.at)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${st.bg} ${st.text}`}>{st.label}</span>
                    </td>
                    <td className="px-4 py-3 text-ink">
                      <div className="font-medium">{e.prospect_name || "—"}</div>
                      <div className="text-xs text-stone">{[e.prospect_city, e.prospect_region].filter(Boolean).join(" · ")}</div>
                    </td>
                    <td className="px-4 py-3 text-stone text-xs break-all max-w-[220px]">{e.email || "—"}</td>
                    <td className="px-4 py-3 text-ink max-w-[380px]">
                      <div className="truncate">{e.subject || "—"}</div>
                      {e.intent && <div className="text-xs text-stone mt-1">intent: <span className="text-olive">{e.intent}</span>{e.reply_lang && ` · ${e.reply_lang.toUpperCase()}`}</div>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <span className={`text-xs ${hasBody ? "text-terracotta" : "text-stone/50"}`} data-testid={`history-open-${i}`}>
                        {hasBody ? "Leggi →" : "Apri →"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Email body modal */}
      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="history-email-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-ink">
              {selected?.prospect_name || "—"}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone">Data</div>
                  <div className="text-ink">{fmtDate(selected.at)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-stone">A</div>
                  <div className="text-ink break-all">{selected.email}</div>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-stone">Subject</div>
                <div className="text-ink font-medium">{selected.subject || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-stone">Corpo email {selected.regenerated && <span className="text-terracotta">(rigenerato · non identico all'originale)</span>}</div>
                {selected.body ? (
                  <pre className="mt-1 whitespace-pre-wrap font-sans text-sm text-ink bg-sand/60 rounded-lg p-4 border border-line/50 leading-relaxed max-h-[420px] overflow-y-auto" data-testid="history-email-body">
                    {selected.body}
                  </pre>
                ) : (
                  <div className="mt-2 rounded-lg bg-gold/15 border border-gold/40 p-4 text-xs text-stone leading-relaxed">
                    <strong className="text-ink">Corpo non salvato</strong> — questa email è stata inviata prima del 16 Set 2026 12:30 CEST, quando abbiamo iniziato a salvare il corpo completo di ogni invio.
                    Da questo momento in poi ogni email inviata dal worker automatico salverà il corpo completo.
                    <div className="mt-3">
                      <Button onClick={regenerateForView} disabled={regenBusy || !selected.prospect_id} className="rounded-full bg-terracotta text-canvas hover:bg-terracotta/90 text-xs" data-testid="history-regenerate-btn">
                        {regenBusy ? "Genero…" : "Rigenera testo approssimativo con Claude"}
                      </Button>
                      <div className="text-[10px] text-stone mt-2">
                        Nota: il testo rigenerato NON sarà identico a quello inviato (Claude non è deterministico). Struttura, tono e regole sono gli stessi.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OutreachPreview() {
  const [data, setData] = useState({ days: [], queue_size: 0 });
  const [skipped, setSkipped] = useState({ total: 0, recoverable: 0, permanent: 0, counts_by_reason: {}, items: [] });
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(3);
  const [regenerating, setRegenerating] = useState(null);
  const [preflighting, setPreflighting] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [subTab, setSubTab] = useState("upcoming"); // upcoming | excluded
  const lastQueueChangeRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [prev, skp] = await Promise.all([
        api.get(`/outreach/campaign/preview?days=${days}`),
        api.get("/outreach/campaign/skipped"),
      ]);
      setData(prev.data);
      setSkipped(skp.data);
      // Track queue change signature — if it changed since last load, warn user
      const newSig = prev.data?.queue_last_changed_at || null;
      if (lastQueueChangeRef.current && newSig && lastQueueChangeRef.current !== newSig) {
        toast.info("Ordine coda modificato: anteprime rigenerate", { duration: 4000 });
      }
      lastQueueChangeRef.current = newSig;
    } catch (e) {
      toast.error("Errore caricamento anteprima");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const regenerate = async (pid) => {
    setRegenerating(pid);
    try {
      await api.post(`/outreach/campaign/preview/regenerate?prospect_id=${pid}`);
      toast.success("Anteprima rigenerata");
      await load();
    } catch (e) {
      toast.error("Rigenerazione fallita");
    } finally { setRegenerating(null); }
  };

  const runPreflight = async () => {
    if (!confirm("Eseguo il bounce guard su TUTTA la coda?\nOgni prospect con dominio non recapitabile sarà marcato come 'skipped' automaticamente.")) return;
    setPreflighting(true);
    try {
      const r = await api.post("/outreach/campaign/preflight");
      const invMsg = r.data.preview_invalidated > 0 ? ` · ${r.data.preview_invalidated} anteprime invalidate` : "";
      toast.success(`Bounce Guard: ${r.data.scanned} scanned · ${r.data.kept} kept · ${r.data.skipped} skipped${invMsg}`);
      await load();
    } catch (e) {
      toast.error("Bounce guard fallito");
    } finally { setPreflighting(false); }
  };

  const runRecovery = async () => {
    setRecovering(true);
    try {
      const r = await api.post("/outreach/campaign/recover");
      if (r.data.recovered > 0) {
        toast.success(`Recupero: ${r.data.recovered} rimessi in coda · ${r.data.still_failing} ancora non recapitabili`);
      } else {
        toast.info(`Recupero: nessun prospect recuperabile ora (${r.data.scanned} testati, ${r.data.still_failing} ancora down)`);
      }
      await load();
    } catch (e) {
      toast.error("Recupero fallito");
    } finally { setRecovering(false); }
  };

  const toggle = (pid) => setExpanded(s => ({ ...s, [pid]: !s[pid] }));
  const fmtDay = (iso) => {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" });
  };

  const totalPreviews = data.days.reduce((n, d) => n + (d.prospects?.length || 0), 0);
  const pending = data.days.reduce((n, d) => n + (d.prospects?.filter(p => p.pending).length || 0), 0);

  return (
    <div className="space-y-6" data-testid="outreach-preview-view">
      <div className="flex items-baseline gap-3">
        <div className="w-1.5 h-8 rounded-full bg-terracotta"></div>
        <div>
          <div className="text-xs uppercase tracking-widest text-canvas/60">Anteprima Prossimi Invii</div>
          <h2 className="font-serif text-3xl text-canvas">Cosa partirà nei prossimi giorni</h2>
        </div>
      </div>

      <div className="flex gap-2" data-testid="preview-subtabs">
        <button onClick={() => setSubTab("upcoming")}
          data-testid="preview-subtab-upcoming"
          className={`px-4 py-2 text-xs uppercase tracking-widest rounded-full transition ${subTab === "upcoming" ? "bg-terracotta text-canvas" : "bg-sand text-stone hover:bg-terracotta/10"}`}>
          Anteprime prossimi invii <span className="ml-1 opacity-70">({data.queue_size || 0})</span>
        </button>
        <button onClick={() => setSubTab("excluded")}
          data-testid="preview-subtab-excluded"
          className={`px-4 py-2 text-xs uppercase tracking-widest rounded-full transition ${subTab === "excluded" ? "bg-terracotta text-canvas" : "bg-sand text-stone hover:bg-terracotta/10"}`}>
          Prospect esclusi <span className="ml-1 opacity-70">({skipped.total || 0}{skipped.recoverable > 0 ? ` · ${skipped.recoverable} recuperabili` : ""})</span>
        </button>
      </div>

      {subTab === "upcoming" && (
      <>
      <div className="rounded-2xl bg-canvas p-5 border border-line" data-testid="preview-controls">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <div className="text-xs uppercase tracking-widest text-stone">Finestra</div>
            <div className="flex gap-1">
              {[1, 3, 5].map(n => (
                <button key={n} onClick={() => setDays(n)}
                  data-testid={`preview-window-${n}`}
                  className={`px-3 py-1.5 text-xs uppercase tracking-widest rounded-full transition ${days === n ? "bg-terracotta text-canvas" : "bg-sand text-stone hover:bg-terracotta/10"}`}>
                  {n} {n === 1 ? "giorno" : "giorni"}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-stone">
              <span className="text-ink font-medium">{data.queue_size || 0}</span> in coda · <span className="text-ink font-medium">{totalPreviews}</span> in anteprima
              {pending > 0 && <span className="text-terracotta"> · {pending} da generare</span>}
            </div>
            <Button onClick={load} disabled={loading} variant="ghost" className="rounded-full text-stone hover:bg-terracotta/10 text-xs" data-testid="preview-refresh-btn">
              <ArrowClockwise size={14} weight="bold" className="mr-1" />
              {loading ? "Carico…" : "Refresh"}
            </Button>
            <Button onClick={runPreflight} disabled={preflighting} className="rounded-full bg-olive text-canvas hover:bg-olive/90 text-xs" data-testid="preview-bounce-guard-btn">
              {preflighting ? "Scansione…" : "Bounce Guard"}
            </Button>
          </div>
        </div>
        {pending > 0 && (
          <div className="mt-3 text-xs text-stone bg-gold/20 rounded-lg p-3" data-testid="preview-pending-notice">
            {pending} anteprima/e non ancora generata/e — clicca <strong>Refresh</strong> per continuare (max 8 generazioni per chiamata per non stressare Claude).
          </div>
        )}
      </div>

      {loading && (
        <div className="rounded-2xl bg-canvas p-8 border border-line text-center text-stone" data-testid="preview-loading">
          Genero le anteprime con Claude Sonnet 4.6…<br />
          <span className="text-xs">Può richiedere ~8-10 secondi per email</span>
        </div>
      )}

      {!loading && data.days.length === 0 && (
        <div className="rounded-2xl bg-canvas p-8 border border-line text-center text-stone" data-testid="preview-empty">
          Nessuna anteprima disponibile. Assicurati di avere una campagna attiva con prospect in coda.
        </div>
      )}

      {!loading && data.days.map((day, i) => (
        <div key={day.date} className="rounded-2xl bg-canvas border border-line overflow-hidden" data-testid={`preview-day-${i}`}>
          <div className="bg-sand px-5 py-3 flex items-center justify-between border-b border-line">
            <div>
              <div className="text-xs uppercase tracking-widest text-stone">Working Day {i + 1}</div>
              <div className="font-serif text-lg text-ink capitalize">{fmtDay(day.date)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-stone">Cap</div>
              <div className="font-serif text-2xl text-terracotta">{day.cap}</div>
            </div>
          </div>
          <div className="divide-y divide-line/40">
            {day.prospects.length === 0 ? (
              <div className="p-6 text-center text-stone text-sm" data-testid={`preview-day-${i}-empty`}>
                Nessun prospect da inviare in questo giorno (coda esaurita o cap = 0).
              </div>
            ) : day.prospects.map((entry, j) => (
              <div key={entry.prospect.id} className="p-5" data-testid={`preview-day-${i}-item-${j}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs uppercase tracking-widest text-stone">#{j + 1}</span>
                      <span className="font-medium text-ink">{entry.prospect.property_name}</span>
                      {entry.prospect.property_type && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-olive/10 text-olive">{entry.prospect.property_type}</span>
                      )}
                      {entry.cached && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-line/50 text-stone" title="Preview cached (non rigenerata)">cache</span>
                      )}
                      {entry.pending && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-gold/30 text-ink">da generare</span>
                      )}
                      {entry.error && (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-red-100 text-red-700">errore</span>
                      )}
                    </div>
                    <div className="text-xs text-stone mt-1 break-all">
                      {[entry.prospect.city, entry.prospect.region].filter(Boolean).join(" · ")} → <span className="text-ink">{entry.prospect.email}</span>
                    </div>
                    {entry.subject && (
                      <div className="mt-2 text-sm text-ink"><span className="text-xs uppercase tracking-widest text-stone">Subject:</span> {entry.subject}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {entry.body && (
                      <Button onClick={() => toggle(entry.prospect.id)} variant="ghost" className="rounded-full text-xs text-terracotta hover:bg-terracotta/10" data-testid={`preview-toggle-${entry.prospect.id}`}>
                        {expanded[entry.prospect.id] ? "Nascondi" : "Leggi email"}
                      </Button>
                    )}
                    {entry.body && (
                      <Button onClick={() => regenerate(entry.prospect.id)} disabled={regenerating === entry.prospect.id} variant="ghost" className="rounded-full text-xs text-stone hover:bg-olive/10" data-testid={`preview-regen-${entry.prospect.id}`}>
                        {regenerating === entry.prospect.id ? "Genero…" : "Rigenera"}
                      </Button>
                    )}
                  </div>
                </div>
                {expanded[entry.prospect.id] && entry.body && (
                  <pre className="mt-3 whitespace-pre-wrap font-sans text-sm text-ink bg-sand/60 rounded-lg p-4 border border-line/50 leading-relaxed" data-testid={`preview-body-${entry.prospect.id}`}>
                    {entry.body}
                  </pre>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {!loading && data.note && (
        <div className="rounded-xl bg-gold/10 border border-gold/40 p-4 text-xs text-stone leading-relaxed" data-testid="preview-disclaimer">
          <strong className="text-ink">Nota:</strong> {data.note}
        </div>
      )}
      </>
      )}

      {subTab === "excluded" && (
        <SkippedProspectsView
          skipped={skipped}
          loading={loading}
          recovering={recovering}
          onRecoverNow={runRecovery}
          onReload={load}
        />
      )}
    </div>
  );
}


function SkippedProspectsView({ skipped, loading, recovering, onRecoverNow, onReload }) {
  const REASON_LABELS = {
    mx_nxdomain: { label: "Dominio inesistente", tone: "recover" },
    mx_no_nameservers: { label: "Nameserver irraggiungibili", tone: "recover" },
    mx_no_mx: { label: "Nessun record MX", tone: "recover" },
    mx_no_mx_no_a: { label: "Nessun MX né A", tone: "recover" },
    "mx_dns_err:EmptyLabel": { label: "Email malformata", tone: "perm" },
    mx_invalid_format: { label: "Formato email non valido", tone: "perm" },
    mx_invalid_tld: { label: "TLD non valido (.local/.test)", tone: "perm" },
    invalid_tld_local: { label: "TLD di test (.local)", tone: "perm" },
    invalid_email_format: { label: "Formato email non valido", tone: "perm" },
    duplicate_email: { label: "Email duplicata (già in coda)", tone: "perm" },
  };
  const isRecoverable = (r) => (REASON_LABELS[r]?.tone === "recover") || r?.startsWith("mx_dns_err:") && r !== "mx_dns_err:EmptyLabel";
  const fmt = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day:"2-digit", month:"short", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—";

  return (
    <div className="space-y-4" data-testid="excluded-view">
      <div className="rounded-2xl bg-canvas p-5 border border-line">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-stone">Prospect esclusi dalla campagna</div>
            <h3 className="font-serif text-xl text-ink mt-1">{skipped.total || 0} prospect · {skipped.recoverable || 0} recuperabili · {skipped.permanent || 0} permanenti</h3>
            <p className="text-xs text-stone mt-2 leading-relaxed">
              Prossimo tentativo automatico di recupero: <strong className="text-ink">{skipped.next_recovery_at_utc || "07:00 UTC (Lun-Ven)"}</strong>.
              Il sistema ri-testa il DNS per i domini che hanno fallito con errori transitori (NXDOMAIN, nameserver down) e rimette in coda quelli tornati raggiungibili — <strong>senza alcuna azione manuale richiesta</strong>.
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={onReload} disabled={loading} variant="ghost" className="rounded-full text-stone hover:bg-terracotta/10 text-xs" data-testid="excluded-refresh-btn">
              <ArrowClockwise size={14} weight="bold" className="mr-1" />
              Refresh
            </Button>
            <Button onClick={onRecoverNow} disabled={recovering} className="rounded-full bg-olive text-canvas hover:bg-olive/90 text-xs" data-testid="excluded-recover-now-btn">
              {recovering ? "Scansione DNS…" : "Retry recupero ora"}
            </Button>
          </div>
        </div>
      </div>

      {skipped.total === 0 ? (
        <div className="rounded-2xl bg-canvas p-8 border border-line text-center text-stone" data-testid="excluded-empty">
          Nessun prospect escluso. La coda è pulita.
        </div>
      ) : (
        <div className="rounded-2xl bg-canvas border border-line overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-sand text-xs uppercase tracking-widest text-stone">
              <tr>
                <th className="text-left px-4 py-3">Struttura</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Motivo esclusione</th>
                <th className="text-left px-4 py-3">Stato</th>
                <th className="text-left px-4 py-3">Escluso il</th>
              </tr>
            </thead>
            <tbody>
              {skipped.items.map((it, i) => {
                const meta = REASON_LABELS[it.skipped_reason] || { label: it.skipped_reason || "sconosciuto", tone: it.skipped_reason?.startsWith("mx_") ? "recover" : "perm" };
                const recoverable = isRecoverable(it.skipped_reason);
                return (
                  <tr key={it.id} className="border-t border-line/40 hover:bg-sand/30" data-testid={`excluded-row-${i}`}>
                    <td className="px-4 py-3 text-ink">
                      <div className="font-medium">{it.property_name || "—"}</div>
                      <div className="text-xs text-stone">{[it.city, it.region].filter(Boolean).join(" · ")}</div>
                    </td>
                    <td className="px-4 py-3 text-stone text-xs break-all max-w-[220px]">{it.email || "—"}</td>
                    <td className="px-4 py-3 text-ink text-xs">{meta.label}<div className="text-[10px] text-stone mt-1 font-mono">{it.skipped_reason}</div></td>
                    <td className="px-4 py-3">
                      {recoverable ? (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-olive/15 text-olive" title="Verrà auto-testato ogni giorno alle 07:00 UTC">Recupero auto</span>
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-line/50 text-stone" title="Errore permanente — non recuperabile">Permanente</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-stone text-xs whitespace-nowrap">{fmt(it.skipped_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



function DraftReplyInbox() {
  const [replies, setReplies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [draftSubject, setDraftSubject] = useState("");
  const [extraTo, setExtraTo] = useState("");
  const [draftCc, setDraftCc] = useState("");
  const [draftConf, setDraftConf] = useState(false);
  const [draftBody, setDraftBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [intentFilter, setIntentFilter] = useState(""); // "" | hot_lead | question | opt_out | neutral

  const load = async () => {
    try {
      const q = new URLSearchParams({ status: filter });
      if (intentFilter) q.set("intent", intentFilter);
      const r = await api.get(`/outreach/replies?${q}`);
      setReplies(r.data || []);
    } catch { toast.error("Errore caricamento"); }
  };
  useEffect(() => { load(); }, [filter, intentFilter]);

  const openReply = (r) => {
    setSelected(r);
    setDraftSubject(r.ai_draft?.subject || `Re: ${r.subject || ""}`);
    setDraftBody(r.ai_draft?.body || "");
  };

  const regenerate = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post(`/outreach/replies/${selected.id}/draft`);
      setDraftSubject(r.data.subject);
      setDraftBody(r.data.body);
      toast.success(`Draft rigenerato · intent: ${r.data.intent}`);
      load();
    } catch { toast.error("Errore generazione"); }
    finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!selected || !draftBody.trim()) return;
    setBusy(true);
    try {
      await api.post("/outreach/replies/send", {
        reply_id: selected.id, subject: draftSubject, body: draftBody, extra_to: extraTo, cc: draftCc, confidential: draftConf,
      });
      toast.success("Risposta inviata!");
      setSelected(null); setDraftBody(""); setDraftSubject(""); setExtraTo(""); setDraftCc(""); setDraftConf(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore invio"); }
    finally { setBusy(false); }
  };

  const ignoreReply = async () => {
    if (!selected) return;
    if (!window.confirm("Ignora questa risposta senza rispondere?")) return;
    setBusy(true);
    try {
      await api.post(`/outreach/replies/${selected.id}/ignore`);
      toast.success("Risposta ignorata");
      setSelected(null); load();
    } catch { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  const INTENT_STYLES = {
    "interested": { c: "bg-olive/15 text-olive", l: "Interessato" },
    "demo-request": { c: "bg-terracotta/15 text-terracotta", l: "Richiesta prova gratuita" },
    "pricing": { c: "bg-gold/15 text-[#8a6d16]", l: "Domanda prezzo" },
    "info-request": { c: "bg-ink/10 text-ink", l: "Info generica" },
    "opt-out": { c: "bg-red-100 text-red-800", l: "Opt-out" },
  };

  return (
    <div className="space-y-6" data-testid="draft-reply-inbox">
      <div className="rounded-2xl bg-ink text-canvas p-6 lg:p-7">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs tracking-widest uppercase text-canvas/60 mb-1">Draft-Reply Inbox</div>
            <h2 className="font-serif text-3xl leading-tight">
              Risposte alla campagna · <em className="italic text-gold">draft AI pronti</em>
            </h2>
            <p className="text-sm text-canvas/80 mt-2 max-w-2xl leading-relaxed">
              Quando un prospect risponde alla tua cold email, Claude Sonnet 4.6 prepara subito la migliore risposta possibile.
              Tu leggi, approvi con 1 click, oppure modifichi 2 righe prima di inviare. Zero email lasciate senza risposta.
            </p>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <ComposeEmail />
            <div className="flex gap-2">
              {["pending", "sent", "ignored"].map(f => (
                <button key={f} onClick={()=>setFilter(f)}
                  className={`px-3 py-1.5 rounded-full text-xs ${filter === f ? "bg-canvas text-ink" : "bg-canvas/10 text-canvas/70 hover:bg-canvas/20"}`}
                  data-testid={`filter-${f}`}>
                  {f === "pending" ? "Da rispondere" : f === "sent" ? "Inviate" : "Ignorate"}
                </button>
              ))}
            </div>
            <div className="flex gap-1 flex-wrap justify-end">
              <button onClick={()=>setIntentFilter("")}
                className={`px-2.5 py-1 rounded-full text-[10px] ${intentFilter === "" ? "bg-gold text-ink" : "bg-canvas/10 text-canvas/60 hover:bg-canvas/20"}`}
                data-testid="intent-filter-all">Tutte</button>
              <button onClick={()=>setIntentFilter("hot_lead")}
                className={`px-2.5 py-1 rounded-full text-[10px] ${intentFilter === "hot_lead" ? "bg-terracotta text-canvas" : "bg-canvas/10 text-canvas/60 hover:bg-canvas/20"}`}
                data-testid="intent-filter-hot">🔥 Hot Lead</button>
              <button onClick={()=>setIntentFilter("question")}
                className={`px-2.5 py-1 rounded-full text-[10px] ${intentFilter === "question" ? "bg-olive text-canvas" : "bg-canvas/10 text-canvas/60 hover:bg-canvas/20"}`}
                data-testid="intent-filter-question">❓ Domande</button>
              <button onClick={()=>setIntentFilter("neutral")}
                className={`px-2.5 py-1 rounded-full text-[10px] ${intentFilter === "neutral" ? "bg-stone text-canvas" : "bg-canvas/10 text-canvas/60 hover:bg-canvas/20"}`}
                data-testid="intent-filter-neutral">💬 Neutre</button>
              <button onClick={()=>setIntentFilter("opt_out")}
                className={`px-2.5 py-1 rounded-full text-[10px] ${intentFilter === "opt_out" ? "bg-ink text-canvas" : "bg-canvas/10 text-canvas/60 hover:bg-canvas/20"}`}
                data-testid="intent-filter-optout">🚫 Opt-out</button>
            </div>
          </div>
        </div>
      </div>

      {replies.length === 0 ? (
        <div className="rounded-2xl border border-line bg-canvas p-10 text-center">
          <ChatCircleText size={48} className="mx-auto text-stone/40 mb-3" />
          <p className="font-serif text-lg text-ink">Nessuna risposta {filter === "pending" ? "in attesa" : filter}.</p>
          <p className="text-sm text-stone mt-2">
            Le risposte compaiono qui automaticamente quando arrivano al webhook Resend Inbound / Cloudflare Email Routing.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left: reply list */}
          <div className="space-y-3">
            {replies.map(r => (
              <button key={r.id} onClick={()=>openReply(r)}
                className={`w-full text-left rounded-xl border p-4 transition ${selected?.id === r.id ? "border-terracotta bg-sand/40" : "border-line bg-canvas hover:border-ink/30"}`}
                data-testid={`reply-item-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs text-stone truncate">{r.from_name} · {r.from_email}</div>
                    <div className="font-serif text-base text-ink truncate mt-0.5">{r.subject || "(senza oggetto)"}</div>
                    <div className="text-xs text-stone mt-1 line-clamp-2">{(r.body_text || "").slice(0, 160)}</div>
                  </div>
                  {r.intent && INTENT_STYLES[r.intent] && (
                    <span className={`text-[10px] rounded-full px-2 py-0.5 whitespace-nowrap ${INTENT_STYLES[r.intent].c}`}>
                      {INTENT_STYLES[r.intent].l}
                    </span>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-2 text-[10px] text-stone">
                  <span>{new Date(r.received_at).toLocaleString("it-IT")}</span>
                  {r.ai_draft && <span className="text-olive">· AI draft pronto</span>}
                </div>
              </button>
            ))}
          </div>

          {/* Right: draft editor */}
          {selected ? (
            <div className="rounded-xl border border-line bg-canvas p-5 sticky top-6 self-start" data-testid="draft-editor">
              <div className="border-b border-line pb-3 mb-3">
                <div className="text-[11px] uppercase tracking-widest text-stone">Loro risposta</div>
                <div className="font-serif text-base text-ink mt-1">{selected.subject}</div>
                <p className="text-sm text-stone mt-2 whitespace-pre-wrap max-h-40 overflow-auto">{selected.body_text || "(vuoto)"}</p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-widest text-terracotta">Il tuo draft (Claude Sonnet 4.6)</div>
                  <button onClick={regenerate} disabled={busy} className="text-[11px] text-stone hover:text-ink underline" data-testid="regenerate-draft-btn">
                    ↻ Rigenera
                  </button>
                </div>
                <input value={extraTo} onChange={e=>setExtraTo(e.target.value)}
                  className="w-full rounded-md border border-line px-3 py-2 text-sm mb-2 focus:outline-none focus:border-terracotta"
                  placeholder={`A: ${selected.from_email || ""} + altri indirizzi (separati da virgola)`} data-testid="draft-extra-to-input" />
                <input value={draftCc} onChange={e=>setDraftCc(e.target.value)}
                  className="w-full rounded-md border border-line px-3 py-2 text-sm mb-2 focus:outline-none focus:border-terracotta"
                  placeholder="Cc: per conoscenza (facoltativo)" data-testid="draft-cc-input" />
                <label className="flex items-center gap-2 text-xs text-stone mb-2" data-testid="draft-confidential">
                  <input type="checkbox" checked={draftConf} onChange={e=>setDraftConf(e.target.checked)} data-testid="draft-confidential-toggle" />
                  Aggiungi la nota di riservatezza e tutela legale
                </label>
                <input value={draftSubject} onChange={e=>setDraftSubject(e.target.value)}
                  className="w-full rounded-md border border-line px-3 py-2 text-sm mb-2 focus:outline-none focus:border-terracotta"
                  placeholder="Subject" data-testid="draft-subject-input" />
                <textarea value={draftBody} onChange={e=>setDraftBody(e.target.value)} rows={10}
                  className="w-full rounded-md border border-line px-3 py-2 text-sm focus:outline-none focus:border-terracotta font-mono leading-relaxed"
                  placeholder="Corpo email..." data-testid="draft-body-textarea" />
              </div>
              <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
                <ThreadButton prospectId={selected.prospect_id} />
                <Button onClick={ignoreReply} variant="outline" disabled={busy}
                  className="rounded-full border-line h-9 text-xs" data-testid="draft-ignore-btn">
                  Ignora
                </Button>
                <div className="flex gap-2">
                  <Button onClick={()=>setSelected(null)} variant="ghost" disabled={busy}
                    className="rounded-full h-9 text-xs" data-testid="draft-cancel-btn">
                    Chiudi
                  </Button>
                  <Button onClick={sendReply} disabled={busy || !draftBody.trim()}
                    className="rounded-full bg-terracotta hover:bg-terracotta/90 text-canvas h-9 text-xs" data-testid="draft-send-btn">
                    <PaperPlaneTilt weight="fill" size={12} className="mr-1"/> Invia ora
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-line bg-canvas/50 p-10 text-center text-stone text-sm">
              Seleziona una risposta a sinistra per vedere il draft AI e inviare.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TrustDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/trust-dashboard/stats")
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return null;
  if (!data) return null;
  const d = data;
  const anyAlert = d.opt_outs_today > 0 || d.stuck_at_max > 0 || d.near_conversion_count > 0;
  return (
    <div className="rounded-2xl border border-ink bg-ink text-canvas p-5 lg:p-6" data-testid="trust-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-gold">Trust Dashboard · Auto-dialog</div>
          <h3 className="font-serif text-xl mt-1">
            {anyAlert ? "Serve un tuo colpo d'occhio" : "Tutto scorre, nessuna attenzione richiesta"}
          </h3>
        </div>
        <div className="text-[10px] uppercase tracking-widest text-canvas/50">
          Snapshot {new Date(d.snapshot_at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-xl bg-canvas/5 border border-canvas/10 p-4" data-testid="trust-kpi-sent">
          <div className="text-[10px] uppercase tracking-widest text-canvas/60">Auto-reply ieri</div>
          <div className="font-serif text-3xl mt-1 text-canvas">{d.auto_replies_24h}</div>
          <div className="text-[11px] text-canvas/50 mt-1">Email mandate da Claude</div>
        </div>
        <div className={`rounded-xl p-4 border ${d.near_conversion_count > 0 ? "bg-gold/15 border-gold/40" : "bg-canvas/5 border-canvas/10"}`} data-testid="trust-kpi-near">
          <div className="text-[10px] uppercase tracking-widest text-canvas/60">Vicino a conversione</div>
          <div className="font-serif text-3xl mt-1 text-canvas">{d.near_conversion_count}</div>
          <div className="text-[11px] text-canvas/50 mt-1">Prospect al 3° scambio+</div>
        </div>
        <div className={`rounded-xl p-4 border ${d.opt_outs_today > 0 ? "bg-terracotta/15 border-terracotta/40" : "bg-canvas/5 border-canvas/10"}`} data-testid="trust-kpi-optouts">
          <div className="text-[10px] uppercase tracking-widest text-canvas/60">Opt-out oggi</div>
          <div className="font-serif text-3xl mt-1 text-canvas">{d.opt_outs_today}</div>
          <div className="text-[11px] text-canvas/50 mt-1">"No grazie" rilevati</div>
        </div>
        <div className={`rounded-xl p-4 border ${d.stuck_at_max > 0 ? "bg-canvas/15 border-canvas/30" : "bg-canvas/5 border-canvas/10"}`} data-testid="trust-kpi-stuck">
          <div className="text-[10px] uppercase tracking-widest text-canvas/60">Fermi a 5 scambi</div>
          <div className="font-serif text-3xl mt-1 text-canvas">{d.stuck_at_max}</div>
          <div className="text-[11px] text-canvas/50 mt-1">Push manuale consigliato</div>
        </div>
      </div>

      {d.near_conversion_list?.length > 0 && (
        <div className="mt-5 pt-5 border-t border-canvas/15">
          <div className="text-[11px] uppercase tracking-widest text-gold mb-3">
            ▸ Prospect caldi al 3° scambio+ — intervieni tu adesso
          </div>
          <div className="space-y-1">
            {d.near_conversion_list.map(p => (
              <div
                key={p.id}
                className="flex items-center justify-between py-2 px-3 rounded-lg bg-canvas/5 hover:bg-canvas/10 transition text-sm"
                data-testid={`trust-near-prospect-${p.id}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gold text-ink text-xs font-medium flex-shrink-0">
                    {p.auto_dialog_turn_count}
                  </div>
                  <div className="min-w-0">
                    <div className="text-canvas truncate">{p.property_name || p.name || p.email}</div>
                    <div className="text-[11px] text-canvas/50 truncate">{p.email} · {p.city || "—"}</div>
                  </div>
                </div>
                <div className="text-[11px] text-canvas/60 ml-3 flex-shrink-0">
                  {p.last_contact_at && new Date(p.last_contact_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-canvas/60 italic">
            Tip: al 3°-4° scambio è il momento d'oro per una tua email personale. Claude li ha scaldati — adesso chiudi tu con una call-to-action diretta ("quando vuoi che ci sentiamo?").
          </p>
        </div>
      )}

      {Object.keys(d.turn_distribution || {}).length > 0 && (
        <div className="mt-4 pt-4 border-t border-canvas/15">
          <div className="text-[10px] uppercase tracking-widest text-canvas/50 mb-2">Distribuzione scambi · {d.in_dialog_total} prospect in dialogo</div>
          <div className="flex gap-1 items-end h-10">
            {[1,2,3,4,5].map(t => {
              const n = d.turn_distribution[String(t)] || 0;
              const max = Math.max(...Object.values(d.turn_distribution || {1:1}), 1);
              const h = Math.max(4, (n / max) * 36);
              return (
                <div key={t} className="flex-1 flex flex-col items-center gap-1">
                  <div
                    className={`w-full rounded-sm ${t >= 3 ? "bg-gold" : "bg-canvas/30"}`}
                    style={{ height: `${h}px` }}
                    title={`Turn ${t}: ${n} prospect`}
                  />
                  <div className="text-[9px] text-canvas/50">T{t}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function AutoDialogToggle() {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/user/auto-dialog-hot-leads")
      .then(r => setEnabled(!!r.data?.auto_dialog_hot_leads))
      .finally(() => setLoading(false));
  }, []);
  const toggle = async () => {
    const next = !enabled;
    try {
      await api.post("/user/auto-dialog-hot-leads", { enabled: next });
      setEnabled(next);
      toast.success(next
        ? "Auto-dialog ON · Claude ora dialoga da solo con i prospect caldi (max 5 scambi, 30-min delay)"
        : "Auto-dialog OFF · torni al controllo manuale");
    } catch { toast.error("Errore"); }
  };
  if (loading) return null;
  return (
    <div className="rounded-2xl border border-line bg-canvas p-5 lg:p-6 flex items-start gap-4" data-testid="auto-dialog-toggle">
      <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${enabled ? "bg-gold/20 text-gold" : "bg-sand text-stone"}`}>
        {enabled ? "✦" : "✧"}
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-stone">Auto-dialog · prospect caldi</div>
            <h3 className="font-serif text-lg text-ink mt-1">
              {enabled ? "Claude dialoga da solo fino alla conversione" : "Dialogo manuale (default)"}
            </h3>
          </div>
          <button
            onClick={toggle}
            className={`relative inline-flex items-center h-7 w-14 rounded-full transition ${enabled ? "bg-gold" : "bg-stone/30"}`}
            data-testid="auto-dialog-switch"
          >
            <span className={`absolute h-5 w-5 bg-canvas rounded-full shadow transition-all ${enabled ? "left-8" : "left-1"}`} />
          </button>
        </div>
        <p className="mt-2 text-xs text-stone leading-relaxed">
          Quando ON, se un prospect <strong>clicca un link</strong> e poi <strong>risponde a una tua email</strong>, Claude genera e invia da solo la risposta (max 5 scambi per prospect, delay 30 min per sembrare umano, stop su "no grazie"/opt-out). Vedi tutto in Draft-Reply Inbox.
        </p>
      </div>
    </div>
  );
}

function ColdOutreach() {
  const [cbKey, setCbKey] = useState(0);
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState(null);
  const [clickStats, setClickStats] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [selected, setSelected] = useState(null);
  const [outreach, setOutreach] = useState(null);
  const [busyGen, setBusyGen] = useState(false);
  const [form, setForm] = useState({ property_name: "", property_type: "hotel", city: "", region: "", email: "", phone: "", whatsapp: "", website: "", source: "manual", notes: "" });

  const load = async () => {
    const [p, s, c] = await Promise.all([
      api.get("/prospects"),
      api.get("/prospects/stats"),
      api.get("/track/stats").catch(() => ({ data: null })),
    ]);
    setProspects(p.data); setStats(s.data); setClickStats(c.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.property_name.trim()) return toast.error("Nome struttura obbligatorio");
    try {
      await api.post("/prospects", form);
      toast.success("Prospect aggiunto");
      setForm({ property_name: "", property_type: "hotel", city: "", region: "", email: "", phone: "", whatsapp: "", website: "", source: "manual", notes: "" });
      setShowAdd(false); load();
    } catch { toast.error("Errore"); }
  };

  const importCsv = async () => {
    if (!csvText.trim()) return toast.error("CSV vuoto");
    try {
      const r = await api.post("/prospects/import", { csv: csvText });
      toast.success(`${r.data.inserted} prospect importati`);
      setCsvText(""); setShowImport(false); load();
    } catch { toast.error("Errore import"); }
  };

  const importFromLeads = async () => {
    if (!window.confirm("Importare fino a 77 decision-maker dal pool di lead scrapati? (Score ≥65 · Umbria/Toscana/Lazio)")) return;
    try {
      const r = await api.post("/outreach/import-from-leads", { limit: 77, min_score: 65 });
      toast.success(`${r.data.imported} decision-maker importati dal pool!`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore import da pool"); }
  };

  const moveTo = async (p, status) => {
    try { await api.patch(`/prospects/${p.id}`, { status }); toast.success(`→ ${status}`); load(); }
    catch { toast.error("Errore"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare ${p.property_name}?`)) return;
    try { await api.delete(`/prospects/${p.id}`); toast.success("Eliminato"); load(); }
    catch { toast.error("Errore"); }
  };

  const openOutreach = (p, channel) => {
    setSelected({ ...p, channel });
    setOutreach(null);
  };

  const generate = async () => {
    if (!selected) return;
    setBusyGen(true); setOutreach(null);
    try {
      const r = await api.post("/prospects/generate-outreach", { prospect_id: selected.id, channel: selected.channel });
      setOutreach(r.data);
    } catch { toast.error("Errore generazione"); }
    finally { setBusyGen(false); }
  };

  const send = async () => {
    if (!outreach || !selected) return;
    try {
      await api.post("/prospects/send-outreach", {
        prospect_id: selected.id, channel: selected.channel,
        subject: outreach.subject, body: outreach.body,
      });
      toast.success(`${selected.channel === "email" ? "Email" : "WhatsApp"} inviato!`);
      setSelected(null); setOutreach(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore invio");
    }
  };

  const grouped = OUTREACH_COLUMNS.reduce((acc, c) => { acc[c.k] = prospects.filter(p => (p.status || "new") === c.k); return acc; }, {});

  return (
    <div className="space-y-6" data-testid="cold-outreach">
      {/* Header + stats */}
      <div className="rounded-2xl bg-ink text-canvas p-6 lg:p-8 relative overflow-hidden">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-canvas/60">Cold Outreach</div>
            <h2 className="font-serif text-2xl lg:text-3xl mt-2 leading-tight">
              Il tuo <em className="italic text-gold">funnel di acquisizione</em> automatizzato.
            </h2>
            <p className="mt-2 text-canvas/80 text-sm leading-relaxed">
              Aggiungi hotel, B&B, agriturismi target. L'AI di Otelya genera email e WhatsApp personalizzati. Invii in 1 tap. Traccia tutto.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={()=>setShowAdd(true)} className="rounded-full bg-canvas text-ink hover:bg-gold" data-testid="outreach-add-btn">
              <Plus weight="bold" size={14} className="mr-1"/> Nuovo prospect
            </Button>
            <Button onClick={()=>setShowImport(true)} variant="ghost" className="rounded-full text-canvas hover:bg-canvas/10" data-testid="outreach-import-btn">
              Importa CSV
            </Button>
            <Button onClick={importFromLeads} variant="ghost" className="rounded-full text-canvas hover:bg-canvas/10" data-testid="outreach-import-leads-btn">
              <Sparkle weight="fill" size={14} className="mr-1 text-gold"/> Importa da pool (77 DM)
            </Button>
          </div>
        </div>
      </div>

      {/* Automated Campaign Panel */}
      <AutoCampaignPanel onChange={load} />

      {/* Trust Dashboard · colpo d'occhio su auto-dialog */}
      <TrustDashboard />

      {/* Auto-dialog toggle: AI dialoga da sola con prospect caldi (click + reply) */}
      <AutoDialogToggle />

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <KPI testid="kpi-prospects-total"   label="Prospect totali" value={stats.total} hint="Nel funnel"/>
          <KPI testid="kpi-prospects-contact" label="Contattati"      value={(stats.by_status.contacted||0) + (stats.by_status.replied||0) + (stats.by_status.demo||0) + (stats.by_status.won||0) + (stats.by_status.lost||0)} hint="Almeno 1 outreach"/>
          <KPI testid="kpi-prospects-reply"   label="Reply rate"      value={`${stats.reply_rate}%`} hint="Su contattati"/>
          <KPI testid="kpi-prospects-clicks"  label="Click outreach"  value={clickStats?.total_clicks ?? 0} hint={`${clickStats?.unique_clickers ?? 0} prospect unici`}/>
          <KPI testid="kpi-prospects-conv"    label="Conversion rate" value={`${stats.conversion_rate}%`} hint="→ Clienti paganti"/>
        </div>
      )}

      <UpcomingCallbacks refreshKey={cbKey} />

      {/* Kanban board */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="outreach-board">
        {OUTREACH_COLUMNS.map(col => (
          <div key={col.k} className="rounded-2xl bg-sand/40 p-3 min-h-[300px]" data-testid={`col-${col.k}`}>
            <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs uppercase tracking-widest ${col.color} mb-3`}>
              {col.l} · {grouped[col.k].length}
            </div>
            <div className="space-y-2">
              {grouped[col.k].map(p => (
                <div key={p.id} className="rounded-xl bg-canvas border border-line p-3 text-sm" data-testid={`prospect-${p.id}`}>
                  <div className="font-serif text-base text-ink leading-tight">{p.property_name}</div>
                  <div className="mt-1 text-xs text-stone">{p.property_type}{p.city && ` · ${p.city}`}</div>
                  {p.email && <div className="mt-1 text-xs text-stone truncate">✉ {p.email}</div>}
                  {p.whatsapp && <div className="text-xs text-stone">📱 {p.whatsapp}</div>}
                  <div className="mt-2 flex gap-1 flex-wrap">
                    {p.email && (
                      <button onClick={()=>openOutreach(p, "email")} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-terracotta/10 text-terracotta hover:bg-terracotta hover:text-canvas transition" data-testid={`email-${p.id}`}>
                        ✉ Email AI
                      </button>
                    )}
                    {(p.whatsapp || p.phone) && (
                      <button onClick={()=>openOutreach(p, "whatsapp")} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-olive/10 text-olive hover:bg-olive hover:text-canvas transition" data-testid={`wa-${p.id}`}>
                        📱 WA AI
                      </button>
                    )}
                    <select value={p.status || "new"} onChange={(e)=>moveTo(p, e.target.value)} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-canvas border border-line" data-testid={`move-${p.id}`}>
                      {OUTREACH_COLUMNS.map(c => <option key={c.k} value={c.k}>{c.l}</option>)}
                    </select>
                    <CallbackButton p={p} onSaved={() => { load(); setCbKey(k => k + 1); }} />
                    <button onClick={()=>remove(p)} className="text-[10px] px-2 py-1 rounded-full text-stone hover:text-terracotta" data-testid={`del-${p.id}`}>×</button>
                  </div>
                </div>
              ))}
              {grouped[col.k].length === 0 && (
                <div className="text-xs text-stone italic text-center py-4">Vuoto</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg" data-testid="add-prospect-dialog">
          <DialogHeader><DialogTitle>Nuovo Prospect</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input placeholder="Nome struttura *" value={form.property_name} onChange={e=>setForm({...form, property_name: e.target.value})} className="w-full rounded-lg border border-line px-3 py-2 text-sm" data-testid="prospect-name"/>
            <div className="grid grid-cols-2 gap-2">
              <select value={form.property_type} onChange={e=>setForm({...form, property_type: e.target.value})} className="rounded-lg border border-line px-3 py-2 text-sm">
                {PROP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input placeholder="Città" value={form.city} onChange={e=>setForm({...form, city: e.target.value})} className="rounded-lg border border-line px-3 py-2 text-sm"/>
            </div>
            <input placeholder="Regione" value={form.region} onChange={e=>setForm({...form, region: e.target.value})} className="w-full rounded-lg border border-line px-3 py-2 text-sm"/>
            <input placeholder="Email" value={form.email} onChange={e=>setForm({...form, email: e.target.value})} className="w-full rounded-lg border border-line px-3 py-2 text-sm"/>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="WhatsApp (+39...)" value={form.whatsapp} onChange={e=>setForm({...form, whatsapp: e.target.value})} className="rounded-lg border border-line px-3 py-2 text-sm"/>
              <input placeholder="Website" value={form.website} onChange={e=>setForm({...form, website: e.target.value})} className="rounded-lg border border-line px-3 py-2 text-sm"/>
            </div>
            <textarea placeholder="Note (feature specifica, contesto...)" value={form.notes} onChange={e=>setForm({...form, notes: e.target.value})} className="w-full rounded-lg border border-line px-3 py-2 text-sm min-h-[80px]"/>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={()=>setShowAdd(false)}>Annulla</Button>
            <Button onClick={create} className="bg-olive text-canvas" data-testid="prospect-save">Aggiungi</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Importa Prospect da CSV</DialogTitle></DialogHeader>
          <p className="text-xs text-stone mb-2">Formato header (case-sensitive): <code className="bg-sand px-1 rounded">property_name,property_type,city,region,email,phone,whatsapp,website,source,notes</code></p>
          <textarea placeholder="Incolla qui il CSV..." value={csvText} onChange={e=>setCsvText(e.target.value)} className="w-full rounded-lg border border-line px-3 py-2 text-xs font-mono min-h-[240px]" data-testid="csv-textarea"/>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={()=>setShowImport(false)}>Annulla</Button>
            <Button onClick={importCsv} className="bg-olive text-canvas" data-testid="csv-import-btn">Importa</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Outreach AI Dialog */}
      <Dialog open={!!selected} onOpenChange={(o)=>{ if (!o) { setSelected(null); setOutreach(null); }}}>
        <DialogContent className="max-w-2xl" data-testid="outreach-dialog">
          <DialogHeader>
            <DialogTitle>
              {selected?.channel === "email" ? "Email AI" : "WhatsApp AI"} · {selected?.property_name}
            </DialogTitle>
          </DialogHeader>
          {!outreach && (
            <div className="text-center py-8">
              <Button onClick={generate} disabled={busyGen} className="rounded-full bg-terracotta text-canvas hover:bg-terracotta/90" data-testid="gen-outreach-btn">
                {busyGen ? "L'AI di Otelya sta scrivendo…" : "✨ Genera messaggio personalizzato"}
              </Button>
              <p className="mt-3 text-xs text-stone">L'AI di Otelya genererà un messaggio unico basato su nome, città, tipo di struttura e le tue note interne.</p>
            </div>
          )}
          {outreach && (
            <div className="space-y-3">
              {selected?.channel === "email" && (
                <input value={outreach.subject} onChange={e=>setOutreach({...outreach, subject: e.target.value})} placeholder="Subject" className="w-full rounded-lg border border-line px-3 py-2 text-sm font-medium" data-testid="outreach-subject"/>
              )}
              <textarea value={outreach.body} onChange={e=>setOutreach({...outreach, body: e.target.value})} className="w-full rounded-lg border border-line px-3 py-2 text-sm min-h-[280px]" data-testid="outreach-body"/>
              <div className="flex justify-between items-center">
                <Button variant="ghost" onClick={generate} disabled={busyGen} className="text-stone text-xs">🔁 Rigenera</Button>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={()=>{ setSelected(null); setOutreach(null); }}>Annulla</Button>
                  <Button onClick={send} className="bg-olive text-canvas rounded-full" data-testid="send-outreach-btn">
                    <PaperPlaneTilt weight="fill" size={12} className="mr-1"/> Invia ora
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ----- FAQ CMS -----
function VoiceCalls() {
  const [calls, setCalls] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [voiceConfig, setVoiceConfig] = useState({ number_ready: false });

  const load = async () => {
    try {
      const r = await api.get("/voice/calls?limit=30");
      setCalls(r.data);
      const cfg = await api.get("/voice/config").catch(() => ({ data: { number_ready: false } }));
      setVoiceConfig(cfg.data);
    } catch (e) {
      setCalls([]);
    }
  };
  useEffect(() => { load(); }, []);

  if (calls === null) return <div className="text-stone">Carico chiamate...</div>;

  return (
    <div className="space-y-6" data-testid="voice-calls">
      <div className="rounded-3xl bg-gradient-to-br from-terracotta via-terracotta to-ink text-canvas p-8 lg:p-10 relative overflow-hidden">
        <div className="relative z-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-canvas/10 px-3 py-1 text-[11px] tracking-widest uppercase text-canvas/90 mb-4">
            <Phone weight="bold" size={12} /> Otelya Voice
          </div>
          <h2 className="font-serif text-3xl lg:text-4xl leading-tight">
            La receptionist AI che risponde al telefono in italiano.
          </h2>
          <p className="mt-3 text-canvas/80 leading-relaxed max-w-xl">
            Ogni chiamata è gestita 24/7 con voce naturale (Amazon Polly Bianca), turno-per-turno con Claude, handoff intelligente al team quando serve. Trascrizioni + summary automatico salvati in Inbox.
          </p>
          {!voiceConfig.number_ready && (
            <div className="mt-4 rounded-xl bg-canvas/10 border border-canvas/20 p-3 text-xs text-canvas/90 leading-relaxed">
              <strong>Numero italiano non ancora attivo.</strong> Il backend Voice è pronto — devi collegare un numero +39 Twilio. <a href="#" onClick={(e) => { e.preventDefault(); alert('Segui la guida in Dashboard → AI Config → Numero telefono, oppure chiedi ad Alfio per la procedura Twilio Console (30 secondi setup).'); }} className="underline">Come attivarlo →</a>
            </div>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <KPI testid="kpi-voice-total" label="Chiamate totali" value={calls.length} hint="Ultimi 30" />
        <KPI testid="kpi-voice-completed" label="Completate" value={calls.filter(c => c.status === "completed").length} hint="Con turni AI" />
        <KPI testid="kpi-voice-avg-turns" label="Turni medi" value={calls.length ? Math.round(calls.reduce((s, c) => s + (c.turns_count || 0), 0) / calls.length / 2) : 0} hint="Scambi user↔AI" />
        <KPI testid="kpi-voice-avg-duration" label="Durata media" value={calls.length ? Math.round(calls.reduce((s, c) => s + (c.total_seconds || 0), 0) / calls.length) + "s" : "—"} hint="Secondi/chiamata" />
      </div>

      {/* Recent calls list */}
      <div className="rounded-2xl border border-line bg-canvas p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-medium text-ink">Chiamate recenti</div>
          <button onClick={load} className="text-xs text-stone hover:text-terracotta transition-colors">
            <ArrowClockwise size={14} className="inline mr-1" /> Aggiorna
          </button>
        </div>
        {calls.length === 0 ? (
          <div className="text-center py-12 text-stone/70">
            <Phone weight="thin" size={44} className="mx-auto mb-2 opacity-40" />
            <div className="text-sm">Nessuna chiamata ancora.</div>
            <div className="text-xs mt-1">Le chiamate ricevute sul numero collegato appariranno qui.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {calls.map((c) => (
              <button key={c.id} onClick={() => setSelectedCall(c)} className="w-full text-left flex items-start gap-3 p-3 rounded-lg hover:bg-sand/40 transition-colors" data-testid={`voice-call-${c.id}`}>
                <div className="w-9 h-9 rounded-full bg-terracotta/10 text-terracotta grid place-items-center flex-shrink-0">
                  <Phone size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs text-stone mb-0.5 flex-wrap">
                    <span className="font-medium text-ink">{c.from_number || "Anonimo"}</span>
                    <span>·</span>
                    <span>{new Date(c.started_at).toLocaleString("it-IT")}</span>
                    <span>·</span>
                    <span>{c.total_seconds || 0}s</span>
                    <span>·</span>
                    <span>{Math.round((c.turns_count || 0) / 2)} turni</span>
                    <Badge className={`ml-auto ${c.status === "completed" ? "bg-olive/20 text-olive" : "bg-stone/10 text-stone"}`}>{c.status || "in-progress"}</Badge>
                  </div>
                  <div className="text-sm text-ink line-clamp-2">
                    {c.ai_summary || (c.turns_preview?.[0]?.text) || "Nessun contenuto"}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Call detail modal */}
      {selectedCall && (
        <Dialog open={!!selectedCall} onOpenChange={(o) => !o && setSelectedCall(null)}>
          <DialogContent className="max-w-2xl bg-canvas">
            <DialogHeader>
              <DialogTitle>Trascrizione chiamata · {selectedCall.from_number || "Anonimo"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-xs text-stone flex flex-wrap gap-3">
                <span>{new Date(selectedCall.started_at).toLocaleString("it-IT")}</span>
                <span>·</span>
                <span>Durata: {selectedCall.total_seconds || 0}s</span>
                <span>·</span>
                <span>{Math.round((selectedCall.turns_count || 0) / 2)} turni</span>
                <span>·</span>
                <span>Stato: {selectedCall.status}</span>
              </div>
              {selectedCall.ai_summary && (
                <div className="rounded-lg bg-sand/60 p-3 text-sm text-ink">
                  <div className="text-xs uppercase tracking-wider text-stone mb-1">Summary AI</div>
                  {selectedCall.ai_summary}
                </div>
              )}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {(selectedCall.turns || []).map((t, i) => (
                  <div key={i} className={`p-3 rounded-lg text-sm ${t.role === "user" ? "bg-terracotta/10 border-l-2 border-terracotta" : "bg-canvas border border-line"}`}>
                    <div className="text-[10px] uppercase tracking-wider text-stone mb-1">
                      {t.role === "user" ? "Ospite" : "Otelya"}
                      {t.confidence !== undefined && ` · confidence ${Math.round(t.confidence * 100)}%`}
                    </div>
                    <div className="text-ink">{t.text}</div>
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function WidgetEmbed() {
  const [data, setData] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const iframeRef = useRef(null);

  const load = async () => {
    const [t, conv] = await Promise.all([
      api.get("/widget/token").then(r => r.data),
      api.get("/widget/conversations?limit=10").then(r => r.data).catch(() => []),
    ]);
    setData(t);
    setConversations(conv);
  };
  useEffect(() => { load(); }, []);

  const handleCopy = () => {
    if (!data?.snippet) return;
    navigator.clipboard.writeText(data.snippet);
    setCopied(true);
    toast.success("Snippet copiato negli appunti");
    setTimeout(() => setCopied(false), 2500);
  };

  const handleRegenerate = async () => {
    if (!confirm("Rigenerare il token invalidereebbe il widget attualmente installato sui siti dei clienti. Continuare?")) return;
    setRegenerating(true);
    try {
      await api.post("/widget/token/regenerate");
      await load();
      toast.success("Token rigenerato. Aggiorna lo snippet sui siti dei clienti.");
    } catch { toast.error("Errore durante la rigenerazione"); }
    setRegenerating(false);
  };

  const openPreview = () => {
    // Open a preview page that loads the widget on a blank test site
    if (data?.widget_token) {
      window.open(`/widget-preview.html?token=${data.widget_token}`, "_blank");
    }
  };

  if (!data) return <div className="text-stone">Carico configurazione widget...</div>;

  return (
    <div className="space-y-6" data-testid="widget-embed">
      <div className="rounded-3xl bg-gradient-to-br from-ink via-ink to-olive text-canvas p-8 lg:p-10 relative overflow-hidden">
        <div className="relative z-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-canvas/10 px-3 py-1 text-[11px] tracking-widest uppercase text-canvas/80 mb-4">
            <Code weight="bold" size={12} /> Widget Embed
          </div>
          <h2 className="font-serif text-3xl lg:text-4xl leading-tight">
            Trasforma il sito di ogni cliente in un canale AI 24/7.
          </h2>
          <p className="mt-3 text-canvas/70 leading-relaxed max-w-xl">
            Copia questo snippet e chiedi ai tuoi clienti di incollarlo nel loro sito (prima del tag <code className="bg-canvas/10 px-1.5 py-0.5 rounded text-xs">&lt;/body&gt;</code>). Otelya risponderà agli ospiti in oltre 30 lingue con il contesto della struttura, e ogni widget porta un link discreto "Powered by Otelya" al tuo dominio → moltiplicatore virale di backlink SEO.
          </p>
        </div>
      </div>

      {/* Incentivi widget — 3 leve per convincere il cliente a installarlo */}
      <div className="rounded-2xl border border-line bg-gradient-to-br from-canvas to-sand/40 p-6 lg:p-8" data-testid="widget-incentives">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-terracotta/10 text-terracotta px-3 py-1 text-[11px] tracking-widest uppercase font-medium mb-2">
              <Gift weight="bold" size={12} /> 3 leve per convincerli
            </div>
            <h3 className="font-serif text-2xl text-ink leading-tight">Cosa offrire al cliente quando gli chiedi di installarlo.</h3>
            <p className="text-sm text-stone mt-1 max-w-2xl">Non chiedere solo "installa lo snippet". Presentagli il pacchetto: <strong>tre leve</strong> che rendono l'adesione una scelta ovvia — e attivano una crescita virale del tuo network.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Incentivo 1: 20% sconto a vita */}
          <div className="rounded-xl bg-canvas border border-line p-5 flex flex-col" data-testid="incentive-discount">
            <div className="w-10 h-10 rounded-full bg-gold/20 text-[#8a6d16] grid place-items-center mb-3">
              <TrendUp weight="bold" size={18} />
            </div>
            <div className="text-xs tracking-widest uppercase text-stone mb-1">Incentivo #1</div>
            <div className="font-serif text-xl text-ink leading-tight mb-2">20% sconto <em className="italic text-terracotta">a vita</em></div>
            <p className="text-sm text-stone leading-relaxed flex-1">
              Chi installa il widget entro il periodo di lancio (primi 3 mesi dal go-live) ottiene <strong>−20% ricorrente per sempre</strong> sul suo piano Otelya. Non un mese, non un anno: per sempre.
            </p>
            <div className="mt-4 pt-4 border-t border-line/60 text-xs text-stone">
              <div className="flex items-baseline gap-2">
                <span className="line-through">€199/mese</span>
                <span className="text-ink font-medium text-base">€159/mese</span>
                <span className="text-terracotta">·40€ al mese risparmiati</span>
              </div>
              <div className="mt-1 text-[11px]">= <strong>€480/anno</strong> di sconto ricorrente</div>
            </div>
          </div>

          {/* Incentivo 2: Certified Partner Badge */}
          <div className="rounded-xl bg-canvas border border-line p-5 flex flex-col" data-testid="incentive-badge">
            <div className="w-10 h-10 rounded-full bg-terracotta/15 text-terracotta grid place-items-center mb-3">
              <MedalMilitary weight="bold" size={18} />
            </div>
            <div className="text-xs tracking-widest uppercase text-stone mb-1">Incentivo #2</div>
            <div className="font-serif text-xl text-ink leading-tight mb-2">Badge <em className="italic text-terracotta">Certified Partner</em></div>
            <p className="text-sm text-stone leading-relaxed flex-1">
              Otelya inserisce la struttura nella pagina pubblica <code className="bg-sand/60 px-1.5 py-0.5 rounded text-[11px]">otelya.app/partners</code> con foto, link diretto e badge "Certified 2026". Un backlink SEO in DR alto + credibilità presso i loro ospiti.
            </p>
            <div className="mt-4 pt-4 border-t border-line/60 text-xs text-stone">
              <div>Vantaggio SEO stimato: <strong className="text-ink">+15-25% traffico organico</strong> nei primi 6 mesi.</div>
              <div className="mt-1 text-[11px]">Include: profilo verificato · badge scaricabile · menzione LinkedIn Otelya</div>
            </div>
          </div>

          {/* Incentivo 3: Referral 2x2 */}
          <div className="rounded-xl bg-canvas border border-line p-5 flex flex-col" data-testid="incentive-referral">
            <div className="w-10 h-10 rounded-full bg-olive/20 text-olive grid place-items-center mb-3">
              <ShareNetwork weight="bold" size={18} />
            </div>
            <div className="text-xs tracking-widest uppercase text-stone mb-1">Incentivo #3</div>
            <div className="font-serif text-xl text-ink leading-tight mb-2">Referral <em className="italic text-terracotta">2×2</em></div>
            <p className="text-sm text-stone leading-relaxed flex-1">
              Per ogni struttura amica che si iscrive con il loro link referral: <strong>2 mesi gratis</strong> a chi porta + <strong>2 mesi gratis</strong> a chi arriva. Nessun limite: 5 referral = 10 mesi gratis.
            </p>
            <div className="mt-4 pt-4 border-t border-line/60 text-xs text-stone">
              <div>Con 6 referral in un anno: <strong className="text-ink">12 mesi gratis</strong> = <span className="text-terracotta">€1.428 risparmiati</span></div>
              <div className="mt-1 text-[11px]">Link tracciato univoco · nessuna scadenza · combinabile con l'incentivo #1</div>
            </div>
          </div>
        </div>

        {/* Esempio pratico + CTA */}
        <div className="mt-6 rounded-xl bg-ink/95 text-canvas p-5 lg:p-6">
          <div className="flex items-start gap-3 flex-wrap">
            <div className="w-8 h-8 rounded-full bg-terracotta grid place-items-center flex-shrink-0">
              <Sparkle weight="fill" size={14} className="text-canvas" />
            </div>
            <div className="flex-1 min-w-[280px]">
              <div className="text-[11px] tracking-widest uppercase text-canvas/60 mb-1">Esempio · Come proporlo al cliente</div>
              <p className="text-sm text-canvas/90 leading-relaxed italic">
                "Ti mando lo snippet: 30 secondi di installazione. In cambio hai <strong className="not-italic">20% a vita</strong>, entri come <strong className="not-italic">Partner Certificato</strong> sulla nostra pagina pubblica e ricevi <strong className="not-italic">2 mesi gratis</strong> per ogni collega che ci porti. Se in un anno mi presenti 6 strutture, hai Otelya gratis per tutto l'anno. Ci sto?"
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <a href="#top" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="widget-snippet-code"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); }} className="inline-flex items-center gap-1.5 rounded-full bg-terracotta hover:bg-terracotta/90 text-canvas px-4 py-2 text-xs font-medium transition-colors" data-testid="incentives-cta">
                Copia snippet & scrivigli <ArrowUpRight weight="bold" size={12} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Snippet card */}
      <div className="rounded-2xl border border-line bg-canvas p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-medium text-ink">Il tuo snippet</div>
            <div className="text-xs text-stone mt-0.5">Da incollare prima di <code>&lt;/body&gt;</code> sul sito della struttura</div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={openPreview} className="rounded-full text-xs" data-testid="widget-preview-btn">
              <ArrowUpRight size={14} className="mr-1" /> Anteprima
            </Button>
            <Button size="sm" onClick={handleCopy} className="rounded-full bg-terracotta text-canvas hover:bg-terracotta/90 text-xs" data-testid="widget-copy-btn">
              {copied ? <><Check size={14} className="mr-1" /> Copiato</> : <><Copy size={14} className="mr-1" /> Copia snippet</>}
            </Button>
          </div>
        </div>
        <pre className="bg-ink text-canvas rounded-xl p-4 text-xs overflow-x-auto whitespace-pre-wrap break-all font-mono" data-testid="widget-snippet-code">
{data.snippet}
        </pre>
        <div className="mt-4 flex items-center justify-between">
          <div className="text-xs text-stone">
            Token univoco: <code className="bg-sand/60 px-1.5 py-0.5 rounded text-[11px]">{data.widget_token}</code>
          </div>
          <button onClick={handleRegenerate} disabled={regenerating} className="text-xs text-stone hover:text-terracotta transition-colors disabled:opacity-50" data-testid="widget-regen-btn">
            {regenerating ? "Rigenero..." : "Rigenera token"}
          </button>
        </div>
      </div>

      {/* Installation steps */}
      <div className="rounded-2xl border border-line bg-canvas p-6">
        <div className="text-sm font-medium text-ink mb-4">Istruzioni per il cliente (3 passi)</div>
        <ol className="space-y-3 text-sm text-stone">
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-terracotta/20 text-terracotta grid place-items-center font-medium text-xs flex-shrink-0">1</span>
            <div>Accedi al pannello di amministrazione del sito web (WordPress, Wix, Squarespace, sito custom, ecc.)</div>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-terracotta/20 text-terracotta grid place-items-center font-medium text-xs flex-shrink-0">2</span>
            <div>Trova la sezione <em>"Custom HTML"</em>, <em>"Header/Footer script"</em> o <em>"Codice personalizzato"</em>. Incolla lo snippet nel <strong>footer del sito</strong> (viene applicato a tutte le pagine).</div>
          </li>
          <li className="flex gap-3">
            <span className="w-6 h-6 rounded-full bg-terracotta/20 text-terracotta grid place-items-center font-medium text-xs flex-shrink-0">3</span>
            <div>Salva e ricarica la pagina. Comparirà un bottone chat in basso a destra. Testa scrivendo <em>"Buongiorno, vorrei info sulle disponibilità"</em>.</div>
          </li>
        </ol>
      </div>

      {/* Recent conversations */}
      <div className="rounded-2xl border border-line bg-canvas p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-medium text-ink">Ultime conversazioni widget</div>
          <div className="text-xs text-stone">{conversations.length} sessioni recenti</div>
        </div>
        {conversations.length === 0 ? (
          <div className="text-center py-8 text-stone/70">
            <Chat weight="thin" size={40} className="mx-auto mb-2 opacity-40" />
            <div className="text-sm">Nessuna conversazione ancora.</div>
            <div className="text-xs mt-1">Installa il widget sul sito di un cliente per iniziare.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {conversations.map((c) => (
              <div key={c.session_id} className="flex items-start gap-3 p-3 rounded-lg hover:bg-sand/40 transition-colors" data-testid={`widget-conv-${c.session_id}`}>
                <div className="w-8 h-8 rounded-full bg-terracotta/10 text-terracotta grid place-items-center flex-shrink-0">
                  <Chat size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs text-stone mb-0.5">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${c.last_role === "user" ? "bg-terracotta" : "bg-olive"}`}></span>
                    <span>{new Date(c.last_at).toLocaleString("it-IT")}</span>
                    <span>·</span>
                    <span>{c.messages_count} messaggi</span>
                    {c.referrer && <><span>·</span><span className="truncate">{c.referrer}</span></>}
                  </div>
                  <div className="text-sm text-ink truncate">
                    <span className="text-stone">{c.last_role === "user" ? "Ospite: " : "Otelya: "}</span>
                    {c.last_text}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FaqCms() {
  const [lang, setLang] = useState("it");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState(""); const [a, setA] = useState("");
  const [editing, setEditing] = useState(null); // {id, q, a}
  const load = () => api.get(`/faq?lang=${lang}`).then(r => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [lang]);

  const create = async () => {
    if (!q.trim() || !a.trim()) return toast.error("Domanda e risposta obbligatorie");
    try {
      await api.post("/faq", { q, a, language: lang, order: items.length });
      setQ(""); setA(""); toast.success("FAQ aggiunta"); load();
    } catch { toast.error("Errore"); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/faq/${editing.id}`, { q: editing.q, a: editing.a });
      setEditing(null); toast.success("Aggiornata"); load();
    } catch { toast.error("Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa FAQ?")) return;
    try { await api.delete(`/faq/${id}`); toast.success("Eliminata"); load(); } catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6" data-testid="faq-cms">
      <div className="flex items-center justify-between">
        <p className="text-stone text-sm max-w-xl">
          Le FAQ mostrate sulla landing page pubblica. Modifica in tempo reale — le modifiche si vedono subito su otelya.app.
        </p>
        <div className="flex items-center gap-2">
          {["it","en"].map(l => (
            <button key={l} onClick={()=>setLang(l)} data-testid={`faqcms-lang-${l}`}
              className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border transition ${lang===l?"bg-ink text-canvas border-ink":"bg-canvas text-stone border-line hover:border-ink"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* Add new FAQ */}
      <div className="rounded-2xl border border-line bg-canvas p-6" data-testid="faq-new">
        <div className="font-serif text-lg text-ink mb-3">Nuova FAQ ({lang.toUpperCase()})</div>
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Domanda…"
          className="w-full rounded-lg border border-line px-3 py-2 text-sm mb-2 focus:outline-none focus:border-olive"
          data-testid="faq-input-q"/>
        <textarea value={a} onChange={e=>setA(e.target.value)} placeholder="Risposta…"
          className="w-full rounded-lg border border-line px-3 py-2 text-sm min-h-[100px] focus:outline-none focus:border-olive"
          data-testid="faq-input-a"/>
        <div className="mt-3 flex justify-end">
          <Button onClick={create} className="rounded-full bg-olive hover:bg-olivelight text-canvas" data-testid="faq-add-btn">
            <Plus weight="bold" size={14} className="mr-1"/> Aggiungi
          </Button>
        </div>
      </div>

      {/* Existing FAQ list */}
      <div className="space-y-3" data-testid="faq-list">
        {items.length === 0 && (
          <div className="text-center text-sm text-stone py-8 border border-dashed border-line rounded-2xl">
            Nessuna FAQ ancora. Aggiungi la prima qui sopra.
          </div>
        )}
        {items.map(f => (
          <div key={f.id} className="rounded-2xl border border-line bg-canvas p-5" data-testid={`faq-row-${f.id}`}>
            {editing?.id === f.id ? (
              <div>
                <input value={editing.q} onChange={e=>setEditing({...editing, q: e.target.value})}
                  className="w-full rounded-lg border border-olive px-3 py-2 text-sm mb-2 font-medium"/>
                <textarea value={editing.a} onChange={e=>setEditing({...editing, a: e.target.value})}
                  className="w-full rounded-lg border border-olive px-3 py-2 text-sm min-h-[100px]"/>
                <div className="mt-3 flex gap-2 justify-end">
                  <Button variant="ghost" onClick={()=>setEditing(null)} className="rounded-full text-xs h-8">Annulla</Button>
                  <Button onClick={saveEdit} className="rounded-full bg-olive text-canvas hover:bg-olivelight text-xs h-8" data-testid={`faq-save-${f.id}`}>Salva</Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-serif text-lg text-ink leading-snug">{f.q}</div>
                    <p className="mt-2 text-sm text-stone leading-relaxed whitespace-pre-wrap">{f.a}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={()=>setEditing({id: f.id, q: f.q, a: f.a})} className="h-8 w-8 p-0" data-testid={`faq-edit-${f.id}`}>
                      <PencilSimple size={14}/>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={()=>remove(f.id)} className="h-8 w-8 p-0 text-stone hover:text-terracotta" data-testid={`faq-delete-${f.id}`}>
                      <Trash size={14}/>
                    </Button>
                  </div>
                </div>
                {(f.helpful || f.unhelpful) ? (
                  <div className="mt-3 flex gap-3 text-xs text-stone">
                    <span className="flex items-center gap-1"><Check size={12} className="text-olive"/> {f.helpful || 0} utili</span>
                    <span className="flex items-center gap-1">✕ {f.unhelpful || 0} non utili</span>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ----- SOCIAL AUTOPILOT (Editorial Calendar) -----
const SOCIAL_PLATFORM_META = {
  instagram: { label: "Instagram", icon: InstagramLogo, color: "text-terracotta", bg: "bg-terracotta/10", shareBase: "https://www.instagram.com/" },
  facebook:  { label: "Facebook",  icon: FacebookLogo,  color: "text-olive",      bg: "bg-olive/10",      shareBase: "https://www.facebook.com/" },
  facebook_instagram: { label: "FB + IG", icon: InstagramLogo, color: "text-terracotta", bg: "bg-gold/15", shareBase: "" },
  tiktok:    { label: "TikTok",    icon: TiktokLogo,    color: "text-ink",        bg: "bg-sand",          shareBase: "https://www.tiktok.com/upload" },
  blog:      { label: "Blog",      icon: Article,       color: "text-[#8a6d16]",  bg: "bg-gold/15",       shareBase: "" },
};

function groupByDay(items) {
  const map = {};
  for (const it of items) {
    const day = (it.scheduled_for || "").slice(0, 10);
    if (!day) continue;
    (map[day] = map[day] || []).push(it);
  }
  return Object.entries(map).sort((a,b) => a[0].localeCompare(b[0]));
}

function SocialAutopilot() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyFirst, setBusyFirst] = useState(false);
  const [busyBlog, setBusyBlog] = useState(false);
  const [busyPubId, setBusyPubId] = useState(null);
  const [pageInfo, setPageInfo] = useState(null);
  const [pageInfoOpen, setPageInfoOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [templates, setTemplates] = useState([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [voiceWizardOpen, setVoiceWizardOpen] = useState(false);
  const [voicePreset, setVoicePreset] = useState({tone_style: "", formality: "voi", signature_phrases: "", avoid_words: ""});
  const load = () => api.get("/social/calendar?days=14").then(r => setData(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/social/templates").then(r => setTemplates(r.data.templates || [])).catch(()=>{});
    api.get("/user/voice-preset").then(r => setVoicePreset(r.data || voicePreset)).catch(()=>{});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyTemplate = async (tpl, platform = "facebook_instagram") => {
    try {
      const r = await api.post("/social/templates/apply", { template_id: tpl.id, platform });
      toast.success(`Post "${tpl.title}" schedulato per domani 10:00 CEST`);
      setTemplatesOpen(false);
      load();
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    }
  };

  const saveVoicePreset = async () => {
    try {
      await api.post("/user/voice-preset", voicePreset);
      toast.success("Voice preset salvato — sarà applicato ai prossimi post generati");
      setVoiceWizardOpen(false);
    } catch (e) {
      toast.error("Errore salvataggio");
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      await api.post("/social/generate-week");
      toast.success("Generazione avviata! I post appariranno tra 60-90 secondi.");
      // Poll for updates
      setTimeout(load, 30000);
      setTimeout(load, 60000);
      setTimeout(load, 90000);
    } catch (e) {
      toast.error("Errore durante la generazione.");
    } finally {
      setBusy(false);
    }
  };

  const publishFirstPost = async () => {
    if (!window.confirm("Confermi la pubblicazione del PRIMO post di lancio su Facebook + Instagram?\n\nConsumo stimato: ~1 chiamata Claude + 2 chiamate Meta. Il post andrà LIVE immediatamente.")) return;
    setBusyFirst(true);
    try {
      const r = await api.post("/social/generate-first-post");
      const okFB = r.data?.results?.facebook?.ok;
      const okIG = r.data?.results?.instagram?.ok;
      if (okFB && okIG) toast.success("Primo post pubblicato su Facebook + Instagram!");
      else if (okFB) toast.success("Post pubblicato su Facebook. IG: " + (r.data?.results?.instagram?.error || "errore"));
      else if (okIG) toast.success("Post pubblicato su Instagram. FB: " + (r.data?.results?.facebook?.error || "errore"));
      else toast.error("Pubblicazione fallita: " + JSON.stringify(r.data?.results));
      load();
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    } finally { setBusyFirst(false); }
  };

  const [busyRedist, setBusyRedist] = useState(false);
  const redistributeBlog = async () => {
    if (!window.confirm("Redistribuire le date e le immagini di TUTTI gli articoli del blog?\n\nDate: distribuite su ultimi 6 mesi (Lun/Gio preferiti).\nImmagini: 45 immagini Unsplash gratuite varie.\n\nZero crediti LLM. Idempotente.")) return;
    setBusyRedist(true);
    try {
      const r = await api.post("/admin/blog/redistribute");
      if (r.data?.ok) {
        toast.success(`✓ ${r.data.redistributed} articoli redistribuiti · ${r.data.distinct_dates} date · ${r.data.distinct_images} immagini`);
      } else toast.error("Redistribuzione fallita");
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    } finally { setBusyRedist(false); }
  };

  const [busyFixLang, setBusyFixLang] = useState(false);
  const fixBlogLanguage = async () => {
    setBusyFixLang(true);
    try {
      // Step 1: dry-run to preview changes
      const dry = await api.post("/admin/blog/fix-language", { dry_run: true });
      const mm = dry.data?.mismatched || 0;
      if (mm === 0) {
        toast.success(`✓ Blog già corretto: ${dry.data.already_correct.it} IT + ${dry.data.already_correct.en} EN. Nessuna modifica necessaria.`);
        return;
      }
      const preview = (dry.data.fixes || []).slice(0, 6).map(f => `  · [${f.from}→${f.to}] ${f.title}`).join("\n");
      const more = mm > 6 ? `\n  … e altri ${mm - 6}` : "";
      const ok = window.confirm(
        `Trovati ${mm} articoli con lingua sbagliata su ${dry.data.total_scanned} totali.\n\nEsempio (prime 6):\n${preview}${more}\n\nAggiornare il campo language nel DB? (Zero LLM cost, idempotente)`
      );
      if (!ok) return;
      // Step 2: apply
      const r = await api.post("/admin/blog/fix-language", { dry_run: false });
      toast.success(`✓ ${r.data.applied} articoli corretti · Blog EN ora ha solo articoli in inglese, blog IT solo in italiano.`);
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    } finally { setBusyFixLang(false); }
  };

  const [busyFloodCleanup, setBusyFloodCleanup] = useState(false);
  const [floodCleanupReport, setFloodCleanupReport] = useState(null);  // last result for modal
  const floodCleanup = async () => {
    setBusyFloodCleanup(true);
    try {
      // Step 1: dry-run
      const dry = await api.post("/admin/social/flood-cleanup", { hours: 72, max_keep_per_day: 2, dry_run: true });
      const n = dry.data?.excess_to_delete || 0;
      if (n === 0) {
        toast.success(`✓ Nessun post in eccesso. ${dry.data.total_published_in_window} post pubblicati in 72h · limite ${dry.data.max_keep_per_day}/giorno rispettato.`);
        setFloodCleanupReport({ ...dry.data, phase: "dry_run_clean" });
        return;
      }
      const preview = (dry.data.excess_details || []).slice(0, 6).map(f => `  · [${f.channel}] ${f.day} · ${f.topic}`).join("\n");
      const more = n > 6 ? `\n  … e altri ${n - 6}` : "";
      const ok = window.confirm(
        `TROVATI ${n} POST IN ECCESSO (ultimo 72h · limite 2/giorno).\n\nAzione:\n· Facebook: DELETE via Graph API (rimozione automatica)\n· Instagram: NON supporta delete via API — riceverai la lista degli ID da rimuovere manualmente\n· DB: marcati come "cancelled_spam_cleanup"\n\nEsempi (prime 6):\n${preview}${more}\n\nProcedere?`
      );
      if (!ok) return;
      // Step 2: apply
      const r = await api.post("/admin/social/flood-cleanup", { hours: 72, max_keep_per_day: 2, dry_run: false });
      const fbOk = (r.data.facebook_delete_results || []).filter(x => x.ok).length;
      const fbErr = (r.data.facebook_delete_results || []).filter(x => !x.ok).length;
      const igManual = (r.data.instagram_manual_delete_needed || []).length;
      const msg = `FB: ${fbOk} cancellati${fbErr ? ` · ${fbErr} errori` : ""}${igManual ? ` · IG: ${igManual} da cancellare a mano (vedi riepilogo)` : ""}`;
      if (fbErr > 0 || igManual > 0) toast.warning(msg, { duration: 8000 });
      else toast.success(msg, { duration: 6000 });
      setFloodCleanupReport({ ...r.data, phase: "applied" });
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    } finally { setBusyFloodCleanup(false); }
  };

  const seedBlog = async () => {
    if (!window.confirm("Generare 3 articoli blog iniziali (date scaglionate: 7gg fa, 3gg fa, oggi)?\n\nConsumo stimato: ~3 chiamate Claude. Immagini gratuite Unsplash.")) return;
    setBusyBlog(true);
    try {
      const r = await api.post("/social/blog/seed");
      toast.success(`${r.data.count} articoli creati e pubblicati sul blog.`);
      load();
    } catch (e) {
      toast.error("Errore blog seed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusyBlog(false); }
  };

  const checkPages = async () => {
    try {
      const r = await api.get("/social/meta/page-info");
      setPageInfo(r.data);
      setPageInfoOpen(true);
    } catch (e) {
      toast.error("Errore verifica pagine: " + (e?.response?.data?.detail || e.message));
    }
  };

  const publishNow = async (post) => {
    if (!window.confirm(`Pubblicare "${post.content.slice(0,60)}..." su Facebook + Instagram?`)) return;
    setBusyPubId(post.id);
    try {
      const r = await api.post(`/social/publish-now/${post.id}`, { platforms: ["facebook", "instagram"] });
      if (r.data?.ok) toast.success("Post pubblicato!");
      else toast.error("Pubblicazione fallita: " + JSON.stringify(r.data?.results));
      load();
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    } finally { setBusyPubId(null); }
  };

  const publishAssist = async (post) => {
    try {
      await navigator.clipboard.writeText(post.content);
      toast.success(`Caption copiata! Apri ${SOCIAL_PLATFORM_META[post.platform]?.label || post.platform} per pubblicare.`);
      await api.post("/social/mark-published", { id: post.id, kind: "social" });
      load();
      if (SOCIAL_PLATFORM_META[post.platform]?.shareBase) {
        window.open(SOCIAL_PLATFORM_META[post.platform].shareBase, "_blank");
      }
    } catch (e) {
      toast.error("Impossibile copiare la caption");
    }
  };

  // Drag & drop + date-picker rescheduling ------------------------------------
  const [dragId, setDragId] = useState(null);
  const [dragKind, setDragKind] = useState("social");
  const reschedulePost = async (post, kind, isoDate) => {
    try {
      await api.patch(`/social/posts/${post.id}/reschedule`, { scheduled_for: isoDate, kind });
      toast.success("Data aggiornata");
      load();
    } catch (e) {
      toast.error("Errore riprogrammazione: " + (e?.response?.data?.detail || e.message));
    }
  };
  const onDragStart = (post, kind) => {
    setDragId(post.id); setDragKind(kind);
  };
  const onDropOnDay = async (dayIso) => {
    if (!dragId) return;
    // Preserve time-of-day of dragged post if we have it; else 10:00 UTC
    const target = [...(data?.social||[]), ...(data?.blog||[])].find(x => x.id === dragId);
    const oldDT = target?.scheduled_for ? new Date(target.scheduled_for) : new Date();
    const [y,m,d] = dayIso.split("-").map(Number);
    const newDT = new Date(Date.UTC(y, m-1, d, oldDT.getUTCHours() || 10, oldDT.getUTCMinutes() || 0));
    await reschedulePost({id: dragId}, dragKind, newDT.toISOString());
    setDragId(null);
  };
  const onDateInput = async (post, kind, value) => {
    // value is "YYYY-MM-DDTHH:mm" from datetime-local input (browser local tz)
    if (!value) return;
    const iso = new Date(value).toISOString();
    await reschedulePost(post, kind, iso);
  };

  const deletePost = async (post, kind = "social") => {
    if (!window.confirm("Eliminare questo post?")) return;
    try {
      await api.delete(`/social/posts/${post.id}`);
      toast.success("Post eliminato");
      load();
    } catch { toast.error("Errore eliminazione"); }
  };

  // --- Next-week preview edit state ---
  const [editingPost, setEditingPost] = useState(null);     // post object being edited
  const [editingDraft, setEditingDraft] = useState("");     // textarea value
  const [savingEdit, setSavingEdit] = useState(false);
  const [regenVariants, setRegenVariants] = useState([]);   // Claude's 3 alternatives
  const [regenLoading, setRegenLoading] = useState(false);
  const [presetStyle, setPresetStyle] = useState("");       // active brand voice preset
  const PRESET_OPTIONS = [
    { k: "lirico-italiano",       l: "Lirico italiano",       d: "metafore, slow-storytelling, caldo" },
    { k: "ironico-provocatorio",  l: "Ironico provocatorio",  d: "pattern interrupt, micro-storie, spiazzante" },
    { k: "corporate-sobrio",      l: "Corporate sobrio",      d: "numeri concreti, benefici, fiducioso" },
  ];

  useEffect(() => {
    api.get("/user/voice-preset").then(r => setPresetStyle(r.data?.preset_style || "")).catch(() => {});
  }, []);
  const saveStylePreset = async (ps) => {
    try {
      await api.post("/user/voice-preset", { preset_style: ps, formality: "voi", tone_style: "", signature_phrases: "", avoid_words: "" });
      setPresetStyle(ps);
      toast.success(`Voice-preset "${PRESET_OPTIONS.find(o=>o.k===ps)?.l || ps}" attivo. Tutti i post futuri useranno questo stile.`);
    } catch (e) { toast.error("Errore salvataggio"); }
  };

  const openEditor = (post) => {
    setEditingPost(post);
    setEditingDraft(post.content || "");
    setRegenVariants([]);
  };
  const saveEditor = async () => {
    if (!editingPost) return;
    setSavingEdit(true);
    try {
      await api.patch(`/social/posts/${editingPost.id}`, { content: editingDraft });
      toast.success("Post aggiornato — andrà live con il nuovo testo");
      setEditingPost(null);
      load();
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingEdit(false);
    }
  };
  const regenerateWithClaude = async () => {
    if (!editingPost) return;
    setRegenLoading(true);
    setRegenVariants([]);
    try {
      const res = await api.post(`/social/posts/${editingPost.id}/regenerate`);
      setRegenVariants(res.data.variants || []);
      toast.success("3 varianti generate — clicca quella che preferisci");
    } catch (e) {
      toast.error("Errore Claude: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRegenLoading(false);
    }
  };

  if (!data) return <div className="text-stone">Caricamento…</div>;
  const allItems = [...data.social, ...data.blog].sort((a,b) => (a.scheduled_for||"").localeCompare(b.scheduled_for||""));
  const grouped = groupByDay(allItems);
  const totalSocial = data.social.length;
  const totalBlog = data.blog.length;

  return (
    <div className="space-y-8">
      {/* Header + CTA */}
      <div className="rounded-2xl bg-ink text-canvas p-6 lg:p-8 relative overflow-hidden">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-canvas/60">Social Autopilot</div>
          <h2 className="font-serif text-2xl lg:text-3xl mt-2 leading-tight">
            Un calendario editoriale <em className="italic text-gold">che si scrive da solo.</em>
          </h2>
          <p className="mt-3 text-canvas/80 text-sm leading-relaxed">
            <b className="text-gold">Calendario editoriale fissato per hospitality</b> — 4 blog + 8 social = 12 pezzi/settimana su fasce di picco engagement:
            <br/>· <b>Blog IT</b>: Lun 09:00 · Gio 09:00 CEST (SEO peak Italia)
            <br/>· <b>Blog EN</b>: Mar 09:00 · Ven 09:00 BST (SEO peak UK/US)
            <br/>· <b>Instagram</b>: Lun 11:00 · Mer 19:00 · Ven 11:00 · Dom 19:00 CEST + cross Sab 10:00
            <br/>· <b>Facebook</b>: Mar 13:00 · Gio 13:00 CEST + cross Sab 10:00
            <br/>Rigenerazione automatica ogni domenica 20:00 CEST. Auto-publish via Meta ogni 15min. <b className="text-gold">Trascina i post</b> per riorganizzarli.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Button onClick={publishFirstPost} disabled={busyFirst} className="rounded-full bg-gold text-ink hover:bg-canvas" data-testid="autopilot-first-post-btn">
              {busyFirst ? "Pubblicazione…" : "🚀 Pubblica primo post FB + IG"}
            </Button>
            <Button onClick={seedBlog} disabled={busyBlog} className="rounded-full bg-canvas text-ink hover:bg-gold" data-testid="autopilot-seed-blog-btn">
              {busyBlog ? "Generazione…" : "📝 Seed 3 articoli blog"}
            </Button>
            <Button onClick={redistributeBlog} disabled={busyRedist} variant="ghost" className="rounded-full text-canvas hover:bg-canvas/10 border border-canvas/20" data-testid="autopilot-redistribute-blog-btn">
              {busyRedist ? "Ridistribuzione…" : "🗓️ Redistribuisci date+immagini blog"}
            </Button>
            <Button onClick={fixBlogLanguage} disabled={busyFixLang} variant="ghost" className="rounded-full text-canvas hover:bg-canvas/10 border border-canvas/20" data-testid="autopilot-fix-language-btn">
              {busyFixLang ? "Analisi…" : "🌐 Correggi lingua blog"}
            </Button>
            <Button onClick={floodCleanup} disabled={busyFloodCleanup} variant="ghost" className="rounded-full text-canvas hover:bg-red-500/10 border border-red-400/40 text-red-100" data-testid="autopilot-flood-cleanup-btn">
              {busyFloodCleanup ? "Analisi…" : "🚨 Pulisci post duplicati"}
            </Button>
            <Button onClick={async () => {
              try {
                const r = await api.post("/admin/blog/seed-top10");
                if (r.data.created) toast.success(`✓ Articolo seedato: ${r.data.url}`);
                else toast.success("✓ Articolo già presente (nessuna azione)");
              } catch (e) {
                toast.error("Errore: " + (e?.response?.data?.detail || e.message));
              }
            }} variant="ghost" className="rounded-full text-canvas hover:bg-canvas/10 border border-canvas/20" data-testid="autopilot-seed-top10-btn">
              📊 Seed articolo Top 10
            </Button>
            <Button onClick={async () => {
              if (!window.confirm("Aggiungere Federalberghi + Confturismo + Agriturist alla pipeline Cold Outreach?\n\nVerranno inviate automaticamente nei prossimi giorni (Lun-Ven ore 8-10 UTC) con subject + body pre-scritto.")) return;
              try {
                const r = await api.post("/admin/outreach/add-associations");
                toast.success(`✓ ${r.data.created.length} prospect aggiunti, ${r.data.skipped.length} già presenti.`, { duration: 8000 });
              } catch (e) {
                toast.error("Errore: " + (e?.response?.data?.detail || e.message));
              }
            }} variant="ghost" className="rounded-full text-canvas hover:bg-canvas/10 border border-canvas/20" data-testid="autopilot-associations-btn">
              📧 Aggiungi 3 associazioni
            </Button>
            <Button onClick={()=>setTemplatesOpen(true)} variant="ghost" className="rounded-full text-canvas hover:bg-canvas/10 border border-canvas/20" data-testid="autopilot-templates-btn">
              ✨ Template pronti
            </Button>
            <Button onClick={()=>setVoiceWizardOpen(true)} variant="ghost" className="rounded-full text-canvas hover:bg-canvas/10 border border-canvas/20" data-testid="autopilot-voice-wizard-btn">
              🎙️ Voice preset
            </Button>
            <Button onClick={checkPages} variant="ghost" className="rounded-full text-canvas hover:bg-canvas/10 border border-canvas/20" data-testid="autopilot-check-pages-btn">
              📊 Verifica pagine FB/IG
            </Button>
            <Button onClick={generate} disabled={busy} variant="ghost" className="rounded-full text-canvas hover:bg-canvas/10" data-testid="autopilot-generate-btn">
              {busy ? "Generazione…" : "Genera prossima settimana"}
              <Sparkle weight="fill" size={14} className="ml-1"/>
            </Button>
            <a href="/blog" target="_blank" rel="noreferrer">
              <Button variant="ghost" className="rounded-full text-canvas hover:bg-canvas/10" data-testid="autopilot-view-blog">
                Vedi il blog pubblico <ArrowUpRight weight="bold" size={14} className="ml-1"/>
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testid="autopilot-kpi-total"    label="Post in coda"   value={totalSocial} hint="Instagram + Facebook + TikTok"/>
        <KPI testid="autopilot-kpi-blog"     label="Blog schedati"  value={totalBlog}   hint="Articoli SEO"/>
        <KPI testid="autopilot-kpi-days"     label="Giornate coperte" value={grouped.length} hint="Prossimi 14gg"/>
        <KPI testid="autopilot-kpi-time"     label="Tempo risparmiato" value={`${Math.round(totalSocial * 15/60)}h`} hint="~15 min a post"/>
      </div>

      {/* Voice-preset picker — affects all future Claude generations */}
      <div className="rounded-2xl border border-line bg-canvas p-5 lg:p-6" data-testid="voice-preset-picker">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-stone">Voice-preset · stile narrativo</div>
            <h3 className="font-serif text-xl text-ink mt-1">Il tono con cui Claude scrive i tuoi post</h3>
          </div>
          {presetStyle && (
            <div className="text-[11px] text-olive font-medium">
              Attivo: {PRESET_OPTIONS.find(o => o.k === presetStyle)?.l}
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          {PRESET_OPTIONS.map(opt => (
            <button
              key={opt.k}
              onClick={() => saveStylePreset(opt.k)}
              className={`text-left p-4 rounded-xl border transition ${
                presetStyle === opt.k
                  ? "border-gold bg-gold/5 ring-1 ring-gold"
                  : "border-line hover:border-ink/40 bg-canvas"
              }`}
              data-testid={`voice-preset-${opt.k}`}
            >
              <div className="font-serif text-base text-ink">{opt.l}</div>
              <div className="text-xs text-stone mt-1 leading-relaxed">{opt.d}</div>
              {presetStyle === opt.k && (
                <div className="mt-2 text-[10px] uppercase tracking-widest text-olive">✓ Attivo</div>
              )}
            </button>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-stone/60 italic">
          Il preset guida sia i post settimanali generati automaticamente, sia le 3 varianti di "✨ Rigenera con Claude".
        </p>
        <div className="mt-4 pt-4 border-t border-line/60 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs font-medium text-ink">Test A/B automatico 2+2 settimane</div>
            <div className="text-[11px] text-stone/70 mt-0.5">Il sistema cambia stile ogni 14 giorni: Ironico → Lirico → Corporate. Compara engagement reale.</div>
          </div>
          <Button
            onClick={async () => {
              try {
                await api.post("/user/voice-preset/ab-rotation", { enabled: true, interval_days: 14, sequence: ["ironico-provocatorio", "lirico-italiano", "corporate-sobrio"] });
                toast.success("Test A/B avviato · stile cambia il " + new Date(Date.now() + 14*86400000).toLocaleDateString("it-IT"));
              } catch { toast.error("Errore avvio"); }
            }}
            className="rounded-full bg-ink text-canvas hover:bg-olive text-xs px-4"
            data-testid="voice-preset-ab-start"
          >
            Avvia test A/B
          </Button>
        </div>
      </div>

      {/* Next 7 days preview — editable before auto-publish */}
      {(() => {
        const nowTs = Date.now();
        const in7 = nowTs + 7 * 24 * 60 * 60 * 1000;
        const upcoming = (data.social || [])
          .filter(p => ["scheduled", "generated"].includes(p.status))
          .filter(p => {
            const t = Date.parse(p.scheduled_for || "");
            return t >= nowTs && t <= in7;
          })
          .sort((a, b) => (a.scheduled_for || "").localeCompare(b.scheduled_for || ""));
        if (upcoming.length === 0) return null;
        return (
          <div className="rounded-2xl border border-line bg-canvas p-5 lg:p-6" data-testid="autopilot-next-week">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-stone">Prossima settimana · {upcoming.length} post</div>
                <h3 className="font-serif text-xl text-ink mt-1">Cosa pubblicherà il bot nei prossimi 7 giorni</h3>
              </div>
              <div className="text-xs text-stone/70 italic hidden md:block">Clicca "Sostituisci" per riscrivere un post prima che vada live</div>
            </div>
            <div className="divide-y divide-line/60">
              {upcoming.map(p => {
                const dt = new Date(p.scheduled_for);
                const day = dt.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" });
                const time = dt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
                const platLabel = p.platform === "facebook_instagram" ? "FB + IG" : (p.platform || "").toUpperCase();
                const isReel = (p.media_type || "").toUpperCase() === "REELS";
                const preview = (p.content || "").slice(0, 180);
                return (
                  <div key={p.id} className="py-4 flex gap-4 items-start" data-testid={`autopilot-next-week-item-${p.id}`}>
                    <div className="flex-shrink-0 w-24">
                      <div className="text-[11px] uppercase tracking-widest text-terracotta font-medium">{day}</div>
                      <div className="text-sm text-ink">{time}</div>
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-sand text-stone">
                        {isReel ? "REEL" : platLabel}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-ink truncate">{p.topic || "—"}</div>
                      <div className="mt-1 text-xs text-stone leading-relaxed line-clamp-2">{preview}{(p.content || "").length > 180 ? "…" : ""}</div>
                    </div>
                    <div className="flex-shrink-0 flex gap-2">
                      <button
                        onClick={() => openEditor(p)}
                        disabled={isReel}
                        title={isReel ? "I Reel non sono testo-based" : "Riscrivi il testo"}
                        className="text-xs px-3 py-1.5 rounded-full border border-ink text-ink hover:bg-ink hover:text-canvas transition disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`autopilot-next-week-edit-${p.id}`}
                      >
                        Sostituisci
                      </button>
                      <button
                        onClick={() => deletePost(p, "social")}
                        className="text-xs px-3 py-1.5 rounded-full text-stone hover:text-terracotta hover:bg-terracotta/5 transition"
                        data-testid={`autopilot-next-week-delete-${p.id}`}
                      >
                        Annulla
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Editor dialog for Sostituisci */}
      <Dialog open={!!editingPost} onOpenChange={(v) => !v && setEditingPost(null)}>
        <DialogContent className="max-w-xl" data-testid="autopilot-edit-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-ink">Riscrivi questo post</DialogTitle>
          </DialogHeader>
          {editingPost && (
            <div className="space-y-3">
              <div className="text-xs text-stone">
                {new Date(editingPost.scheduled_for).toLocaleString("it-IT")} · {editingPost.platform} · {editingPost.topic}
              </div>
              <Textarea
                value={editingDraft}
                onChange={(e) => setEditingDraft(e.target.value)}
                rows={10}
                className="text-sm"
                placeholder="Scrivi il tuo testo…"
                data-testid="autopilot-edit-textarea"
              />
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-stone/60">{editingDraft.length} caratteri · consigliato IG ≤ 2200 · FB illimitato</span>
                <button
                  onClick={regenerateWithClaude}
                  disabled={regenLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gold text-ink hover:bg-gold hover:text-ink transition disabled:opacity-40"
                  data-testid="autopilot-edit-regenerate"
                >
                  {regenLoading ? "Claude sta scrivendo…" : "✨ Rigenera con Claude (3 varianti · €0.03)"}
                </button>
              </div>
              {regenVariants.length > 0 && (
                <div className="mt-2 space-y-2 border-t border-line pt-3">
                  <div className="text-[11px] uppercase tracking-widest text-terracotta">Varianti neuromarketing · clicca per usarla</div>
                  {regenVariants.map((v, i) => (
                    <button
                      key={i}
                      onClick={() => { setEditingDraft(v); setRegenVariants([]); toast.success("Variante applicata — clicca Salva per confermare"); }}
                      className="w-full text-left text-sm p-3 rounded-lg border border-line hover:border-gold hover:bg-sand/30 transition"
                      data-testid={`autopilot-edit-variant-${i}`}
                    >
                      <div className="text-[10px] uppercase text-stone/60 mb-1">Variante {i + 1}</div>
                      <div className="text-ink whitespace-pre-wrap">{v}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingPost(null)} className="rounded-full" data-testid="autopilot-edit-cancel">Annulla</Button>
            <Button onClick={saveEditor} disabled={savingEdit || !editingDraft.trim()} className="rounded-full bg-ink text-canvas hover:bg-olive" data-testid="autopilot-edit-save">
              {savingEdit ? "Salvataggio…" : "Salva · andrà live"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editorial timeline */}
      {grouped.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line bg-canvas p-10 text-center" data-testid="autopilot-empty">
          <Sparkle weight="fill" size={28} className="text-olive mx-auto"/>
          <div className="mt-3 font-serif text-xl text-ink">Nessun contenuto ancora</div>
          <p className="mt-2 text-sm text-stone max-w-md mx-auto">
            Clicca "Genera prossima settimana" per creare 14 post social (1 FB + 1 IG al giorno) e 2 articoli blog personalizzati sul tono della tua struttura. Ogni domenica alle 18:00 UTC il cron rigenera automaticamente il calendario per la settimana successiva.
          </p>
        </div>
      ) : (
        <div className="space-y-6" data-testid="autopilot-calendar">
          {grouped.map(([day, items]) => {
            const d = new Date(day);
            const label = d.toLocaleDateString('it-IT', {weekday: 'long', day: 'numeric', month: 'long'});
            return (
              <div
                key={day}
                className="rounded-2xl border border-line bg-canvas p-6 transition-colors"
                data-testid={`day-${day}`}
                onDragOver={(e)=>{e.preventDefault(); e.currentTarget.classList.add("bg-gold/10","border-gold");}}
                onDragLeave={(e)=>{e.currentTarget.classList.remove("bg-gold/10","border-gold");}}
                onDrop={(e)=>{e.preventDefault(); e.currentTarget.classList.remove("bg-gold/10","border-gold"); onDropOnDay(day);}}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="font-serif text-xl text-ink capitalize">{label}</div>
                  <div className="text-xs uppercase tracking-widest text-stone">{items.length} contenuti · trascina per spostare</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                  {items.map(p => {
                    const meta = SOCIAL_PLATFORM_META[p.platform] || SOCIAL_PLATFORM_META.blog;
                    const Icon = meta.icon;
                    const isExpanded = expanded[p.id];
                    const isPublished = p.status === "published";
                    const isBlog = p.platform === "blog";
                    return (
                      <div
                        key={p.id}
                        draggable={!isPublished}
                        onDragStart={()=>onDragStart(p, isBlog ? "blog" : "social")}
                        onDragEnd={()=>setDragId(null)}
                        className={`rounded-2xl border p-4 ${isPublished ? "border-olive/40 bg-olive/5" : "border-line bg-canvas cursor-move hover:border-gold/60"} ${dragId===p.id ? "opacity-40" : ""}`}
                        data-testid={`post-${p.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs ${meta.bg}`}>
                            <Icon weight="fill" size={14} className={meta.color}/>
                            <span className={meta.color}>{meta.label}</span>
                          </div>
                          {isPublished && (
                            <span className="text-[10px] uppercase tracking-widest text-olive font-medium flex items-center gap-1">
                              <Check weight="bold" size={12}/> Pubblicato
                            </span>
                          )}
                        </div>
                        {isBlog && p.title && (
                          <div className="font-serif text-lg text-ink leading-snug mb-2">{p.title}</div>
                        )}
                        <div className={`text-sm text-stone leading-relaxed whitespace-pre-wrap ${isExpanded ? "" : "line-clamp-4"}`}>
                          {p.content}
                        </div>
                        <button
                          onClick={() => setExpanded(e => ({...e, [p.id]: !e[p.id]}))}
                          className="mt-2 text-xs text-ink underline underline-offset-2 hover:text-terracotta"
                          data-testid={`toggle-${p.id}`}
                        >
                          {isExpanded ? "Riduci" : "Mostra tutto"}
                        </button>
                        <div className="mt-4 flex items-center gap-2 flex-wrap">
                          {!isPublished && (
                            <label className="inline-flex items-center gap-1 text-[10px] text-stone" title="Modifica data e ora di pubblicazione">
                              <CalendarBlank weight="bold" size={12}/>
                              <input
                                type="datetime-local"
                                defaultValue={p.scheduled_for ? new Date(p.scheduled_for).toISOString().slice(0,16) : ""}
                                onBlur={(e)=>onDateInput(p, isBlog?"blog":"social", e.target.value)}
                                className="border border-line rounded px-1 py-0.5 text-[10px] bg-canvas focus:outline-none focus:border-terracotta"
                                data-testid={`reschedule-${p.id}`}
                              />
                            </label>
                          )}
                          {isBlog ? (
                            <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer">
                              <Button size="sm" className="rounded-full bg-ink text-canvas hover:bg-olive text-xs h-8" data-testid={`view-blog-${p.id}`}>
                                Apri articolo <ArrowUpRight weight="bold" size={12} className="ml-1"/>
                              </Button>
                            </a>
                          ) : !isPublished ? (
                            <>
                              {(p.platform === "instagram" || p.platform === "facebook" || p.platform === "facebook_instagram") && (
                                <Button size="sm" onClick={()=>publishNow(p)} disabled={busyPubId===p.id} className="rounded-full bg-gold text-ink hover:bg-ink hover:text-canvas text-xs h-8" data-testid={`publish-now-${p.id}`}>
                                  🚀 {busyPubId===p.id ? "Pubblicando…" : "Pubblica ora FB+IG"}
                                </Button>
                              )}
                              <Button size="sm" onClick={()=>publishAssist(p)} variant="ghost" className="rounded-full text-terracotta hover:bg-terracotta/10 text-xs h-8" data-testid={`publish-${p.id}`}>
                                <Copy weight="bold" size={12} className="mr-1"/>Copia caption
                              </Button>
                            </>
                          ) : (
                            <Button size="sm" variant="ghost" onClick={()=>publishAssist(p)} className="rounded-full text-xs h-8" data-testid={`repub-${p.id}`}>
                              <Copy weight="bold" size={12} className="mr-1"/>Copia di nuovo
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={()=>deletePost(p)} className="rounded-full text-stone hover:text-terracotta text-xs h-8" data-testid={`delete-${p.id}`}>
                            <Trash weight="bold" size={12}/>
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Meta status */}
      <div className="rounded-2xl border border-line bg-sand/40 p-6" data-testid="autopilot-meta-status">
        <div className="flex items-start gap-3">
          <Check weight="fill" size={18} className="text-olive mt-0.5"/>
          <div>
            <div className="font-serif text-lg text-ink">Auto-publish Meta ATTIVO</div>
            <p className="mt-1 text-sm text-stone leading-relaxed">
              Facebook e Instagram sono collegati via Meta Graph API. I post schedulati vengono pubblicati automaticamente ogni 15 minuti tramite cron. Puoi anche pubblicare qualsiasi post generato con un click ("Pubblica ora FB+IG"). TikTok: in attesa dell'approvazione TikTok Content Posting API.
            </p>
          </div>
        </div>
      </div>

      {/* Meta Page-info modal */}
      {pageInfoOpen && pageInfo && (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={()=>setPageInfoOpen(false)} data-testid="page-info-modal">
          <div className="bg-canvas rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
            <div className="p-6 border-b border-line flex items-center justify-between sticky top-0 bg-canvas z-10">
              <div>
                <div className="text-xs uppercase tracking-widest text-stone">Verifica pagine collegate</div>
                <h3 className="font-serif text-xl text-ink mt-1">Facebook + Instagram</h3>
              </div>
              <Button variant="ghost" size="sm" onClick={()=>setPageInfoOpen(false)} data-testid="close-page-info">Chiudi</Button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* FB */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <FacebookLogo weight="fill" size={20} className="text-olive"/>
                  <span className="font-medium text-ink">{pageInfo.facebook?.name || "—"}</span>
                </div>
                {pageInfo.facebook?.picture?.data?.url && (
                  <img src={pageInfo.facebook.picture.data.url} alt="FB profilo" className="w-20 h-20 rounded-full object-cover"/>
                )}
                {pageInfo.facebook?.cover?.source && (
                  <img src={pageInfo.facebook.cover.source} alt="FB cover" className="w-full h-32 object-cover rounded-lg"/>
                )}
                <div className="text-xs text-stone space-y-1">
                  <div><b>Categoria:</b> {pageInfo.facebook?.category || "—"}</div>
                  <div><b>Bio:</b> {pageInfo.facebook?.about || pageInfo.facebook?.description || "—"}</div>
                  <div><b>Sito:</b> {pageInfo.facebook?.website || "—"}</div>
                  <div><b>Indirizzo:</b> {pageInfo.facebook?.location ? `${pageInfo.facebook.location.street||""}, ${pageInfo.facebook.location.city||""} ${pageInfo.facebook.location.zip||""}` : "—"}</div>
                  <div><b>Telefono:</b> {pageInfo.facebook?.phone || "—"}</div>
                  <div><b>Email:</b> {(pageInfo.facebook?.emails || []).join(", ") || "—"}</div>
                  <div><b>Fan:</b> {pageInfo.facebook?.fan_count ?? "—"} · <b>Follower:</b> {pageInfo.facebook?.followers_count ?? "—"}</div>
                  {pageInfo.facebook?.link && <div><a href={pageInfo.facebook.link} target="_blank" rel="noreferrer" className="underline text-terracotta">Apri pagina FB ↗</a></div>}
                </div>
              </div>
              {/* IG */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <InstagramLogo weight="fill" size={20} className="text-terracotta"/>
                  <span className="font-medium text-ink">@{pageInfo.instagram?.username || "—"}</span>
                </div>
                {pageInfo.instagram?.profile_picture_url && (
                  <img src={pageInfo.instagram.profile_picture_url} alt="IG profilo" className="w-20 h-20 rounded-full object-cover"/>
                )}
                <div className="text-xs text-stone space-y-1">
                  <div><b>Nome:</b> {pageInfo.instagram?.name || "—"}</div>
                  <div><b>Bio:</b> {pageInfo.instagram?.biography || "—"}</div>
                  <div><b>Sito:</b> {pageInfo.instagram?.website || "—"}</div>
                  <div><b>Follower:</b> {pageInfo.instagram?.followers_count ?? "—"} · <b>Post:</b> {pageInfo.instagram?.media_count ?? "—"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Newsletter Ospiti */}
      <NewsletterCard />

      {/* Templates modal */}
      {templatesOpen && (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={()=>setTemplatesOpen(false)} data-testid="templates-modal">
          <div className="bg-canvas rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
            <div className="p-6 border-b border-line flex items-center justify-between sticky top-0 bg-canvas z-10">
              <div>
                <div className="text-xs uppercase tracking-widest text-stone">Template one-tap</div>
                <h3 className="font-serif text-xl text-ink mt-1">Post pronti in 1 click</h3>
                <p className="text-xs text-stone mt-1">Zero crediti LLM · Placeholders {`{property_name}`}, {`{city}`} già sostituiti</p>
              </div>
              <Button variant="ghost" size="sm" onClick={()=>setTemplatesOpen(false)}>Chiudi</Button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map(t => (
                <div key={t.id} className="rounded-xl border border-line bg-sand/30 p-4 flex flex-col gap-3" data-testid={`tpl-${t.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-2xl">{t.emoji}</div>
                      <div className="font-serif text-base text-ink mt-1">{t.title}</div>
                    </div>
                    {t.image_url && <img src={t.image_url} alt="" className="w-16 h-16 rounded-lg object-cover"/>}
                  </div>
                  <div className="text-xs text-stone whitespace-pre-line line-clamp-5">{t.content.slice(0, 260)}…</div>
                  <div className="mt-auto flex gap-2">
                    <Button size="sm" onClick={()=>applyTemplate(t, "facebook_instagram")} className="rounded-full bg-gold text-ink hover:bg-ink hover:text-canvas text-xs h-8 flex-1" data-testid={`tpl-apply-${t.id}`}>
                      Schedula FB + IG
                    </Button>
                    <Button size="sm" variant="ghost" onClick={()=>applyTemplate(t, "instagram")} className="rounded-full text-xs h-8" title="Solo IG">
                      IG
                    </Button>
                    <Button size="sm" variant="ghost" onClick={()=>applyTemplate(t, "facebook")} className="rounded-full text-xs h-8" title="Solo FB">
                      FB
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Voice Preset Wizard modal */}
      {voiceWizardOpen && (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={()=>setVoiceWizardOpen(false)} data-testid="voice-wizard-modal">
          <div className="bg-canvas rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()}>
            <div className="p-6 border-b border-line">
              <div className="text-xs uppercase tracking-widest text-stone">Voice Preset Wizard</div>
              <h3 className="font-serif text-xl text-ink mt-1">Cattura il tono del tuo brand</h3>
              <p className="text-xs text-stone mt-2 leading-relaxed">Rispondi a 3 domande. Il preset sarà applicato a TUTTI i post, articoli e email generati dall'AI. Zero crediti aggiuntivi.</p>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="text-sm font-medium text-ink">1. Che tono ha il tuo brand?</label>
                <select value={voicePreset.tone_style} onChange={(e)=>setVoicePreset({...voicePreset, tone_style: e.target.value})}
                  className="mt-2 w-full rounded-lg border border-line px-3 py-2 text-sm bg-canvas focus:outline-none focus:border-terracotta"
                  data-testid="voice-tone-select">
                  <option value="">— Scegli —</option>
                  <option value="warm-intimate">Caldo e intimo (amichevole, familiare)</option>
                  <option value="professional-elegant">Professionale ed elegante (raffinato, misurato)</option>
                  <option value="playful-fresh">Giocoso e fresco (leggero, ironico)</option>
                  <option value="poetic-slow">Poetico e lento (evocativo, riflessivo)</option>
                  <option value="direct-clear">Diretto e chiaro (senza fronzoli, informativo)</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-ink">2. Come vuoi rivolgerti al lettore?</label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {["tu", "voi", "lei"].map(f => (
                    <button key={f} onClick={()=>setVoicePreset({...voicePreset, formality: f})}
                      className={`rounded-lg border px-3 py-2 text-sm ${voicePreset.formality === f ? "border-terracotta bg-terracotta/10 text-ink" : "border-line bg-canvas text-stone hover:border-ink/30"}`}
                      data-testid={`voice-formality-${f}`}>
                      {f === "tu" ? "Tu" : f === "voi" ? "Voi (plurale)" : "Lei (formale)"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-ink">3. Ci sono espressioni signature o parole da evitare? <span className="text-stone font-normal">(opzionale)</span></label>
                <input value={voicePreset.signature_phrases} onChange={(e)=>setVoicePreset({...voicePreset, signature_phrases: e.target.value})}
                  placeholder='Es: "buon riposo", "a domani con calma"'
                  className="mt-2 w-full rounded-lg border border-line px-3 py-2 text-sm bg-canvas focus:outline-none focus:border-terracotta"
                  data-testid="voice-signature-input"/>
                <input value={voicePreset.avoid_words} onChange={(e)=>setVoicePreset({...voicePreset, avoid_words: e.target.value})}
                  placeholder='Parole da NON usare (es: "esperienza", "scopri")'
                  className="mt-2 w-full rounded-lg border border-line px-3 py-2 text-sm bg-canvas focus:outline-none focus:border-terracotta"
                  data-testid="voice-avoid-input"/>
              </div>
              <div className="flex justify-end gap-2 pt-3">
                <Button variant="ghost" onClick={()=>setVoiceWizardOpen(false)}>Annulla</Button>
                <Button onClick={saveVoicePreset} className="bg-ink text-canvas hover:bg-olive" data-testid="voice-save-btn">Salva preset</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Report ultimo cleanup post duplicati */}
      {floodCleanupReport && (
        <div className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto" onClick={() => setFloodCleanupReport(null)} data-testid="flood-cleanup-report-modal">
          <div className="bg-canvas rounded-2xl max-w-3xl w-full mt-10 p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="font-serif text-2xl text-ink">🚨 Report pulizia post duplicati</h3>
                <p className="text-xs text-stone mt-1">Finestra ultime {floodCleanupReport.window_hours}h · Tetto {floodCleanupReport.max_keep_per_day}/giorno</p>
              </div>
              <button onClick={() => setFloodCleanupReport(null)} className="text-stone hover:text-ink text-2xl" aria-label="Chiudi">×</button>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-sand rounded-lg p-3">
                <div className="text-xs text-stone">Pubblicati totali</div>
                <div className="text-2xl font-medium text-ink">{floodCleanupReport.total_published_in_window}</div>
              </div>
              <div className="bg-sand rounded-lg p-3">
                <div className="text-xs text-stone">In eccesso rilevati</div>
                <div className="text-2xl font-medium text-ink">{floodCleanupReport.excess_to_delete}</div>
              </div>
              <div className="bg-sand rounded-lg p-3">
                <div className="text-xs text-stone">Fase</div>
                <div className="text-xs font-mono text-ink mt-2">{floodCleanupReport.phase}</div>
              </div>
            </div>

            {/* Facebook results */}
            {Array.isArray(floodCleanupReport.facebook_delete_results) && floodCleanupReport.facebook_delete_results.length > 0 && (
              <div className="mb-6">
                <h4 className="font-serif text-lg text-ink mb-2">Facebook — cancellati via API</h4>
                <div className="space-y-1 text-xs">
                  {floodCleanupReport.facebook_delete_results.map((r, i) => (
                    <div key={i} className={`font-mono px-2 py-1 rounded ${r.ok ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-900'}`}>
                      {r.ok ? '✓' : '✗'} FB post {r.fb_post_id || '?'} {r.ok ? '(deleted)' : `(error: ${r.error || r.status})`}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Instagram manual list */}
            {Array.isArray(floodCleanupReport.instagram_manual_delete_needed) && floodCleanupReport.instagram_manual_delete_needed.length > 0 && (
              <div className="mb-6">
                <h4 className="font-serif text-lg text-ink mb-2">Instagram — da cancellare a mano dall'app</h4>
                <p className="text-xs text-stone mb-3">Instagram Graph API non supporta DELETE. Apri l'app Instagram sul telefono, vai sul tuo profilo @otelya.hq, cancella uno per uno i post con questi media ID (per cancellare: tocca il post → "..." → "Elimina"):</p>
                <div className="space-y-1 text-xs max-h-96 overflow-y-auto">
                  {floodCleanupReport.instagram_manual_delete_needed.map((p, i) => (
                    <div key={i} className="font-mono px-2 py-1 bg-amber-50 text-amber-900 rounded flex justify-between gap-2">
                      <span>#{i+1} IG media: {p.ig_media_id}</span>
                      <span className="text-stone">{p.published_at ? p.published_at.slice(0,16).replace('T',' ') : '?'}</span>
                    </div>
                  ))}
                </div>
                <button
                  className="mt-3 text-xs text-terracotta underline"
                  onClick={() => { navigator.clipboard.writeText(floodCleanupReport.instagram_manual_delete_needed.map(p => p.ig_media_id).join('\n')); toast.success('IDs copiati negli appunti'); }}
                  data-testid="copy-ig-ids-btn"
                >Copia tutti gli IG media ID negli appunti</button>
              </div>
            )}

            {/* Excess details (nothing was actioned) */}
            {floodCleanupReport.phase === "dry_run_clean" && (
              <div className="text-sm text-stone bg-sand/40 rounded-lg p-4">
                ✓ Nessun post trovato in eccesso nelle ultime 72h. Il calendario è pulito.
              </div>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setFloodCleanupReport(null)} className="rounded-full" data-testid="close-cleanup-report-btn">Chiudi</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NewsletterCard() {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const loadPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post("/newsletter/preview", {});
      setPreview(r.data.html);
      setShowPreview(true);
    } catch { toast.error("Errore anteprima"); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    if (!window.confirm("Inviare la newsletter mensile a tutti i tuoi ospiti con email valida?")) return;
    setBusy(true);
    try {
      await api.post("/newsletter/send-now", {});
      toast.success("Invio avviato! Controlla i log tra qualche minuto.");
    } catch { toast.error("Errore invio"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-olive/30 bg-olive/5 p-6 lg:p-8" data-testid="newsletter-card">
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-olive font-medium flex items-center gap-2">
            <EnvelopeSimple weight="fill" size={14}/> Newsletter mensile ospiti
          </div>
          <div className="font-serif text-xl text-ink mt-2 leading-snug">
            Ogni mese, riabbraccia i tuoi ospiti.
          </div>
          <p className="mt-2 text-sm text-stone leading-relaxed">
            Il 1° di ogni mese alle 09:00 Otelya invia automaticamente ai tuoi ospiti passati (con email) una newsletter con i migliori articoli del blog + <strong>uno sconto del 10%</strong> sul prossimo soggiorno. La lingua si adatta a ogni ospite (IT o EN).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={loadPreview} disabled={busy} className="rounded-full border-olive text-olive hover:bg-olive hover:text-canvas" data-testid="newsletter-preview-btn">
            <MagnifyingGlass weight="bold" size={12} className="mr-1"/> Anteprima
          </Button>
          <Button size="sm" onClick={sendNow} disabled={busy} className="rounded-full bg-olive hover:bg-olivelight text-canvas" data-testid="newsletter-send-btn">
            <PaperPlaneTilt weight="fill" size={12} className="mr-1"/> Invia ora
          </Button>
        </div>
      </div>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Anteprima newsletter mensile</DialogTitle>
          </DialogHeader>
          {preview && <iframe title="Preview" srcDoc={preview} className="w-full min-h-[600px] rounded-lg border border-line" data-testid="newsletter-preview-frame"/>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ----- INBOX -----
function Inbox() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/inbox").then(r => { setItems(r.data); if (r.data[0] && !selected) setSelected(r.data[0]); });
  useEffect(() => { load(); }, []);

  const reply = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const r = await api.post("/inbox/reply", { message_id: selected.id, tone: "warm-professional" });
      toast.success("Otelya ha risposto");
      setSelected({ ...selected, ai_reply: r.data.reply, status: "replied" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore AI"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    await api.delete(`/inbox/${id}`);
    if (selected?.id === id) setSelected(null);
    load();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 h-[calc(100vh-11rem)]">
      <div className="lg:col-span-4 rounded-2xl border border-line bg-canvas overflow-hidden flex flex-col">
        <div className="p-4 border-b border-line flex items-center justify-between">
          <div className="font-serif text-lg text-ink">Messaggi</div>
          <NewInboxDialog onCreated={load} />
        </div>
        <div className="flex-1 overflow-y-auto divide-y divide-line">
          {items.length === 0 && <div className="p-6 text-stone text-sm">Inbox vuota.</div>}
          {items.map(m => (
            <button key={m.id} onClick={()=>setSelected(m)} data-testid={`inbox-item-${m.id}`}
              className={`w-full text-left p-4 hover:bg-sand/60 transition-colors ${selected?.id===m.id?"bg-sand/70":""}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-ink"><ChannelIcon ch={m.channel}/> {m.from_name}</div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${m.status==="replied"?"bg-olive/10 text-olive":"bg-terracotta/10 text-terracotta"}`}>
                  {m.status==="replied"?"risposto":"nuovo"}
                </span>
              </div>
              <div className="mt-1 text-xs text-stone line-clamp-2">{m.message}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="lg:col-span-8 rounded-2xl border border-line bg-canvas p-6 overflow-y-auto">
        {!selected ? (
          <div className="h-full grid place-items-center text-stone">Seleziona un messaggio</div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-stone flex items-center gap-2">
                  <ChannelIcon ch={selected.channel}/> {selected.channel} · {selected.language.toUpperCase()}
                </div>
                <div className="mt-1 font-serif text-2xl text-ink">{selected.from_name}</div>
                {selected.from_contact && <div className="text-xs text-stone">{selected.from_contact}</div>}
              </div>
              <Button variant="ghost" size="icon" onClick={()=>del(selected.id)} data-testid="inbox-delete-btn"><Trash size={16}/></Button>
            </div>

            <div className="rounded-xl bg-sand/50 p-5 text-ink leading-relaxed">{selected.message}</div>

            {selected.ai_reply ? (
              <div className="rounded-xl border border-olive/30 bg-olive/5 p-5">
                <div className="text-xs uppercase tracking-widest text-olive flex items-center gap-2 mb-2"><Sparkle weight="fill" size={12}/> Risposta di Otelya</div>
                <div className="whitespace-pre-wrap text-ink leading-relaxed">{selected.ai_reply}</div>
              </div>
            ) : (
              <Button onClick={reply} disabled={busy} className="rounded-full bg-terracotta hover:bg-terracotta/90 text-canvas" data-testid="inbox-generate-reply-btn">
                <PaperPlaneTilt size={16} className="mr-2"/> {busy ? "Otelya scrive…" : "Genera risposta AI"}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function NewInboxDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ channel:"whatsapp", from_name:"", from_contact:"", language:"it", message:"" });
  const submit = async () => {
    try { await api.post("/inbox", f); toast.success("Messaggio creato"); setOpen(false); setF({ channel:"whatsapp", from_name:"", from_contact:"", language:"it", message:"" }); onCreated(); }
    catch(e){ toast.error("Errore"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full border-line" data-testid="inbox-new-btn"><Plus size={14} className="mr-1"/>Nuovo</Button></DialogTrigger>
      <DialogContent className="bg-canvas">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Simula un messaggio ospite</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Canale</Label>
              <Select value={f.channel} onValueChange={(v)=>setF({...f,channel:v})}>
                <SelectTrigger className="mt-1.5" data-testid="new-msg-channel"><SelectValue/></SelectTrigger>
                <SelectContent>{["whatsapp","email","phone","website","booking"].map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Lingua</Label>
              <Select value={f.language} onValueChange={(v)=>setF({...f,language:v})}>
                <SelectTrigger className="mt-1.5" data-testid="new-msg-lang"><SelectValue/></SelectTrigger>
                <SelectContent>{["it","en","fr","de","es"].map(x=><SelectItem key={x} value={x}>{x.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Nome mittente</Label><Input className="mt-1.5" value={f.from_name} onChange={(e)=>setF({...f,from_name:e.target.value})} data-testid="new-msg-name"/></div>
          <div><Label>Contatto</Label><Input className="mt-1.5" value={f.from_contact} onChange={(e)=>setF({...f,from_contact:e.target.value})} data-testid="new-msg-contact"/></div>
          <div><Label>Messaggio</Label><Textarea className="mt-1.5" rows={4} value={f.message} onChange={(e)=>setF({...f,message:e.target.value})} data-testid="new-msg-text"/></div>
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-full bg-ink text-canvas hover:bg-olive" data-testid="new-msg-submit">Aggiungi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ----- Generic list -----
function ListSection({ title, endpoint, columns, formFields, testKey }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(Object.fromEntries(formFields.map(f=>[f.k, f.default ?? ""])));
  const load = () => api.get(`/${endpoint}`).then(r=>setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post(`/${endpoint}`, form); toast.success("Creato"); setOpen(false); setForm(Object.fromEntries(formFields.map(f=>[f.k, f.default ?? ""]))); load(); }
    catch(e){ toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const del = async (id) => { await api.delete(`/${endpoint}/${id}`); load(); };

  return (
    <div className="rounded-2xl border border-line bg-canvas overflow-hidden">
      <div className="p-6 flex items-center justify-between">
        <div className="font-serif text-2xl text-ink">{title}</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full bg-ink text-canvas hover:bg-olive" data-testid={`${testKey}-new-btn`}><Plus size={16} className="mr-1"/>Nuovo</Button></DialogTrigger>
          <DialogContent className="bg-canvas">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Nuovo · {title}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {formFields.map(f => (
                <div key={f.k}>
                  <Label>{f.l}</Label>
                  {f.type === "select" ? (
                    <Select value={form[f.k]} onValueChange={(v)=>setForm({...form, [f.k]: v})}>
                      <SelectTrigger className="mt-1.5" data-testid={`${testKey}-field-${f.k}`}><SelectValue/></SelectTrigger>
                      <SelectContent>{f.options.map(o=><SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.type === "textarea" ? (
                    <Textarea className="mt-1.5" rows={3} value={form[f.k]} onChange={(e)=>setForm({...form,[f.k]:e.target.value})} data-testid={`${testKey}-field-${f.k}`}/>
                  ) : (
                    <Input className="mt-1.5" type={f.type || "text"} value={form[f.k]} onChange={(e)=>setForm({...form,[f.k]: f.type==="number"?parseFloat(e.target.value||0):e.target.value})} data-testid={`${testKey}-field-${f.k}`}/>
                  )}
                </div>
              ))}
            </div>
            <DialogFooter><Button onClick={create} className="rounded-full bg-ink text-canvas hover:bg-olive" data-testid={`${testKey}-submit`}>Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="border-t border-line">
        <div className="grid text-xs uppercase tracking-widest text-stone px-6 py-3 border-b border-line" style={{gridTemplateColumns: `repeat(${columns.length},1fr) 40px`}}>
          {columns.map(c => <div key={c.k}>{c.l}</div>)}
          <div/>
        </div>
        {items.length === 0 && <div className="px-6 py-8 text-sm text-stone">Nessun elemento. Aggiungine uno.</div>}
        {items.map(row => (
          <div key={row.id} className="grid items-center px-6 py-4 border-b border-line last:border-b-0 text-sm text-ink" style={{gridTemplateColumns: `repeat(${columns.length},1fr) 40px`}} data-testid={`${testKey}-row-${row.id}`}>
            {columns.map(c => <div key={c.k} className="pr-4 truncate">{c.render ? c.render(row) : row[c.k]}</div>)}
            <Button variant="ghost" size="icon" onClick={()=>del(row.id)} data-testid={`${testKey}-delete-${row.id}`}><Trash size={16}/></Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ----- AI Config -----
function AIConfig() {
  const { user, refresh } = useAuth();
  const [f, setF] = useState(user?.ai_config || { tone:"warm-professional", languages:["it","en"], signature:"", hotel_context:"", auto_reply_email:false, auto_reply_whatsapp:false });
  const [previewMsg, setPreviewMsg] = useState("Ciao, vorrei sapere se avete disponibilità per il prossimo weekend.");
  const [previewLang, setPreviewLang] = useState("it");
  const [preview, setPreview] = useState(""); const [busy, setBusy] = useState(false);

  const save = async () => {
    try { await api.post("/ai/config", f); toast.success("Configurazione salvata"); await refresh(); }
    catch(e){ toast.error("Errore"); }
  };
  const doPreview = async () => {
    setBusy(true); setPreview("");
    try { const r = await api.post("/ai/preview", { message: previewMsg, language: previewLang, channel:"chat", tone: f.tone }); setPreview(r.data.reply); }
    catch(e){ toast.error(e?.response?.data?.detail || "Errore AI"); }
    finally { setBusy(false); }
  };

  const toggleLang = (l) => {
    const has = f.languages.includes(l);
    setF({...f, languages: has ? f.languages.filter(x=>x!==l) : [...f.languages, l] });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="rounded-2xl border border-line bg-canvas p-6 space-y-5">
        <div className="font-serif text-2xl text-ink">Personalità di Otelya</div>
        <div>
          <Label>Tono di voce</Label>
          <Select value={f.tone} onValueChange={(v)=>setF({...f,tone:v})}>
            <SelectTrigger className="mt-1.5" data-testid="ai-tone"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="warm-professional">Caloroso & Professionale</SelectItem>
              <SelectItem value="luxury-elegant">Lusso & Elegante</SelectItem>
              <SelectItem value="friendly-casual">Amichevole & Informale</SelectItem>
              <SelectItem value="concise-precise">Conciso & Preciso</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Lingue supportate</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {["it","en","fr","de","es","pt","nl","ru","zh","ja","ar"].map(l => (
              <button key={l} onClick={()=>toggleLang(l)} data-testid={`ai-lang-${l}`}
                className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border transition ${f.languages.includes(l)?"bg-olive text-canvas border-olive":"bg-canvas text-stone border-line hover:border-ink"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div>
          <Label>Signature</Label>
          <Input className="mt-1.5" value={f.signature} onChange={(e)=>setF({...f, signature:e.target.value})} placeholder="Es. Villa del Sole — Cordiali saluti" data-testid="ai-signature"/>
        </div>
        <div>
          <Label>Contesto struttura (regole, orari, servizi, prezzi indicativi)</Label>
          <Textarea className="mt-1.5" rows={6} value={f.hotel_context} onChange={(e)=>setF({...f, hotel_context:e.target.value})}
            placeholder="Es. Check-in 15:00 – 21:00. Colazione dalle 7:30 alle 10:30. Pet friendly. Piscina aperta da maggio a ottobre. Camere doppie da €120/notte." data-testid="ai-context"/>
        </div>
        <div className="rounded-xl border border-line p-4 flex items-start justify-between gap-4 bg-sand/40">
          <div>
            <div className="text-sm font-medium text-ink">Auto-reply email in arrivo</div>
            <div className="text-xs text-stone mt-1 leading-relaxed">Quando un ospite scrive a <code className="text-terracotta">hello@otelya.app</code>, Otelya risponde in automatico via email nella lingua del cliente. Puoi sempre modificare il tono qui sopra.</div>
          </div>
          <button type="button" onClick={()=>setF({...f, auto_reply_email: !f.auto_reply_email})} data-testid="ai-auto-reply-toggle"
            className={`shrink-0 w-11 h-6 rounded-full transition ${f.auto_reply_email?"bg-olive":"bg-line"} relative`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-canvas shadow transition-transform ${f.auto_reply_email?"translate-x-5":"translate-x-0.5"}`}/>
          </button>
        </div>
        <div className="rounded-xl border border-line p-4 flex items-start justify-between gap-4 bg-sand/40">
          <div>
            <div className="text-sm font-medium text-ink flex items-center gap-1.5"><WhatsappLogo weight="fill" className="text-olive" size={16}/> Auto-reply WhatsApp in arrivo</div>
            <div className="text-xs text-stone mt-1 leading-relaxed">Quando un ospite scrive su WhatsApp, Otelya risponde in automatico con un messaggio breve, informale, calibrato al canale (max 2-3 frasi, no markdown).</div>
          </div>
          <button type="button" onClick={()=>setF({...f, auto_reply_whatsapp: !f.auto_reply_whatsapp})} data-testid="ai-auto-reply-wa-toggle"
            className={`shrink-0 w-11 h-6 rounded-full transition ${f.auto_reply_whatsapp?"bg-olive":"bg-line"} relative`}>
            <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-canvas shadow transition-transform ${f.auto_reply_whatsapp?"translate-x-5":"translate-x-0.5"}`}/>
          </button>
        </div>
        <Button onClick={save} className="rounded-full bg-ink text-canvas hover:bg-olive" data-testid="ai-save-btn"><Check size={16} className="mr-1"/> Salva configurazione</Button>
      </div>

      <div className="rounded-2xl border border-line bg-canvas p-6 space-y-4">
        <div className="font-serif text-2xl text-ink">Anteprima live</div>
        <div className="flex gap-2 flex-wrap">
          {["it","en","fr","de","es"].map(l => (
            <button key={l} onClick={()=>setPreviewLang(l)} data-testid={`ai-preview-lang-${l}`}
              className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border transition ${previewLang===l?"bg-ink text-canvas border-ink":"bg-canvas text-stone border-line hover:border-ink"}`}>{l}</button>
          ))}
        </div>
        <Textarea rows={4} value={previewMsg} onChange={(e)=>setPreviewMsg(e.target.value)} data-testid="ai-preview-msg"/>
        <Button onClick={doPreview} disabled={busy} className="rounded-full bg-terracotta hover:bg-terracotta/90 text-canvas" data-testid="ai-preview-btn">
          {busy ? "Otelya scrive…" : "Prova risposta"}
        </Button>
        {preview && (
          <div className="rounded-xl border border-olive/30 bg-olive/5 p-4" data-testid="ai-preview-reply">
            <div className="text-xs uppercase tracking-widest text-olive mb-2 flex items-center gap-2"><Sparkle weight="fill" size={12}/> Otelya</div>
            <div className="whitespace-pre-wrap text-ink leading-relaxed">{preview}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ----- Billing -----
function Billing() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState({});
  const [params, setParams] = useSearchParams();
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => { api.get("/billing/plans").then(r => setPlans(r.data)); }, []);

  useEffect(() => {
    const status = params.get("checkout");
    if (status === "cancelled") {
      localStorage.removeItem("otelya_checkout_session");
      toast.info("Pagamento annullato");
      setParams({});
      return;
    }
    if (status !== "success") return;
    const sessionId = localStorage.getItem("otelya_checkout_session");
    if (!sessionId) return;
    if (pollRef.current) return; // already polling

    setPolling(true);
    const start = Date.now();
    const stop = () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      setPolling(false);
    };
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        if (r.data.paid) {
          stop();
          localStorage.removeItem("otelya_checkout_session");
          toast.success("Abbonamento attivato!");
          await refresh();
          setParams({});
        } else if (Date.now() - start > 60000) {
          stop();
          toast.error("Verifica pagamento non riuscita. Contattaci se hai già pagato.");
          setParams({});
        }
      } catch (e) {
        stop();
        localStorage.removeItem("otelya_checkout_session");
        toast.error(e?.response?.data?.detail || "Sessione di pagamento non valida");
        setParams({});
      }
    }, 2500);

    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("checkout")]);

  const subscribe = async (planKey) => {
    try {
      const r = await api.post("/billing/checkout", { plan: planKey, origin_url: window.location.origin });
      localStorage.setItem("otelya_checkout_session", r.data.session_id);
      window.location.href = r.data.url;
    } catch(e){ toast.error(e?.response?.data?.detail || "Errore Stripe"); }
  };

  const sub = user?.subscription || {};
  const ordered = ["1m","3m","6m","12m"];

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-line bg-canvas p-6 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone">Stato abbonamento</div>
          <div className="font-serif text-3xl text-ink mt-1">
            {sub.status === "active" ? `Attivo · ${plans[sub.plan]?.label || sub.plan}` : "Trial gratuito"}
          </div>
          {sub.expires_at && <div className="text-xs text-stone mt-1">Rinnovo: {new Date(sub.expires_at).toLocaleDateString('it')}</div>}
        </div>
        <Badge className="bg-gold/20 text-[#8a6d16] hover:bg-gold/20 border-transparent">Otelya Pro</Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {ordered.map(k => {
          const p = plans[k]; if (!p) return null;
          const active = sub.plan === k && sub.status === "active";
          return (
            <div key={k} className={`rounded-3xl border p-6 flex flex-col ${k==="6m"?"border-olive bg-olive/[0.04]":"border-line bg-canvas"}`} data-testid={`billing-plan-${k}`}>
              <div className="text-xs uppercase tracking-widest text-stone">{p.label}</div>
              <div className="mt-3 font-serif text-3xl text-ink">€{p.monthly_price}<span className="text-sm text-stone font-sans">/mese</span></div>
              <div className="mt-1 text-xs text-stone">Fatturato €{p.total} · {p.months} mese/i</div>
              {p.discount>0 && <div className="mt-3 inline-flex w-max rounded-full bg-gold/15 text-[#8a6d16] text-xs px-2.5 py-1">-{p.discount}%</div>}
              <Button onClick={()=>subscribe(k)} disabled={active} className={`mt-6 rounded-full ${k==="6m"?"bg-olive hover:bg-olivelight":"bg-ink hover:bg-olive"} text-canvas`} data-testid={`billing-subscribe-${k}`}>
                {active ? "Piano attivo" : `Scegli ${p.label}`}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ----- Marketing Autopilot -----
const PLATFORM_META = {
  instagram:        { label: "Instagram",       Icon: InstagramLogo,  color: "text-terracotta" },
  facebook:         { label: "Facebook",        Icon: FacebookLogo,   color: "text-olive" },
  google_business:  { label: "Google Business", Icon: GoogleLogo,     color: "text-[#8a6d16]" },
  email_newsletter: { label: "Newsletter",      Icon: EnvelopeSimple, color: "text-olive" },
};

function Marketing() {
  const [platform, setPlatform] = useState("instagram");
  const [topic, setTopic] = useState("Weekend romantico d'autunno con degustazione di vini locali");
  const [language, setLanguage] = useState("it");
  const [tone, setTone] = useState("luxury-elegant");
  const [busy, setBusy] = useState(false);
  const [posts, setPosts] = useState([]);
  const load = () => api.get("/marketing/posts").then(r => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!topic.trim()) return toast.error("Inserisci un argomento");
    setBusy(true);
    try {
      await api.post("/marketing/generate", { platform, topic, language, tone, include_hashtags: true, include_cta: true });
      toast.success("Post generato!");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore AI"); }
    finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/marketing/posts/${id}`); load(); };
  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success("Copiato negli appunti"); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-5 rounded-2xl border border-line bg-canvas p-6 space-y-4">
        <div className="font-serif text-2xl text-ink">Nuovo contenuto</div>
        <div>
          <Label>Piattaforma</Label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {Object.entries(PLATFORM_META).map(([k, m]) => (
              <button key={k} onClick={()=>setPlatform(k)} data-testid={`mkt-platform-${k}`}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm transition ${platform===k?"bg-ink text-canvas border-ink":"bg-canvas text-stone border-line hover:border-ink"}`}>
                <m.Icon size={18} weight={platform===k?"fill":"regular"} /> {m.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label>Argomento / tema</Label>
          <Textarea rows={3} className="mt-1.5" value={topic} onChange={(e)=>setTopic(e.target.value)}
            placeholder="Es. Nuovo menù autunnale con tartufo nero" data-testid="mkt-topic"/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Lingua</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="mt-1.5" data-testid="mkt-language"><SelectValue/></SelectTrigger>
              <SelectContent>{["it","en","fr","de","es","pt","nl"].map(l=><SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tono</Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger className="mt-1.5" data-testid="mkt-tone"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="luxury-elegant">Lusso & Elegante</SelectItem>
                <SelectItem value="warm-professional">Caloroso & Professionale</SelectItem>
                <SelectItem value="friendly-casual">Amichevole & Informale</SelectItem>
                <SelectItem value="playful-vivid">Giocoso & Vivido</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={generate} disabled={busy} className="w-full rounded-full bg-terracotta hover:bg-terracotta/90 text-canvas" data-testid="mkt-generate-btn">
          <Sparkle size={16} weight="fill" className="mr-2"/> {busy ? "Otelya sta scrivendo…" : "Genera contenuto"}
        </Button>
      </div>

      <div className="lg:col-span-7 space-y-4">
        <div className="font-serif text-2xl text-ink flex items-center gap-2">
          <Megaphone size={22} className="text-olive"/> Post generati
        </div>
        {posts.length === 0 && (
          <div className="rounded-2xl border border-line bg-canvas p-10 text-center text-stone" data-testid="mkt-empty">
            Nessun contenuto ancora. Genera il primo per iniziare a raccontare la tua struttura.
          </div>
        )}
        {posts.map(p => {
          const meta = PLATFORM_META[p.platform] || PLATFORM_META.instagram;
          return (
            <div key={p.id} className="rounded-2xl border border-line bg-canvas p-5" data-testid={`mkt-post-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone">
                  <meta.Icon size={16} className={meta.color} weight="fill"/> {meta.label} · {p.language.toUpperCase()}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="ghost" onClick={()=>copyText(p.content)} data-testid={`mkt-copy-${p.id}`}><Copy size={16}/></Button>
                  <Button size="icon" variant="ghost" onClick={()=>del(p.id)} data-testid={`mkt-delete-${p.id}`}><Trash size={16}/></Button>
                </div>
              </div>
              <div className="mt-2 text-xs text-stone italic">Argomento: {p.topic}</div>
              <div className="mt-3 whitespace-pre-wrap text-ink leading-relaxed">{p.content}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ----- iCal Calendars -----
function Calendars() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name:"", url:"", property_unit:"", channel:"booking" });
  const [syncing, setSyncing] = useState(null);
  const load = () => api.get("/calendars").then(r=>setItems(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.name || !f.url || !f.property_unit) return toast.error("Compila tutti i campi");
    try {
      await api.post("/calendars", f);
      toast.success("Calendario aggiunto");
      setOpen(false); setF({ name:"", url:"", property_unit:"", channel:"booking" }); load();
    } catch(e){ toast.error("Errore"); }
  };
  const del = async (id) => { await api.delete(`/calendars/${id}`); load(); };
  const sync = async (id) => {
    setSyncing(id);
    try {
      const r = await api.post(`/calendars/${id}/sync`);
      toast.success(r.data.message);
      load();
    } catch(e){ toast.error(e?.response?.data?.detail || "Errore sync"); }
    finally { setSyncing(null); }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-olive/30 bg-olive/[0.04] p-5 flex items-start gap-4">
        <ArrowsClockwise size={22} weight="fill" className="text-olive mt-0.5"/>
        <div className="text-sm text-ink/90 leading-relaxed">
          <div className="font-serif text-lg mb-1">Sincronizza le prenotazioni da Booking, Airbnb, VRBO</div>
          Aggiungi un link iCal (lo trovi in "Sincronizzazione calendario" nel tuo pannello Booking/Airbnb). Otelya importerà automaticamente le prenotazioni evitando doppie prenotazioni.
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="font-serif text-2xl text-ink">I tuoi calendari</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full bg-ink text-canvas hover:bg-olive" data-testid="cal-new-btn"><Plus size={16} className="mr-1"/>Aggiungi</Button></DialogTrigger>
          <DialogContent className="bg-canvas">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Nuova sorgente iCal</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nome (es. "Booking — Suite Mare")</Label>
                <Input className="mt-1.5" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} data-testid="cal-field-name"/></div>
              <div><Label>URL iCal (.ics)</Label>
                <Input className="mt-1.5" placeholder="https://admin.booking.com/hotel/hoteladmin/ical.html?..." value={f.url} onChange={(e)=>setF({...f,url:e.target.value})} data-testid="cal-field-url"/></div>
              <div><Label>Alloggio associato</Label>
                <Input className="mt-1.5" placeholder="Es. Suite Mare" value={f.property_unit} onChange={(e)=>setF({...f,property_unit:e.target.value})} data-testid="cal-field-unit"/></div>
              <div><Label>Canale</Label>
                <Select value={f.channel} onValueChange={(v)=>setF({...f,channel:v})}>
                  <SelectTrigger className="mt-1.5" data-testid="cal-field-channel"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="booking">Booking.com</SelectItem>
                    <SelectItem value="airbnb">Airbnb</SelectItem>
                    <SelectItem value="vrbo">VRBO / HomeAway</SelectItem>
                    <SelectItem value="other">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={create} className="rounded-full bg-ink text-canvas hover:bg-olive" data-testid="cal-submit">Aggiungi calendario</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 && <div className="rounded-2xl border border-line bg-canvas p-10 text-center text-stone" data-testid="cal-empty">
        Nessun calendario collegato. Aggiungi il primo per sincronizzare automaticamente le prenotazioni.
      </div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map(c => (
          <div key={c.id} className="rounded-2xl border border-line bg-canvas p-5" data-testid={`cal-item-${c.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-stone">{c.channel}</div>
                <div className="font-serif text-lg text-ink mt-1">{c.name}</div>
                <div className="text-xs text-stone mt-1">Alloggio: {c.property_unit}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={()=>del(c.id)} data-testid={`cal-delete-${c.id}`}><Trash size={16}/></Button>
            </div>
            <div className="mt-3 text-xs text-stone break-all opacity-70">{c.url}</div>
            <div className="mt-3 flex items-center justify-between">
              <div className="text-xs text-stone">
                {c.last_sync ? `Ultimo sync: ${new Date(c.last_sync).toLocaleString('it')}` : "Mai sincronizzato"}
                {c.last_result && <div className="text-olive mt-0.5">{c.last_result}</div>}
              </div>
              <Button size="sm" onClick={()=>sync(c.id)} disabled={syncing===c.id}
                className="rounded-full bg-terracotta hover:bg-terracotta/90 text-canvas" data-testid={`cal-sync-${c.id}`}>
                <ArrowsClockwise size={14} className={`mr-1 ${syncing===c.id?"animate-spin":""}`}/>
                {syncing===c.id ? "Sincronizzo…" : "Sincronizza ora"}
              </Button>
            </div>
          </div>
        ))}
      </div>
      <IcalExport />
    </div>
  );
}

function IcalExport() {
  const [url, setUrl] = useState("");
  useEffect(() => { api.get("/calendars/export-url").then(r=>setUrl(r.data.url)).catch(()=>{}); }, []);
  const copy = async () => { try { await navigator.clipboard.writeText(url); toast.success("Link copiato"); } catch { toast.error("Copia non riuscita: seleziona il link e copialo a mano"); } };
  return (
    <div className="rounded-2xl border border-line bg-canvas p-5" data-testid="ical-export">
      <div className="font-serif text-lg text-ink">Esporta le prenotazioni dirette di Otelya</div>
      <div className="text-sm text-stone mt-1">Incolla questo link in Octorate, Booking o Airbnb (sezione "Importa calendario"): le prenotazioni prese da Otelya bloccheranno le date anche lì. Sincronizzazione automatica ogni ora.</div>
      <div className="mt-3 flex gap-2">
        <Input readOnly value={url} className="text-xs" data-testid="ical-export-url"/>
        <Button onClick={copy} className="rounded-full bg-ink text-canvas hover:bg-olive" data-testid="ical-export-copy"><Copy size={14} className="mr-1"/>Copia</Button>
      </div>
    </div>
  );
}


// ----- Reviews AI (Google / TripAdvisor / Booking / Airbnb) -----
const REVIEW_PLATFORMS = {
  google:       { label: "Google",      Icon: GoogleLogo,       color: "text-[#8a6d16]" },
  tripadvisor:  { label: "TripAdvisor", Icon: Compass,           color: "text-olive" },
  booking:      { label: "Booking",     Icon: CalendarBlank,    color: "text-terracotta" },
  airbnb:       { label: "Airbnb",      Icon: AirplaneTilt,     color: "text-terracotta" },
  other:        { label: "Altro",       Icon: Chat,             color: "text-stone" },
};

function StarRating({ value, onChange }) {
  return (
    <div className="flex items-center gap-1" data-testid="rev-rating">
      {[1,2,3,4,5].map(n => (
        <button key={n} type="button" onClick={()=>onChange(n)} data-testid={`rev-star-${n}`}
          className="transition-transform hover:scale-110">
          <Star size={22} weight={n<=value?"fill":"regular"} className={n<=value?"text-gold":"text-line"}/>
        </button>
      ))}
      <span className="ml-2 text-sm text-stone">{value}/5</span>
    </div>
  );
}

function Reviews() {
  const [platform, setPlatform] = useState("google");
  const [guest, setGuest] = useState("Marco Rossi");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("Soggiorno indimenticabile, il personale attento in ogni dettaglio e la colazione con prodotti locali una vera gioia. Torneremo sicuramente!");
  const [language, setLanguage] = useState("it");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const load = () => api.get("/reviews").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!text.trim() || !guest.trim()) return toast.error("Inserisci nome ospite e testo recensione");
    setBusy(true);
    try {
      await api.post("/reviews/reply", { platform, guest_name: guest, rating, review_text: text, language });
      toast.success("Risposta generata!");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore AI"); }
    finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/reviews/${id}`); load(); };
  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success("Copiato negli appunti"); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-5 rounded-2xl border border-line bg-canvas p-6 space-y-4">
        <div className="font-serif text-2xl text-ink">Rispondi a una recensione</div>
        <div>
          <Label>Piattaforma</Label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {Object.entries(REVIEW_PLATFORMS).map(([k, m]) => (
              <button key={k} onClick={()=>setPlatform(k)} data-testid={`rev-platform-${k}`}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm transition ${platform===k?"bg-ink text-canvas border-ink":"bg-canvas text-stone border-line hover:border-ink"}`}>
                <m.Icon size={18} weight={platform===k?"fill":"regular"} /> {m.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label>Nome ospite</Label>
          <Input className="mt-1.5" value={guest} onChange={(e)=>setGuest(e.target.value)} data-testid="rev-guest"/>
        </div>
        <div>
          <Label>Punteggio</Label>
          <div className="mt-2"><StarRating value={rating} onChange={setRating}/></div>
        </div>
        <div>
          <Label>Testo recensione</Label>
          <Textarea rows={5} className="mt-1.5" value={text} onChange={(e)=>setText(e.target.value)} data-testid="rev-text"/>
        </div>
        <div>
          <Label>Lingua della risposta</Label>
          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger className="mt-1.5" data-testid="rev-language"><SelectValue/></SelectTrigger>
            <SelectContent>{["it","en","fr","de","es","pt","nl"].map(l=><SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={generate} disabled={busy} className="w-full rounded-full bg-terracotta hover:bg-terracotta/90 text-canvas" data-testid="rev-generate-btn">
          <Sparkle size={16} weight="fill" className="mr-2"/> {busy ? "Otelya sta scrivendo…" : "Genera risposta"}
        </Button>
      </div>

      <div className="lg:col-span-7 space-y-4">
        <div className="font-serif text-2xl text-ink flex items-center gap-2">
          <Star weight="fill" className="text-gold"/> Risposte generate
        </div>
        {items.length === 0 && (
          <div className="rounded-2xl border border-line bg-canvas p-10 text-center text-stone" data-testid="rev-empty">
            Nessuna risposta ancora. Incolla una recensione per generare la prima risposta professionale.
          </div>
        )}
        {items.map(r => {
          const meta = REVIEW_PLATFORMS[r.platform] || REVIEW_PLATFORMS.other;
          const isNeg = r.sentiment === "negative";
          return (
            <div key={r.id} className={`rounded-2xl border p-5 ${isNeg?"border-terracotta/30 bg-terracotta/[0.03]":"border-line bg-canvas"}`} data-testid={`rev-item-${r.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone">
                  <meta.Icon size={16} className={meta.color} weight="fill"/> {meta.label} · {r.language.toUpperCase()}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="icon" variant="ghost" onClick={()=>copyText(r.reply)} data-testid={`rev-copy-${r.id}`}><Copy size={16}/></Button>
                  <Button size="icon" variant="ghost" onClick={()=>del(r.id)} data-testid={`rev-delete-${r.id}`}><Trash size={16}/></Button>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <div className="font-serif text-lg text-ink">{r.guest_name}</div>
                <div className="flex items-center gap-0.5">
                  {[1,2,3,4,5].map(n => <Star key={n} size={14} weight={n<=r.rating?"fill":"regular"} className={n<=r.rating?"text-gold":"text-line"}/>)}
                </div>
                {isNeg && <span className="text-[10px] uppercase tracking-widest bg-terracotta/15 text-terracotta px-2 py-0.5 rounded-full">Critica</span>}
              </div>
              <div className="mt-3 rounded-lg bg-sand/60 p-3 text-sm text-ink/80 italic">"{r.review_text}"</div>
              <div className="mt-4 rounded-xl border border-olive/30 bg-olive/5 p-4">
                <div className="text-xs uppercase tracking-widest text-olive mb-2 flex items-center gap-2"><Sparkle weight="fill" size={12}/> Risposta pronta</div>
                <div className="whitespace-pre-wrap text-ink leading-relaxed">{r.reply}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ----- Ticket Support (support@otelya.app channel) -----
const TICKET_PRIORITY = {
  urgent: { label: "Urgente", color: "bg-red-100 text-red-800 border-red-200", dot: "bg-red-500" },
  high:   { label: "Alta",    color: "bg-terracotta/15 text-terracotta border-terracotta/30", dot: "bg-terracotta" },
  normal: { label: "Normale", color: "bg-olive/10 text-olive border-olive/30", dot: "bg-olive" },
  low:    { label: "Bassa",   color: "bg-sand text-stone border-line", dot: "bg-stone" },
};
const TICKET_STATUS = {
  open:        { label: "Aperto",        color: "bg-terracotta/15 text-terracotta" },
  in_progress: { label: "In lavorazione", color: "bg-gold/20 text-[#8a6d16]" },
  resolved:    { label: "Risolto",       color: "bg-olive/15 text-olive" },
  closed:      { label: "Chiuso",        color: "bg-sand text-stone" },
};
const TICKET_CATEGORY = {
  billing: "Fatturazione", technical: "Tecnico", account: "Account",
  general: "Generale", feature_request: "Richiesta feature", bug: "Bug",
};

function TicketDetail({ ticket, onUpdate, onClose }) {
  const [replyText, setReplyText] = useState("");
  const [busy, setBusy] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);

  useEffect(() => { if (ticket?.ai_suggested_reply) setReplyText(ticket.ai_suggested_reply); else setReplyText(""); }, [ticket?.id, ticket?.ai_suggested_reply]);

  const sendReply = async (close=false) => {
    if (!replyText.trim()) return toast.error("Scrivi una risposta");
    setBusy(true);
    try {
      const r = await api.post(`/tickets/${ticket.id}/reply`, { reply: replyText, close_ticket: close });
      toast.success(r.data.email_sent ? "Risposta inviata via email" : "Risposta salvata (email non inviata)");
      onUpdate();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const regenerateAI = async () => {
    setRegenBusy(true);
    try {
      const r = await api.post(`/tickets/${ticket.id}/ai-suggest`);
      setReplyText(r.data.suggestion);
      toast.success("Nuova bozza AI generata");
    } catch(e){ toast.error(e?.response?.data?.detail || "Errore AI"); }
    finally { setRegenBusy(false); }
  };

  const updateField = async (field, value) => {
    try {
      const r = await api.patch(`/tickets/${ticket.id}`, { [field]: value });
      toast.success("Aggiornato");
      onUpdate(r.data);
    } catch(e){ toast.error("Errore"); }
  };

  const del = async () => {
    if (!window.confirm("Eliminare definitivamente questo ticket?")) return;
    await api.delete(`/tickets/${ticket.id}`);
    onClose(); onUpdate();
  };

  const p = TICKET_PRIORITY[ticket.priority] || TICKET_PRIORITY.normal;
  const s = TICKET_STATUS[ticket.status] || TICKET_STATUS.open;

  return (
    <div className="rounded-2xl border border-line bg-canvas p-6 lg:p-8 space-y-5" data-testid={`ticket-detail-${ticket.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-stone flex-wrap">
            <span className="font-mono">#{ticket.id.slice(0,8)}</span> ·
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${p.color} border`}>
              <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`}/> {p.label}
            </span>
            <span className={`px-2 py-0.5 rounded-full ${s.color}`}>{s.label}</span>
          </div>
          <h2 className="font-serif text-2xl text-ink mt-2 leading-tight break-words">{ticket.subject}</h2>
          <div className="mt-1 text-sm text-stone">
            Da <strong className="text-ink">{ticket.from_name}</strong> ({ticket.from_email}) · {TICKET_CATEGORY[ticket.category]} · {new Date(ticket.created_at).toLocaleString('it')}
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={del} data-testid="ticket-delete-btn"><Trash size={16}/></Button>
      </div>

      <div className="rounded-xl bg-sand/50 p-5 whitespace-pre-wrap text-ink leading-relaxed" data-testid="ticket-message">
        {ticket.message}
      </div>

      <div className="flex flex-wrap gap-2">
        <Select value={ticket.status} onValueChange={(v)=>updateField("status", v)}>
          <SelectTrigger className="w-40" data-testid="ticket-status-select"><SelectValue/></SelectTrigger>
          <SelectContent>{Object.entries(TICKET_STATUS).map(([k,v])=><SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={ticket.priority} onValueChange={(v)=>updateField("priority", v)}>
          <SelectTrigger className="w-40" data-testid="ticket-priority-select"><SelectValue/></SelectTrigger>
          <SelectContent>{Object.entries(TICKET_PRIORITY).map(([k,v])=><SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={ticket.category} onValueChange={(v)=>updateField("category", v)}>
          <SelectTrigger className="w-44" data-testid="ticket-category-select"><SelectValue/></SelectTrigger>
          <SelectContent>{Object.entries(TICKET_CATEGORY).map(([k,v])=><SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {ticket.replies?.length > 0 && (
        <div className="space-y-3">
          <div className="text-xs uppercase tracking-widest text-stone">Risposte precedenti</div>
          {ticket.replies.map((r, i) => (
            <div key={i} className="rounded-xl border border-olive/30 bg-olive/5 p-4" data-testid={`ticket-reply-${i}`}>
              <div className="text-xs text-stone mb-1">{r.author} · {new Date(r.at).toLocaleString('it')}</div>
              <div className="whitespace-pre-wrap text-ink leading-relaxed">{r.reply}</div>
            </div>
          ))}
        </div>
      )}

      <div className="pt-4 border-t border-line">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-widest text-stone flex items-center gap-2">
            <Sparkle weight="fill" size={12} className="text-gold"/> Bozza AI · modificabile
          </div>
          <Button variant="ghost" size="sm" onClick={regenerateAI} disabled={regenBusy} data-testid="ticket-regenerate-ai-btn">
            <ArrowClockwise size={14} className={`mr-1 ${regenBusy?"animate-spin":""}`}/>
            {regenBusy ? "Rigenero…" : "Rigenera con AI"}
          </Button>
        </div>
        <Textarea rows={8} value={replyText} onChange={(e)=>setReplyText(e.target.value)}
          placeholder={ticket.ai_suggested_reply ? "" : "Otelya sta generando una bozza…"}
          className="w-full" data-testid="ticket-reply-text"/>
        <div className="mt-3 flex flex-wrap gap-2 justify-end">
          <Button variant="outline" className="rounded-full border-line" onClick={()=>sendReply(false)} disabled={busy} data-testid="ticket-send-reply-btn">
            <PaperPlaneTilt size={14} className="mr-1"/> {busy ? "Invio…" : "Invia risposta"}
          </Button>
          <Button className="rounded-full bg-olive hover:bg-olivelight text-canvas" onClick={()=>sendReply(true)} disabled={busy} data-testid="ticket-send-close-btn">
            <Check size={14} className="mr-1"/> Invia e chiudi ticket
          </Button>
        </div>
      </div>
    </div>
  );
}

function NewTicketDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ subject:"", message:"", from_name:"", from_email:"", priority:"normal", category:"general" });
  const submit = async () => {
    if (!f.subject || !f.message || !f.from_email) return toast.error("Compila oggetto, messaggio ed email cliente");
    try {
      await api.post("/tickets", f); toast.success("Ticket creato · bozza AI in generazione");
      setOpen(false); setF({ subject:"", message:"", from_name:"", from_email:"", priority:"normal", category:"general" });
      onCreated();
    } catch(e){ toast.error(e?.response?.data?.detail || "Errore"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full bg-ink text-canvas hover:bg-olive" data-testid="ticket-new-btn"><Plus size={16} className="mr-1"/>Nuovo ticket</Button></DialogTrigger>
      <DialogContent className="bg-canvas max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Nuovo ticket manuale</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nome cliente</Label><Input className="mt-1.5" value={f.from_name} onChange={(e)=>setF({...f,from_name:e.target.value})} data-testid="new-ticket-name"/></div>
            <div><Label>Email cliente</Label><Input className="mt-1.5" type="email" value={f.from_email} onChange={(e)=>setF({...f,from_email:e.target.value})} data-testid="new-ticket-email"/></div>
          </div>
          <div><Label>Oggetto</Label><Input className="mt-1.5" value={f.subject} onChange={(e)=>setF({...f,subject:e.target.value})} data-testid="new-ticket-subject"/></div>
          <div><Label>Messaggio</Label><Textarea rows={5} className="mt-1.5" value={f.message} onChange={(e)=>setF({...f,message:e.target.value})} data-testid="new-ticket-message"/></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Priorità</Label>
              <Select value={f.priority} onValueChange={(v)=>setF({...f,priority:v})}><SelectTrigger className="mt-1.5" data-testid="new-ticket-priority"><SelectValue/></SelectTrigger>
                <SelectContent>{Object.entries(TICKET_PRIORITY).map(([k,v])=><SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Categoria</Label>
              <Select value={f.category} onValueChange={(v)=>setF({...f,category:v})}><SelectTrigger className="mt-1.5" data-testid="new-ticket-category"><SelectValue/></SelectTrigger>
                <SelectContent>{Object.entries(TICKET_CATEGORY).map(([k,v])=><SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-full bg-ink text-canvas hover:bg-olive" data-testid="new-ticket-submit">Crea ticket</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const [t, s] = await Promise.all([
      api.get("/tickets" + (filter !== "all" ? `?status=${filter}` : "")),
      api.get("/tickets/stats"),
    ]);
    setTickets(t.data); setStats(s.data);
    if (selected) {
      const fresh = t.data.find(x => x.id === selected.id);
      if (fresh) setSelected(fresh);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const filters = [
    { k:"all",         l:"Tutti",         count: stats.total },
    { k:"open",        l:"Aperti",        count: stats.open },
    { k:"in_progress", l:"In lavorazione", count: stats.in_progress },
    { k:"resolved",    l:"Risolti",       count: stats.resolved },
  ];

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-olive/30 bg-olive/[0.04] p-5 flex items-start gap-4">
        <Headset size={22} weight="fill" className="text-olive mt-0.5"/>
        <div className="text-sm text-ink/90 leading-relaxed">
          <div className="font-serif text-lg mb-1">Ticket Support · Canale <code className="text-terracotta">support@otelya.app</code></div>
          Ogni email inviata a support@otelya.app diventa automaticamente un ticket con priorità e categoria classificate dall'AI. Otelya prepara subito una bozza di risposta professionale.
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KPI testid="tk-total"       label="Totali"        value={stats.total ?? 0}/>
        <KPI testid="tk-open"        label="Aperti"        value={stats.open ?? 0}/>
        <KPI testid="tk-in-progress" label="In lavorazione" value={stats.in_progress ?? 0}/>
        <KPI testid="tk-resolved"    label="Risolti"       value={stats.resolved ?? 0}/>
        <KPI testid="tk-urgent"      label="Urgenti"       value={stats.urgent ?? 0} hint="da gestire subito"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-5 rounded-2xl border border-line bg-canvas overflow-hidden flex flex-col">
          <div className="p-4 border-b border-line flex items-center justify-between gap-2">
            <div className="flex flex-wrap gap-1.5">
              {filters.map(fl => (
                <button key={fl.k} onClick={()=>setFilter(fl.k)} data-testid={`tk-filter-${fl.k}`}
                  className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full transition ${filter===fl.k?"bg-ink text-canvas":"bg-canvas text-stone border border-line hover:border-ink"}`}>
                  {fl.l} {fl.count!==undefined && <span className="opacity-70">({fl.count})</span>}
                </button>
              ))}
            </div>
            <NewTicketDialog onCreated={load}/>
          </div>
          <div className="flex-1 overflow-y-auto max-h-[70vh] divide-y divide-line">
            {tickets.length === 0 && <div className="p-8 text-center text-stone text-sm">Nessun ticket in questa vista.</div>}
            {tickets.map(t => {
              const p = TICKET_PRIORITY[t.priority] || TICKET_PRIORITY.normal;
              const s = TICKET_STATUS[t.status] || TICKET_STATUS.open;
              return (
                <button key={t.id} onClick={()=>setSelected(t)} data-testid={`tk-item-${t.id}`}
                  className={`w-full text-left p-4 hover:bg-sand/60 transition-colors ${selected?.id===t.id?"bg-sand/70":""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${p.dot}`}/>
                      <span className="font-medium text-ink truncate">{t.subject}</span>
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 ${s.color}`}>{s.label}</span>
                  </div>
                  <div className="mt-1 text-xs text-stone truncate">
                    <strong>{t.from_name || t.from_email}</strong> · {TICKET_CATEGORY[t.category]} · {new Date(t.created_at).toLocaleDateString('it')}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="lg:col-span-7">
          {selected ? (
            <TicketDetail ticket={selected} onUpdate={load} onClose={()=>setSelected(null)}/>
          ) : (
            <div className="rounded-2xl border border-line bg-canvas p-10 text-center text-stone" data-testid="tk-empty-detail">
              Seleziona un ticket per vederne il dettaglio, la bozza AI e rispondere via email.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ----- Help & FAQ -----
function Help() {
  const { user } = useAuth();
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [openIdx, setOpenIdx] = useState(-1);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return DASHBOARD_FAQ.filter(f =>
      (category === "all" || f.cat === category) &&
      (!q || f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q))
    );
  }, [category, query]);

  // Render markdown-lite: **bold** → <strong>
  const renderA = (t) => t.split(/(\*\*[^*]+\*\*)/g).map((chunk, i) =>
    chunk.startsWith("**") ? <strong key={i} className="text-ink">{chunk.slice(2,-2)}</strong> : <span key={i}>{chunk}</span>
  );

  return (
    <div className="space-y-6">
      <Guides defaultTab={user?.role === "partner_owner" ? "partner" : "struttura"} />
      <div className="rounded-2xl border border-olive/30 bg-olive/[0.04] p-5 flex items-start gap-4 print:hidden">
        <Question size={22} weight="fill" className="text-olive mt-0.5"/>
        <div className="text-sm text-ink/90 leading-relaxed">
          <div className="font-serif text-lg mb-1">Centro Assistenza Otelya</div>
          Trova risposta rapida a {DASHBOARD_FAQ.length}+ domande comuni. Se non trovi ciò che cerchi, scrivici a <a href="mailto:support@otelya.app" className="text-terracotta underline underline-offset-4">support@otelya.app</a> — rispondiamo entro 24h.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3 space-y-1">
          <button onClick={()=>{setCategory("all");setOpenIdx(-1);}} data-testid="faq-cat-all"
            className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition ${category==="all"?"bg-ink text-canvas":"text-stone hover:bg-sand/60"}`}>
            <span>📚</span> Tutte le domande <span className="ml-auto text-xs opacity-70">({DASHBOARD_FAQ.length})</span>
          </button>
          {DASHBOARD_FAQ_CATEGORIES.map(c => {
            const count = DASHBOARD_FAQ.filter(f => f.cat === c.key).length;
            return (
              <button key={c.key} onClick={()=>{setCategory(c.key);setOpenIdx(-1);}} data-testid={`faq-cat-${c.key}`}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition ${category===c.key?"bg-ink text-canvas":"text-stone hover:bg-sand/60"}`}>
                <span>{c.emoji}</span> {c.label} <span className="ml-auto text-xs opacity-70">({count})</span>
              </button>
            );
          })}
        </aside>

        <div className="lg:col-span-9 space-y-4">
          <div className="relative">
            <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone"/>
            <Input value={query} onChange={(e)=>{setQuery(e.target.value);setOpenIdx(-1);}}
              placeholder="Cerca nelle FAQ… (es. WhatsApp, fatture, GDPR)"
              className="pl-11 h-12 rounded-full border-line" data-testid="faq-search"/>
          </div>

          <div className="rounded-2xl border border-line bg-canvas divide-y divide-line overflow-hidden">
            {filtered.length === 0 && (
              <div className="p-8 text-center text-stone text-sm" data-testid="faq-empty">
                Nessuna FAQ trovata. Scrivici a support@otelya.app.
              </div>
            )}
            {filtered.map((f, i) => {
              const isOpen = openIdx === i;
              const cat = DASHBOARD_FAQ_CATEGORIES.find(c => c.key === f.cat);
              return (
                <div key={i} data-testid={`faq-dash-item-${i}`}>
                  <button onClick={()=>setOpenIdx(isOpen?-1:i)} data-testid={`faq-dash-q-${i}`}
                    className="w-full flex items-start justify-between gap-4 p-5 text-left hover:bg-sand/40 transition-colors">
                    <div className="flex-1">
                      <div className="text-[10px] uppercase tracking-widest text-stone mb-1">{cat?.emoji} {cat?.label}</div>
                      <div className="font-medium text-ink leading-snug">{f.q}</div>
                    </div>
                    <span className={`shrink-0 w-7 h-7 rounded-full border border-line grid place-items-center text-stone text-sm transition-transform ${isOpen?"rotate-45 bg-ink text-canvas border-ink":""}`}>+</span>
                  </button>
                  <div className={`overflow-hidden transition-all ${isOpen?"max-h-[500px] pb-5":"max-h-0"}`}>
                    <p className="px-5 text-stone leading-relaxed" data-testid={`faq-dash-a-${i}`}>{renderA(f.a)}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rounded-2xl border border-line bg-canvas p-6 text-center">
            <div className="font-serif text-xl text-ink">Non hai trovato quello che cercavi?</div>
            <p className="text-sm text-stone mt-2 mb-4">Il team Otelya risponde a qualsiasi domanda entro 24 ore.</p>
            <a href="mailto:support@otelya.app" data-testid="faq-contact-support">
              <Button className="rounded-full bg-ink text-canvas hover:bg-olive"><EnvelopeSimple size={16} className="mr-2"/> Contatta il supporto</Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}


// ----- Case Studies admin -----
function CaseStudiesAdmin() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [article, setArticle] = useState(null); // {cs, data} for the LinkedIn modal
  const [alertModal, setAlertModal] = useState(null); // {cs, preview}
  const [analytics, setAnalytics] = useState(null);

  const load = () => {
    api.get("/admin/case-studies").then(r => setItems(r.data?.items || []))
       .catch(() => setItems([]));
    api.get("/admin/case-studies/analytics").then(r => setAnalytics(r.data))
       .catch(() => setAnalytics(null));
  };
  useEffect(() => { load(); }, []);

  const togglePublish = async (cs) => {
    try {
      const r = await api.post(`/admin/case-study/${cs.id}/publish`);
      toast.success(r.data.published ? "Pubblicato pubblicamente ✓" : "Rimesso in bozza");
      load();
    } catch (e) {
      toast.error("Errore pubblicazione");
    }
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        structure_name: editing.structure_name,
        city: editing.city,
        intro_html: editing.intro_html,
        first_email_subject: editing.first_email_subject,
        first_email_body_anon: editing.first_email_body_anon,
        testimonial: editing.testimonial,
        testimonial_author: editing.testimonial_author,
        testimonial_role: editing.testimonial_role,
        days_to_close: editing.days_to_close === "" || editing.days_to_close === null ? null : Number(editing.days_to_close),
        ai_exchanges: editing.ai_exchanges === "" || editing.ai_exchanges === null ? null : Number(editing.ai_exchanges),
      };
      await api.patch(`/admin/case-study/${editing.id}`, payload);
      toast.success("Case study salvato");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (cs) => {
    if (!window.confirm(`Eliminare definitivamente il case study di "${cs.structure_name}"?`)) return;
    try {
      await api.delete(`/admin/case-study/${cs.id}`);
      toast.success("Eliminato");
      load();
    } catch (e) {
      toast.error("Errore eliminazione");
    }
  };

  const generateArticle = async (cs) => {
    const toastId = toast.loading("Claude sta scrivendo l'articolo LinkedIn…");
    try {
      const r = await api.post(`/admin/case-study/${cs.id}/linkedin-article`);
      toast.dismiss(toastId);
      toast.success(r.data?.cached ? "Articolo recuperato dalla cache" : "Articolo generato ✓");
      setArticle({ cs, data: r.data });
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(e?.response?.data?.detail || "Errore generazione");
    }
  };

  const openAlertModal = async (cs) => {
    const toastId = toast.loading("Conto i prospect caldi…");
    try {
      const r = await api.post(`/admin/case-study/${cs.id}/alert-prospects`, { dry_run: true });
      toast.dismiss(toastId);
      setAlertModal({ cs, preview: r.data });
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const sendAlert = async () => {
    if (!alertModal) return;
    const confirm = window.confirm(`Inviare l'email a ${alertModal.preview.recipient_count} prospect caldi?\n\nQuesta azione è una tantum: non potrai rinviarla per questo case study.`);
    if (!confirm) return;
    const toastId = toast.loading(`Invio a ${alertModal.preview.recipient_count} prospect…`);
    try {
      const r = await api.post(`/admin/case-study/${alertModal.cs.id}/alert-prospects`, {});
      toast.dismiss(toastId);
      if (r.data?.already_sent) {
        toast.error(`Già inviato il ${new Date(r.data.sent_at).toLocaleDateString("it-IT")} a ${r.data.sent_count} prospect`);
      } else {
        toast.success(`Inviato a ${r.data?.sent_count || 0} prospect ✓`);
      }
      setAlertModal(null);
      load();
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(e?.response?.data?.detail || "Errore invio");
    }
  };

  if (!items) return <div className="text-stone">Caricamento…</div>;

  return (
    <div className="space-y-6" data-testid="case-studies-admin">
      <div className="rounded-2xl border border-line bg-canvas p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-widest text-gold">Case Studies · ROI Tracker</div>
            <h2 className="font-serif text-2xl text-ink mt-2">Social proof automatica</h2>
            <p className="text-sm text-stone mt-2 max-w-2xl">
              Ogni volta che un prospect converte, viene generata una <strong>bozza</strong> con
              nome struttura, città, giorni-to-close, scambi AI, prima email (anonimizzata).
              Modifica e pubblica quando vuoi.
            </p>
          </div>
          <div className="text-right">
            <div className="font-serif text-3xl text-ink">{items.length}</div>
            <div className="text-xs uppercase tracking-widest text-stone">Totali</div>
            <div className="font-serif text-xl text-terracotta mt-2">
              {items.filter(c => c.published).length}
            </div>
            <div className="text-xs uppercase tracking-widest text-stone">Pubblicati</div>
          </div>
        </div>
      </div>

      {analytics && analytics.totals.total_views > 0 && (
        <div className="rounded-2xl border border-line bg-canvas p-6" data-testid="case-studies-analytics">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
            <div>
              <div className="text-xs uppercase tracking-widest text-terracotta flex items-center gap-1">
                <ChartBar size={11}/> Analytics
              </div>
              <h3 className="font-serif text-xl text-ink mt-1">Chi legge, chi clicca, chi condivide</h3>
            </div>
            <div className="text-right">
              <div className="font-serif text-2xl text-ink">{analytics.totals.avg_ctr_pct}%</div>
              <div className="text-[10px] uppercase tracking-widest text-stone">CTR medio</div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="rounded-lg bg-sand/40 p-4 text-center">
              <div className="font-serif text-2xl text-ink">{analytics.totals.total_views}</div>
              <div className="text-[10px] uppercase tracking-widest text-stone mt-1">visualizzazioni</div>
              <div className="text-[10px] text-stone/70 mt-0.5">{analytics.totals.total_unique_views} unique</div>
            </div>
            <div className="rounded-lg bg-sand/40 p-4 text-center">
              <div className="font-serif text-2xl text-terracotta">{analytics.totals.total_cta_clicks}</div>
              <div className="text-[10px] uppercase tracking-widest text-stone mt-1">click CTA</div>
            </div>
            <div className="rounded-lg bg-sand/40 p-4 text-center">
              <div className="font-serif text-2xl text-ink">{analytics.totals.total_shares}</div>
              <div className="text-[10px] uppercase tracking-widest text-stone mt-1">condivisioni</div>
            </div>
            <div className="rounded-lg bg-sand/40 p-4 text-center">
              <div className="font-serif text-2xl text-ink">{analytics.totals.published_count}</div>
              <div className="text-[10px] uppercase tracking-widest text-stone mt-1">pubblicati</div>
            </div>
          </div>

          {analytics.top_by_views.length > 0 && (
            <div className="mb-5">
              <div className="text-xs uppercase tracking-widest text-stone mb-2 flex items-center gap-1">
                <Fire size={11} className="text-terracotta"/> Top 3 per visualizzazioni
              </div>
              <div className="space-y-1.5">
                {analytics.top_by_views.map((r, i) => (
                  <div key={r.slug} className="flex items-center justify-between py-2 px-3 rounded-lg bg-canvas border border-line/50" data-testid={`cs-analytics-top-${i}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="font-serif text-base text-gold">#{i+1}</span>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-ink truncate">{r.structure_name}</div>
                        <div className="text-[11px] text-stone">{r.city || "—"} · CTR {r.ctr_pct}%</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-serif text-lg text-ink">{r.views}</div>
                      <div className="text-[10px] uppercase tracking-widest text-stone">views</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <details className="group">
            <summary className="cursor-pointer text-xs uppercase tracking-widest text-stone hover:text-ink inline-flex items-center gap-1">
              <ArrowUpRight size={11} className="transition-transform group-open:rotate-90"/> Dettaglio per canale (tutti i case study)
            </summary>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-stone border-b border-line">
                    <th className="pb-2 pr-3">Case Study</th>
                    <th className="pb-2 px-2 text-right">Views</th>
                    <th className="pb-2 px-2 text-right">CTA</th>
                    <th className="pb-2 px-2 text-right">CTR</th>
                    <th className="pb-2 px-2 text-right">LinkedIn</th>
                    <th className="pb-2 px-2 text-right">WhatsApp</th>
                    <th className="pb-2 px-2 text-right">X</th>
                    <th className="pb-2 pl-2 text-right">Copy</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.rows.map(r => (
                    <tr key={r.slug} className="border-b border-line/40 hover:bg-sand/20" data-testid={`cs-analytics-row-${r.slug}`}>
                      <td className="py-2 pr-3 min-w-[180px]">
                        <div className="font-medium text-ink text-sm">{r.structure_name} {!r.published && <span className="text-[10px] text-stone">(bozza)</span>}</div>
                        <div className="text-[11px] text-stone">{r.city || "—"}</div>
                      </td>
                      <td className="py-2 px-2 text-right text-ink">{r.views} <span className="text-[10px] text-stone">({r.unique_views}u)</span></td>
                      <td className="py-2 px-2 text-right text-ink">{r.cta_clicks}</td>
                      <td className="py-2 px-2 text-right">
                        <span className={r.ctr_pct >= 10 ? "text-olive font-medium" : r.ctr_pct > 0 ? "text-ink" : "text-stone"}>
                          {r.ctr_pct}%
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right text-stone">{r.shares_linkedin || "—"}</td>
                      <td className="py-2 px-2 text-right text-stone">{r.shares_whatsapp || "—"}</td>
                      <td className="py-2 px-2 text-right text-stone">{r.shares_x || "—"}</td>
                      <td className="py-2 pl-2 text-right text-stone">{r.shares_copy || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      )}

      {items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-line p-8 text-center text-stone italic" data-testid="case-studies-admin-empty">
          Nessun case study (ancora). Appena un prospect converte in cliente pagante, apparirà qui una bozza.
        </div>
      )}

      <div className="space-y-3">
        {items.map(cs => (
          <div key={cs.id} className="rounded-xl border border-line bg-canvas p-5 flex items-center gap-4 flex-wrap"
               data-testid={`case-study-admin-row-${cs.slug}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <div className="font-serif text-lg text-ink">{cs.structure_name}</div>
                {cs.published ? (
                  <Badge className="bg-olive text-canvas">Pubblicato</Badge>
                ) : (
                  <Badge className="bg-sand text-ink">Bozza</Badge>
                )}
              </div>
              <div className="text-xs text-stone mt-1">
                {cs.city || "—"} · {cs.plan_label || cs.plan_key} ·
                {' '}{cs.days_to_close !== null && cs.days_to_close !== undefined ? `${cs.days_to_close} gg` : '—'} to close ·
                {' '}{cs.ai_exchanges || 0} scambi
                {cs.testimonial ? <span className="text-olive"> · ✓ testimonianza</span> : <span className="text-terracotta"> · no testimonianza</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing({ ...cs })} data-testid={`case-study-edit-${cs.slug}`}>
                <PencilSimple size={14} className="mr-1"/> Modifica
              </Button>
              <Button size="sm"
                      className={cs.published ? "bg-sand text-ink hover:bg-sand/80" : "bg-ink text-canvas hover:bg-ink/90"}
                      onClick={() => togglePublish(cs)}
                      data-testid={`case-study-toggle-${cs.slug}`}>
                {cs.published ? "Rimetti in bozza" : "Pubblica"}
              </Button>
              {cs.published && (
                <>
                  <a href={`/case-study/${cs.slug}`} target="_blank" rel="noopener noreferrer"
                     className="text-xs text-terracotta hover:underline flex items-center gap-1"
                     data-testid={`case-study-view-${cs.slug}`}>
                    <ArrowUpRight size={12}/> Vedi
                  </a>
                  <Button variant="outline" size="sm" aria-label="Debug anteprima social"
                          title="Verifica OG tags · Facebook Sharing Debugger"
                          onClick={async () => {
                            const toastId = toast.loading("Controllo anteprima social…");
                            try {
                              const r = await api.get(`/admin/case-study/${cs.slug}/og-debug`);
                              const d = r.data;
                              toast.dismiss(toastId);
                              if (d.verdict === "ready") {
                                toast.success(`✓ Pronto. Dimensioni OG: ${d.checks.og_image_dimensions}. Apro Facebook Debugger…`);
                              } else {
                                toast.error(`Attenzione: ${d.checks.warnings.join(" · ")}`);
                              }
                              window.open(d.facebook_debugger_url, "_blank");
                            } catch (e) {
                              toast.dismiss(toastId);
                              toast.error("Debug fallito");
                            }
                          }}
                          data-testid={`case-study-og-debug-${cs.slug}`}>
                    <ShareNetwork size={12} className="mr-1"/> OG Debug
                  </Button>
                  <Button variant="outline" size="sm" aria-label="Genera articolo LinkedIn Pulse"
                          title="Genera articolo pronto per LinkedIn Pulse"
                          onClick={() => generateArticle(cs)}
                          data-testid={`case-study-linkedin-${cs.slug}`}>
                    <LinkedinLogo size={12} className="mr-1"/> LinkedIn
                  </Button>
                  <Button variant="outline" size="sm" aria-label="Avvisa prospect caldi"
                          title={cs.alert_sent_at ? `Alert già inviato a ${cs.alert_sent_count || 0} prospect` : "Notifica i prospect caldi"}
                          onClick={() => openAlertModal(cs)}
                          disabled={!!cs.alert_sent_at}
                          data-testid={`case-study-alert-${cs.slug}`}>
                    <PaperPlaneTilt size={12} className="mr-1"/> {cs.alert_sent_at ? `Inviato (${cs.alert_sent_count||0})` : "Avvisa prospect"}
                  </Button>
                </>
              )}
              <Button variant="ghost" size="sm" aria-label="Elimina case study" title="Elimina" onClick={() => remove(cs)} data-testid={`case-study-delete-${cs.slug}`}>
                <Trash size={14}/>
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica case study</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4" data-testid="case-study-edit-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nome struttura</Label>
                  <Input value={editing.structure_name || ""} onChange={e => setEditing({...editing, structure_name: e.target.value})} data-testid="edit-structure-name"/>
                </div>
                <div>
                  <Label>Città</Label>
                  <Input value={editing.city || ""} onChange={e => setEditing({...editing, city: e.target.value})} data-testid="edit-city"/>
                </div>
                <div>
                  <Label>Giorni to close</Label>
                  <Input type="number" value={editing.days_to_close ?? ""} onChange={e => setEditing({...editing, days_to_close: e.target.value})} data-testid="edit-days"/>
                </div>
                <div>
                  <Label>Scambi AI</Label>
                  <Input type="number" value={editing.ai_exchanges ?? ""} onChange={e => setEditing({...editing, ai_exchanges: e.target.value})} data-testid="edit-exchanges"/>
                </div>
              </div>
              <div>
                <Label>Intro (HTML opzionale)</Label>
                <Textarea rows={3} value={editing.intro_html || ""} onChange={e => setEditing({...editing, intro_html: e.target.value})}
                  placeholder="<p>Qualche riga di contesto sulla struttura…</p>" data-testid="edit-intro"/>
              </div>
              <div>
                <Label>Oggetto prima email</Label>
                <Input value={editing.first_email_subject || ""} onChange={e => setEditing({...editing, first_email_subject: e.target.value})} data-testid="edit-first-subject"/>
              </div>
              <div>
                <Label>Prima email (anonimizzata — rivedila!)</Label>
                <Textarea rows={6} value={editing.first_email_body_anon || ""} onChange={e => setEditing({...editing, first_email_body_anon: e.target.value})} data-testid="edit-first-body"/>
                <div className="text-[11px] text-stone mt-1">L'anonimizzazione è automatica (email, telefoni, P.IVA, IBAN). Rileggi comunque prima di pubblicare.</div>
              </div>
              <div>
                <div className="flex items-center justify-between gap-3 mb-1">
                  <Label>Testimonianza</Label>
                  <Button type="button" variant="outline" size="sm"
                          onClick={async () => {
                            const raw = (editing.testimonial || "").trim();
                            if (raw.length < 20) { toast.error("Testimonianza troppo corta (min 20 caratteri)"); return; }
                            const toastId = toast.loading("Lucido con Claude…");
                            try {
                              const r = await api.post(`/admin/case-study/${editing.id}/polish-testimonial`, { testimonial: raw });
                              const polished = r.data?.polished || "";
                              setEditing({...editing, testimonial: polished, _originalTestimonial: raw});
                              toast.dismiss(toastId);
                              toast.success("Testimonianza lucidata ✓ (puoi annullare o editare)");
                            } catch (e) {
                              toast.dismiss(toastId);
                              toast.error(e?.response?.data?.detail || "Errore Claude");
                            }
                          }}
                          data-testid="polish-testimonial-btn">
                    <Sparkle size={14} className="mr-1"/> Lucida con Claude
                  </Button>
                </div>
                <Textarea rows={4} value={editing.testimonial || ""} onChange={e => setEditing({...editing, testimonial: e.target.value})}
                  placeholder="Verrà raccolta automaticamente 7 giorni dopo conversione. Puoi anche inserirla manualmente." data-testid="edit-testimonial"/>
                {editing._originalTestimonial && (
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-stone">
                    <button type="button" onClick={() => setEditing({...editing, testimonial: editing._originalTestimonial, _originalTestimonial: null})}
                            className="underline hover:text-ink" data-testid="polish-undo">
                      ↩ Ripristina versione originale
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Autore testimonianza</Label>
                  <Input value={editing.testimonial_author || ""} onChange={e => setEditing({...editing, testimonial_author: e.target.value})} data-testid="edit-testimonial-author"/>
                </div>
                <div>
                  <Label>Ruolo</Label>
                  <Input value={editing.testimonial_role || ""} onChange={e => setEditing({...editing, testimonial_role: e.target.value})} data-testid="edit-testimonial-role"/>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="edit-cancel">Annulla</Button>
            <Button onClick={save} disabled={saving} className="bg-ink text-canvas hover:bg-ink/90" data-testid="edit-save">
              {saving ? "Salvo…" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LinkedIn Article Modal */}
      <Dialog open={!!article} onOpenChange={(o) => !o && setArticle(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkedinLogo size={20} className="text-[#0A66C2]"/> Articolo LinkedIn Pulse
            </DialogTitle>
          </DialogHeader>
          {article && (
            <div className="space-y-5" data-testid="linkedin-article-content">
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone">Titolo</Label>
                <div className="font-serif text-xl mt-1 text-ink">{article.data.title}</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone">Hook (prime 2 righe)</Label>
                <div className="italic text-ink mt-1 leading-relaxed">{article.data.hook}</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone">Corpo (Markdown)</Label>
                <Textarea readOnly rows={14} value={article.data.body_markdown}
                          className="mt-1 font-mono text-xs"
                          data-testid="linkedin-article-body"/>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(article.data.hashtags || []).map(h => (
                  <span key={h} className="text-xs bg-sand px-2 py-1 rounded-full text-ink">{h}</span>
                ))}
              </div>
              <div className="text-xs text-stone">
                CTA al fondo: <a href={article.data.cta_url} target="_blank" rel="noopener noreferrer" className="text-terracotta underline">{article.data.cta_url}</a>
              </div>
              <div className="rounded-lg bg-sand/50 border border-line p-4 text-xs text-stone leading-relaxed">
                <strong className="text-ink">Istruzioni:</strong> apri LinkedIn → clicca "Scrivi un articolo" (Pulse) → incolla titolo, hook, corpo (Markdown viene renderizzato automaticamente), hashtag e CTA. Pubblica.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setArticle(null)} data-testid="linkedin-article-close">Chiudi</Button>
            <Button
              onClick={async () => {
                try {
                  const full = `${article.data.title}\n\n${article.data.hook}\n\n${article.data.body_markdown}\n\n👉 ${article.data.cta_url}\n\n${(article.data.hashtags||[]).join(" ")}`;
                  await navigator.clipboard.writeText(full);
                  toast.success("Articolo intero copiato ✓");
                } catch { toast.error("Copia non supportata"); }
              }}
              className="bg-ink text-canvas hover:bg-ink/90"
              data-testid="linkedin-article-copy">
              <CopyIcon size={14} className="mr-1"/> Copia tutto
            </Button>
            <a href="https://www.linkedin.com/post/new/" target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#0A66C2] text-canvas hover:bg-[#0A66C2]/90" data-testid="linkedin-article-open">
                <LinkedinLogo size={14} className="mr-1"/> Apri LinkedIn
              </Button>
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert Prospects Modal */}
      <Dialog open={!!alertModal} onOpenChange={(o) => !o && setAlertModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PaperPlaneTilt size={20} className="text-terracotta"/> Avvisa prospect caldi
            </DialogTitle>
          </DialogHeader>
          {alertModal && (
            <div className="space-y-4 text-sm" data-testid="alert-modal-content">
              <div className="rounded-lg bg-sand/50 border border-line p-4">
                <div className="text-xs uppercase tracking-widest text-stone">Oggetto email</div>
                <div className="font-medium text-ink mt-1">👀 Guarda cos'è successo a {alertModal.cs.structure_name}</div>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg border border-line p-3">
                  <div className="font-serif text-3xl text-ink">{alertModal.preview.recipient_count}</div>
                  <div className="text-[10px] uppercase tracking-widest text-stone mt-1">destinatari</div>
                </div>
                <div className="rounded-lg border border-line p-3">
                  <div className="font-serif text-3xl text-terracotta">{alertModal.preview.opt_outs_excluded}</div>
                  <div className="text-[10px] uppercase tracking-widest text-stone mt-1">opt-out esclusi</div>
                </div>
                <div className="rounded-lg border border-line p-3">
                  <div className="font-serif text-xs text-ink mt-2 leading-tight">{alertModal.preview.statuses.join(", ")}</div>
                  <div className="text-[10px] uppercase tracking-widest text-stone mt-1">status inclusi</div>
                </div>
              </div>
              {alertModal.preview.sample?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-stone mb-2">Esempi destinatari (primi 5)</div>
                  <div className="space-y-1 text-xs">
                    {alertModal.preview.sample.map(s => (
                      <div key={s.email} className="flex justify-between py-1 border-b border-line/50 last:border-0">
                        <span className="text-ink">{s.name || "—"}</span>
                        <span className="text-stone">{s.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-[11px] text-stone italic">
                ⚠️ Invio una tantum. Dopo l'invio, questo bottone sarà disabilitato per questo case study.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlertModal(null)} data-testid="alert-cancel">Annulla</Button>
            <Button onClick={sendAlert}
                    disabled={!alertModal?.preview?.recipient_count}
                    className="bg-ink text-canvas hover:bg-ink/90"
                    data-testid="alert-send">
              <PaperPlaneTilt size={14} className="mr-1"/> Invia a {alertModal?.preview?.recipient_count || 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ----- Main Dashboard -----
export default function Dashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [view, setView] = useState("overview");
  const [params] = useSearchParams();

  useEffect(() => { if (params.get("checkout")) setView("billing"); }, [params]);
  useEffect(() => { const v = params.get("view"); if (v) setView(v); }, [params]);

  const doLogout = () => { logout(); nav("/"); };
  const isAdmin = user?.email === "alfiomoretti@icloud.com";
  const isPartner = user?.role === "partner_owner";
  const navItems = isPartner ? NAV.filter(n => ["partner", "help"].includes(n.k))
    : NAV.filter(n => n.k !== "partner" && (!["visits", "partners_admin", "growth_plan"].includes(n.k) || isAdmin));
  useEffect(() => { if (isPartner) setView("partner"); }, [isPartner]);

  return (
    <div className="min-h-screen bg-canvas flex">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-line bg-canvas p-6 sticky top-0 h-screen h-[100dvh] overflow-hidden">
        <Logo />
        <CoBrand />
        <nav className="mt-8 flex-1 min-h-0 overflow-y-auto overscroll-contain space-y-1 -mx-2 px-2 pb-4 [-webkit-overflow-scrolling:touch]" data-testid="sidebar-nav">
          {navItems.map(n => (
            <button key={n.k} onClick={()=>setView(n.k)} data-testid={`nav-${n.k}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${view===n.k?"bg-ink text-canvas":"text-stone hover:text-ink hover:bg-sand/60"}`}>
              <n.i size={18} weight={view===n.k?"fill":"regular"} />
              {n.l}
            </button>
          ))}
        </nav>
        <div className="border-t border-line pt-4 space-y-1">
          <div className="px-3 py-2 text-xs">
            <div className="text-ink font-medium truncate">{user?.full_name}</div>
            <div className="text-stone truncate">{user?.property_name}</div>
          </div>
          <button onClick={doLogout} data-testid="logout-btn" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-stone hover:text-ink hover:bg-sand/60">
            <SignOut size={18}/> Esci
          </button>
        </div>
      </aside>

      {/* Mobile nav */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-canvas/90 backdrop-blur border-b border-line px-4 py-3 flex items-center justify-between">
        <Logo size="sm"/>
        <Select value={view} onValueChange={setView}>
          <SelectTrigger className="w-48 h-9" data-testid="mobile-nav"><SelectValue/></SelectTrigger>
          <SelectContent className="max-h-[70dvh]">{navItems.map(n => <SelectItem key={n.k} value={n.k}>{n.l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Main */}
      <main className="flex-1 p-6 lg:p-10 pt-20 lg:pt-10 overflow-x-hidden" data-testid="dashboard-main">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-widest text-stone">Ciao {user?.full_name?.split(" ")[0] || "gestore"}</div>
            <h1 className="font-serif text-3xl lg:text-4xl mt-2 text-ink leading-tight">
              {NAV.find(n=>n.k===view)?.l}
            </h1>
          </div>

          {view === "overview" && <Overview setView={setView} />}
          {view === "analytics" && <Analytics />}
          {view === "autopilot" && <SocialAutopilot />}
          {view === "editorial" && <EditorialCalendar />}
          {view === "social_connect" && <MetaConnect />}
          {view === "whatsapp" && <WhatsAppConnect />}
          {view === "photos" && <MyPhotos />}
          {view === "tiktok" && <TikTokStudio />}
          {view === "growth_plan" && isAdmin && <GrowthPlan />}
          {view === "visits" && isAdmin && <VisitsAnalytics />}
          {view === "partners_admin" && isAdmin && <AdminPartners />}
          {view === "partner" && isPartner && <PartnerConsole />}
          {view === "growth" && <GrowthKit />}
          {view === "outreach" && <ColdOutreach />}
          {view === "preview" && <OutreachPreview />}
          {view === "replies" && <DraftReplyInbox />}
          {view === "history" && <OutreachHistory />}
          {view === "voice" && <VoiceCalls />}
          {view === "widget" && <WidgetEmbed />}
          {view === "faqcms" && <FaqCms />}
          {view === "caseStudies" && <CaseStudiesAdmin />}
          {view === "inbox" && <Inbox />}
          {view === "bookings" && (
            <ListSection title="Prenotazioni" endpoint="bookings" testKey="bookings"
              columns={[
                {k:"guest_name", l:"Ospite"},
                {k:"property_unit", l:"Alloggio"},
                {k:"check_in", l:"Check-in"},
                {k:"check_out", l:"Check-out"},
                {k:"total_amount", l:"Totale", render:(r)=>`€${r.total_amount}`},
                {k:"status", l:"Stato"},
              ]}
              formFields={[
                {k:"guest_name", l:"Nome ospite"},
                {k:"property_unit", l:"Alloggio (es. Suite Mare)"},
                {k:"check_in", l:"Check-in", type:"date"},
                {k:"check_out", l:"Check-out", type:"date"},
                {k:"guests_count", l:"N. ospiti", type:"number", default:2},
                {k:"total_amount", l:"Totale €", type:"number", default:0},
                {k:"channel", l:"Canale", type:"select", default:"direct", options:[
                  {v:"direct",l:"Diretto"},{v:"booking",l:"Booking"},{v:"airbnb",l:"Airbnb"},{v:"whatsapp",l:"WhatsApp"},{v:"email",l:"Email"},{v:"phone",l:"Telefono"}
                ]},
                {k:"status", l:"Stato", type:"select", default:"confirmed", options:[
                  {v:"confirmed",l:"Confermata"},{v:"pending",l:"In attesa"},{v:"cancelled",l:"Annullata"}
                ]},
              ]}/>
          )}
          {view === "guests" && (
            <ListSection title="Ospiti" endpoint="guests" testKey="guests"
              columns={[
                {k:"full_name", l:"Nome"},
                {k:"email", l:"Email"},
                {k:"phone", l:"Telefono"},
                {k:"language", l:"Lingua"},
                {k:"notes", l:"Note"},
              ]}
              formFields={[
                {k:"full_name", l:"Nome completo"},
                {k:"email", l:"Email"},
                {k:"phone", l:"Telefono"},
                {k:"language", l:"Lingua", type:"select", default:"it", options:[
                  {v:"it",l:"Italiano"},{v:"en",l:"English"},{v:"fr",l:"Français"},{v:"de",l:"Deutsch"},{v:"es",l:"Español"}
                ]},
                {k:"notes", l:"Note", type:"textarea"},
              ]}/>
          )}
          {view === "suppliers" && (
            <ListSection title="Fornitori" endpoint="suppliers" testKey="suppliers"
              columns={[
                {k:"name", l:"Nome"}, {k:"category", l:"Categoria"}, {k:"contact", l:"Contatto"}, {k:"email", l:"Email"},
              ]}
              formFields={[
                {k:"name", l:"Nome fornitore"},
                {k:"category", l:"Categoria", type:"select", default:"cleaning", options:[
                  {v:"cleaning",l:"Pulizie"},{v:"laundry",l:"Lavanderia"},{v:"maintenance",l:"Manutenzione"},
                  {v:"food",l:"Alimentari"},{v:"transport",l:"Trasporti"},{v:"other",l:"Altro"}
                ]},
                {k:"contact", l:"Telefono"},
                {k:"email", l:"Email"},
                {k:"notes", l:"Note", type:"textarea"},
              ]}/>
          )}
          {view === "services" && (
            <ListSection title="Servizi" endpoint="services" testKey="services"
              columns={[
                {k:"name", l:"Nome"}, {k:"description", l:"Descrizione"},
                {k:"price", l:"Prezzo", render:(r)=>`€${r.price}`},
                {k:"currency", l:"Valuta"},
              ]}
              formFields={[
                {k:"name", l:"Nome servizio"},
                {k:"description", l:"Descrizione", type:"textarea"},
                {k:"price", l:"Prezzo", type:"number", default:0},
                {k:"currency", l:"Valuta", default:"EUR"},
              ]}/>
          )}
          {view === "ai" && <AIConfig />}
          {view === "billing" && <Billing />}
          {view === "marketing" && <Marketing />}
          {view === "calendars" && <Calendars />}
          {view === "reviews" && <Reviews />}
          {view === "tickets" && <Tickets />}
          {view === "help" && <Help />}
        </div>
      </main>
    </div>
  );
}
